import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// Supabase client (public anon key, injected at build time via REACT_APP_*).
// Guarded so the app still loads even if the env vars are not set yet.
const supabase =
  process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY
    ? createClient(
        process.env.REACT_APP_SUPABASE_URL,
        process.env.REACT_APP_SUPABASE_ANON_KEY
      )
    : null;

// Stripe.js (publishable key) for the on-page Identity verification window.
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
let _stripeJs = null;
function loadStripeJs() {
  return new Promise((resolve) => {
    if (!STRIPE_PK) { resolve(null); return; }
    if (_stripeJs) { resolve(_stripeJs); return; }
    if (window.Stripe) { _stripeJs = window.Stripe(STRIPE_PK); resolve(_stripeJs); return; }
    const el = document.createElement("script");
    el.src = "https://js.stripe.com/v3/";
    el.onload = () => { _stripeJs = window.Stripe ? window.Stripe(STRIPE_PK) : null; resolve(_stripeJs); };
    el.onerror = () => resolve(null);
    document.head.appendChild(el);
  });
}

// --- Secure upload pipeline (Transloadit) --------------------------------
// Routes a recording/file through the "val-user-uploads" template:
// type/size filter -> ClamAV virus scan -> re-encode a clean copy -> store the
// clean copy into val-messages under the user's own uid/ prefix. Returns
// { path, size }. Throws (fail-closed) if rejected, so nothing unscanned saves.
function valAssemblyError(status) {
  let blob = "";
  try { blob = JSON.stringify(status || {}); } catch (e) {}
  if (/malicious|virus/i.test(blob)) return "This file was rejected by our virus scan and could not be saved.";
  if (/allowed|declined|filter/i.test(blob)) return "That file type or size isn't allowed. Please use photos, audio, video, PDFs, or text files up to 250 MB.";
  const m = status && (status.message || status.error) ? String(status.message || status.error) : "";
  return "We couldn't process this file securely. Please try again." + (m ? " (" + m + ")" : "");
}
async function valCleanUpload(fileOrBlob, outExt) {
  if (!supabase) throw new Error("Saving is not configured yet.");
  const { data: sess } = await supabase.auth.getSession();
  const token = sess && sess.session && sess.session.access_token;
  if (!token) throw new Error("Your session has expired. Please sign in again.");
  let sign;
  try {
    const r = await fetch("/.netlify/functions/sign-transloadit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token, ext: outExt || "bin" }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j && j.error ? j.error : "Secure upload is unavailable right now. Please try again.");
    sign = j;
  } catch (e) {
    throw new Error(e && e.message ? e.message : "Secure upload is unavailable right now. Please try again.");
  }
  const fd = new FormData();
  fd.append("params", sign.params);
  fd.append("signature", sign.signature);
  fd.append("file", fileOrBlob, "upload." + (outExt || "bin"));
  let status;
  try {
    const cr = await fetch("https://api2.transloadit.com/assemblies", { method: "POST", body: fd });
    status = await cr.json();
  } catch (e) {
    throw new Error("Upload failed. Please check your connection and try again.");
  }
  const statusUrl = status && (status.assembly_ssl_url || status.assembly_url);
  if (!statusUrl) throw new Error(valAssemblyError(status));
  const started = Date.now();
  while (status.ok !== "ASSEMBLY_COMPLETED") {
    if (status.error || status.ok === "ASSEMBLY_CANCELED" || status.ok === "REQUEST_ABORTED") throw new Error(valAssemblyError(status));
    if (Date.now() - started > 5 * 60 * 1000) throw new Error("The upload took too long to process. Please try again.");
    await new Promise((res) => setTimeout(res, 2500));
    try { const pr = await fetch(statusUrl); status = await pr.json(); } catch (e) {}
  }
  let size = 0;
  try {
    const res = status.results || {};
    const order = ["exported", "image_opt", "video_clean", "audio_clean", "image_clean", "others"];
    for (let i = 0; i < order.length; i++) { const a = res[order[i]]; if (a && a[0] && a[0].size) { size = a[0].size; break; } }
  } catch (e) {}
  return { path: sign.dest, size: size };
}

const GOLD = "#C9A84C";
const GOLD_LIGHT = "#E8C96A";
const GOLD_DARK = "#8B6914";
// Theme-aware palette. Values resolve from CSS variables defined in
// styles.css (default light; class "v-dark" on <html> switches to dark).
const BLACK = "var(--v-black)";
const DARK = "var(--v-dark)";
const SURFACE = "var(--v-surface)";
const SURFACE2 = "var(--v-surface2)";
const BORDER = "var(--v-border)";
const TEXT = "var(--v-text)";
const MUTED = "var(--v-muted)";

// ============================================================
// INTERNATIONALIZATION (i18n) — English populated now.
// To add a language later: add its code to LANGUAGES and add a
// matching block to TRANSLATIONS. The dropdown in the nav already works.
// ============================================================
const LANGUAGES = [
  { code: "en", label: "English" },
  // { code: "es", label: "Español" },   // <-- add translations below to enable
  // { code: "fr", label: "Français" },
];

const TRANSLATIONS = {
  en: {
    nav_how: "How It Works",
    nav_pricing: "Pricing",
    nav_company: "Company",
    nav_media: "Media",
    nav_legal: "Policy",
    nav_signin: "SIGN IN",
    nav_getstarted: "GET STARTED",
  },
  // es: { ... }  // future
};

// Simple translator. Falls back to English if a key/lang is missing.
const t = (lang, key) =>
  (TRANSLATIONS[lang] && TRANSLATIONS[lang][key]) ||
  TRANSLATIONS.en[key] ||
  key;

// ============================================================
// LEGAL / POLICY CONTENT
// Each policy has an id, title, and an array of { h, p } sections
// (h = optional heading, p = paragraph). Edit text here freely.
// NOTE: Twilio-critical policies (Terms, Privacy, SMS) are fuller.
// Others are condensed summaries marked "[SUMMARY — expand from
// your Corporate Manual before relying on it]". Replace as needed.
// ============================================================
const CONTACT_BLOCK = {
  h: "Contact",
  p: "Questions may be directed to support@voiceafterlife.com. Voice After Life, Inc., 1207 Delaware Ave, Suite 662, Wilmington, DE 19806-4736. https://www.voiceafterlife.com",
};

const POLICIES = [
  {
    id: "terms",
    title: "Terms & Conditions",
    sections: [
      { h: "1. Who We Are", p: "These Terms govern your access to and use of the websites, mobile applications, and services (the \u201CService\u201D) operated by Voice After Life, Inc. (\u201CVoice After Life,\u201D \u201Cwe,\u201D \u201Cus,\u201D \u201Cour\u201D), and its subsidiary Back After Life, a corporation organized under the laws of the State of Delaware. By creating an account or using the Service, you agree to these Terms. The Service is a digital legacy and posthumous message delivery platform allowing registered Users to create Content to be stored and, upon verification of the User\u2019s death, delivered to designated Legacy Members." },
      { h: "2. Acceptance", p: "By accessing, creating an account, or using Voice After Life, Back After Life, or a subsidiary, Users agree to be bound by these Terms and Conditions and all Voice After Life policies designed to protect consumers." },
      { h: "3. Definitions", p: "Recipient: the designated individual or organization to whom secure messages are provided posthumously. User: creator of the paid account who designates Legacy Members. Trusted Verifiers: 2\u20135 individuals identified during enrollment who may be contacted to confirm the account holder\u2019s death. Plans: Life Starter (up to 5 Legacy Members), Life Family (up to 10), Lifetime (up to 50), Lifetime Family (unlimited, immediate family). Time Capsule: Content scheduled by a User for delivery on a future date, or on the first occurrence after a verified death, available on Life Family, Lifetime and Lifetime Family." },
      { h: "4. Eligibility", p: "Individual Users must be at least 18 years old. Users under 18 must be part of a Lifetime Family plan with written parental/guardian permission and Voice After Life approval. You are responsible for the accuracy of information you provide, including Legacy Member contact details, and for keeping your credentials secure." },
      { h: "5. Subscriptions, Billing, and Payment", p: "Every plan is offered as a monthly or annual subscription, and the Lifetime Family plan may instead be bought outright with a single one-time payment. Despite their names, the Lifetime and Lifetime Family plans are subscriptions unless you choose the one-time Lifetime Family purchase. Payments are processed by Stripe or an identified vendor; we do not store full card numbers. Recurring subscriptions renew automatically unless cancelled before renewal. Refunds are governed by our Refund Policy." },
      { h: "6. Nature of the Service \u2014 Limitations", p: "Delivery depends on events outside our full control, including death verification, accuracy of Legacy Member information, and third-party providers. We do not guarantee any message will be delivered, viewed, or received, and we are not a substitute for a will, trust, or legal instrument. Consult an attorney for estate matters. You are responsible for keeping Legacy Member contact information current." },
      { h: "7. Time Capsule", p: "Time Capsule allows Users on eligible plans to schedule Content for delivery on a future date you choose, or on the first scheduled occurrence following a verified death and staff-approved release. Time Capsule is included at no additional charge on the Life Family, Lifetime, and Lifetime Family plans, and is not available on Life Starter. The number of Time Capsules a User may have scheduled at one time is limited by plan: up to twenty (20) per account on Life Family, up to fifty (50) per account on Lifetime, and up to fifty (50) per User on Lifetime Family. A Time Capsule that has been delivered releases its place, so the limit applies to Time Capsules currently scheduled rather than to the total ever created. Each Time Capsule is addressed to a Legacy Member the User has already added and who has accepted. Handwritten letters are supported as digital uploads only: Voice After Life does not accept, transport, or store physical originals, and Users should never send original documents to us. Delivery of a Time Capsule remains subject to the limitations in Section 6." },
      { h: "8. Time Capsule Delivery After Death", p: "Following a verified death and a staff-approved release, Voice After Life will continue to deliver that User\u2019s scheduled Time Capsules for a limited period measured from the date of the approved release: ten (10) years on the Life Family plan, twenty (20) years on the Lifetime plan, and twenty-five (25) years on the Lifetime Family plan. Time Capsules scheduled to be delivered after that period ends will not be delivered, and no refund is owed for Time Capsules scheduled beyond the window that applies to the plan. Voice After Life may offer an extended or perpetual delivery option as a separate paid product. Once a release has been approved, all of that User\u2019s Time Capsules are permanently locked and can no longer be edited, rescheduled, or deleted by anyone, including the User\u2019s family and Voice After Life staff, so that what a person chose to say is preserved exactly as they left it." },
      { h: "9. Legacy Member Notifications", p: "Users authorize Voice After Life to notify designated Legacy Members regarding future messages. Upon notification, a Legacy Member must confirm and accept the Terms and Policies to participate. By providing a Legacy Member\u2019s phone number or email, you represent you have a relationship with that Legacy Member and a reasonable basis to provide their contact information." },
      { h: "10. User Content & Back After Life", p: "You retain ownership of your Content and grant Voice After Life a limited license to store, process, and deliver it to your designated Legacy Members. Users may opt in to Back After Life, authorizing retention and use of Content in perpetuity to create a voice/likeness for future interactions; this decision may be changed at any time while living." },
      { h: "11. Limitation of Liability", p: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, VOICE AFTER LIFE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY FAILURE OR DELAY IN DELIVERING CONTENT. TOTAL LIABILITY FOR ANY CLAIM WILL NOT EXCEED THE AMOUNT PAID IN THE TWELVE (12) MONTHS BEFORE THE CLAIM." },
      { h: "12. Identity Verification", p: "Voice After Life requires successful identity verification through one or more approved third-party providers before Users may access certain features. Providers may change over time to improve security, compliance, and fraud prevention." },
      { h: "13. Dispute Resolution & Governing Law", p: "These Terms are governed by the laws of the State of Delaware, without regard to conflict-of-laws principles. Arbitration and class-action waiver apply where lawful; litigation will be governed by Delaware law." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "privacy",
    title: "Global Privacy Policy",
    sections: [
      { h: "1. Purpose", p: "This Global Privacy Policy governs your access to and use of the Service operated by Voice After Life, Inc. and its subsidiary Back After Life, organized under the laws of Delaware. It aims to ensure compliance with applicable Data Protection Laws, including GDPR and, where applicable, HIPAA and CAN-SPAM." },
      { h: "2. Information We Collect", p: "Personal Information includes name, address, mobile phone number, email address, Legacy Member contact information, and uploaded or recorded messages (voice, video, written, digital). Users cannot upload Personal Information of another living person; creating an account for a deceased loved one requires verification of death and relationship/next-of-kin status." },
      { h: "3. How We Use Information", p: "Personal Information is used to operate the Service: authenticate Users, process payments, provide notifications, perform verification workflows, improve services, and comply with law. Designated Legacy Member messages are delivered only after verified death is confirmed. Users may opt in to Back After Life." },
      { h: "4. Data Sharing", p: "Information will NOT be shared with marketing vendors. Information may be shared only with processors, hosting providers, communications providers, legal authorities, and authorized Legacy Members, solely to render the paid-for service. We do not sell your personal information." },
      
      { h: "5. Message Escrow", p: "If a Legacy Member refuses to accept the Terms and Conditions, messages remain securely encrypted and stored rather than deleted. If the Legacy Member later accepts, messages may be released once all other release conditions are met." },
      { h: "6. Data Security & Retention", p: "We use encryption, access controls, audit logging, and monitoring. No system is perfectly secure. Personal Information is retained as needed to provide services, comply with obligations, and preserve delivery instructions, unless the User authorizes perpetual use for Back After Life." },
      { h: "7. Your Rights", p: "Users may access, correct, delete, and export information subject to applicable law, and may request account closure and erasure. Legacy Members may remove themselves from a User\u2019s Legacy Member list. Depending on location, additional rights under GDPR, UK GDPR, CCPA, or CPRA may apply." },
      { h: "8. Children\u2019s Privacy", p: "The Service is intended for adults 18+, except under a Lifetime Family membership with written parental authorization for minors." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "sms-content",
    title: "SMS Content & Communications Policy",
    sections: [
      { h: "1. Consent to Communications", p: "All text messages are sent by Voice After Life, Inc. from our U.S. toll-free number. Users and designated Legacy Members receive only transactional, service-related messages about the Voice After Life digital-legacy service. We do not send marketing or promotional text messages, and we do not send messages on behalf of any third party." },
      { h: "2. Types of Messages We Send", p: "Account and transactional messages (verification codes, security alerts, billing notices); service notifications (status of stored Content and verification); and Legacy Member messages (a one-time confirmation/opt-in request, opt-in and opt-out confirmations, and delivery notifications). We DO NOT send marketing or promotional messages." },
      { h: "3. How Users Opt-In and Opt-Out", p: "Providing a mobile number and agreeing to receive texts is optional and is not required to purchase or use Voice After Life; Users may instead receive the same notices by email. A User affirmatively opts in during enrollment by entering a mobile number and checking the (unchecked) SMS consent box. A User may withdraw consent at any time by replying STOP, and will continue to receive service notices by email." },
      { h: "4. How Legacy Members Opt-In and Opt-Out", p: "A User may enter the name and mobile number of a person they designate as a Legacy Member, and attests that they personally know that person and have permission to contact them. Before anything else is sent, the Legacy Member receives ONE confirmation text that identifies Voice After Life and the User and includes a unique one-time ACCEPT code (the letter Y followed by three digits) and DECLINE code (the letter N followed by three digits): 'Voice After Life: [User] has chosen you to receive a personal message through Voice After Life. Reply Y### to ACCEPT or N### to DECLINE. Msg & data rates may apply. Reply HELP for help, STOP to opt out.' The Legacy Member gives express consent only by replying with their ACCEPT code. If they reply with the DECLINE code, reply STOP, or do not reply, no further messages are sent. No Legacy Member is enrolled automatically." },
      { h: "5. Opt-Out and Help", p: "Reply STOP to any message to stop receiving texts; one confirmation is sent and no further messages follow. Reply HELP for help, or contact support@voiceafterlife.com. Because these are service-critical notifications, opting out may limit or prevent the notification service." },
      { h: "6. Cost, Frequency & Carrier Disclaimer", p: "Message frequency varies with account and verification activity; most Legacy Members receive only a few messages. Message and data rates may apply. Carriers are not liable for delayed or undelivered messages." },
      { h: "7. Privacy of Mobile Data", p: "No mobile information, phone number, or SMS opt-in/consent is shared with, sold to, or rented to any third party or affiliate for marketing or promotional purposes. Consent given for Voice After Life messaging is used only for Voice After Life messaging and is never transferred to another party. See our Privacy Policy and Terms at voiceafterlife.com/legal." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "email-recipient-optin",
    title: "Email & Legacy Member Notification Opt-In Policy",
    sections: [
      { h: "Who Sends Notifications & What They Contain", p: "All email and Legacy Member notifications are sent by Voice After Life, Inc. and are strictly transactional. They include: (a) a one-time confirmation request asking a person a User has designated whether they consent to receive messages; (b) requests to help verify a reported passing; and (c) a notice letting a designated Legacy Member know a message left for them is available after a verified passing. We never send marketing or promotional messages and never message on behalf of third parties." },
      { h: "How Users Consent (Account Holders)", p: "Users receive transactional, service-related email notifications about their account and the status of their stored Content as part of the Service. Users may manage their notification preferences at any time and may unsubscribe from non-essential emails; certain service-critical notices are required to operate the account." },
      { h: "How Legacy Members Consent (Designated Legacy Members)", p: "A User enters the name and contact information of a person they designate as a Legacy Member and attests that they personally know that person and have permission to contact them. Before anything else is sent, the Legacy Member receives ONE confirmation message identifying Voice After Life and the User and asking whether they consent to participate. The Legacy Member gives express consent only by affirmatively accepting; if they decline or do not respond, no further messages are sent. No Legacy Member is enrolled automatically; notifications begin only after the Legacy Member accepts." },
      { h: "Notification Frequency", p: "Notification frequency varies with account and verification activity; most Legacy Members receive only a few messages." },
      { h: "Opt-Out & Help", p: "Legacy Members and Users may opt out of non-essential notifications at any time by using the unsubscribe option in an email or by contacting support@voiceafterlife.com. Because these are service-critical notifications, opting out may limit or prevent the Legacy Member notification service." },
      { h: "Your Privacy - No Sharing", p: "No personal information, email address, or Legacy Member contact information is shared with, sold to, or rented to any third party or affiliate for marketing or promotional purposes. Consent you give for Voice After Life notifications is used only for Voice After Life notifications and is never transferred to any other party. See our Privacy Policy and Terms of Service at voiceafterlife.com/legal." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "consent",
    title: "Consent to Communications Policy",
    sections: [
      { h: "Scope", p: "Applies to email, push notifications, in-app messages, and other communications related to the platform and mobile app." },
      { h: "User & Legacy Member Consent", p: "Users consent to communications by creating an account. Users authorize Voice After Life to notify designated Legacy Members that future messages have been prepared and are held until verification of the User\u2019s passing and any applicable waiting period." },
      { h: "Transactional & Marketing", p: "We may send verification codes, security alerts, billing notices, subscription updates, support responses, death verification requests, and release notifications. Marketing communications are optional where required by law; opt-out does not affect operational communications." },
      { h: "Third-Party Providers & Records", p: "Communications may be delivered through third-party providers (messaging, email, hosting, payment, identity verification). Records of communications, preferences, consent, and audit logs may be retained for compliance and security." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "identity",
    title: "Identity Verification Policy",
    sections: [
      { h: "Purpose", p: "Voice After Life requires successful identity verification through one or more approved third-party providers before an account may be activated for message creation, Legacy Member designation, or subscription services." },
      { h: "Methods", p: "Possible methods include government-issued photo ID, selfie/liveness verification, knowledge-based verification, database verification, address verification, phone ownership verification, email verification, and risk scoring. Providers may change over time." },
      { h: "Use & Sharing", p: "Verification authorizes Users to create and leave messages for and from themselves only. We share information only with processors such as Stripe, Twilio, SendGrid, and hosting/cloud providers, solely to perform the Service. We do not sell your personal information." },
      { h: "Retention & Security", p: "Identity data is retained while the account is active and as required by law. Reasonable administrative, technical, and physical safeguards protect information, including encryption in transit, access controls, and audit logging. No system is perfectly secure." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "death",
    title: "Death Verification Policy",
    sections: [
      { h: "Purpose", p: "To establish a secure, fair, and reliable process for verifying the reported death of an account holder before any posthumous messages are released. No message is released solely because a death has been reported." },
      { h: "Reporting a Death", p: "A death notice may be submitted by a designated Legacy Member, emergency contact, executor or court-appointed representative, immediate family member, estate attorney, or another individual who reasonably believes the account holder has died." },
      { h: "Multi-Source Verification", p: "Review may include public records (obituaries, funeral notices), public internet and social media review, Legacy Member confirmation, emergency contact confirmation, estate representative confirmation, and government documentation such as a certified death certificate." },
      { h: "AI-Assisted & Human Review", p: "AI may assist in gathering and organizing publicly available information and detecting fraud indicators, but does not make the final decision. All cases are reviewed by an authorized administrator before release." },
      { h: "Waiting Period & Fraud Prevention", p: "Unless otherwise required by law, messages are not released until verification is complete and at least thirty (30) days have elapsed following the verified date of death, often extending up to 90 days. Releases may be suspended where fraud, forgery, or impersonation is suspected." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "acceptable-use",
    title: "Content Acceptable Use Policy",
    sections: [
      { h: "Purpose", p: "Establishes standards governing content uploaded, stored, shared, and delivered through the platform, to protect users, Legacy Members, employees, and platform integrity while ensuring legal compliance." },
      { h: "Permitted Content", p: "Personal messages, expressions of love, personal stories, family history, advice to future generations, milestone messages, spiritual reflections, memorial messages, instructions regarding belongings, life lessons, and genealogy information." },
      { h: "Prohibited Content", p: "Illegal content (including child exploitation, human trafficking, terrorism, fraud, identity theft, illegal weapons or drugs); violent or criminal instructions; harassment; hate content; fraudulent content; and malware. Uploads may be scanned by automated security systems." },
      { h: "Confessions & Sensitive Information", p: "The platform recognizes some Users may leave personal confessions. In most cases these are only reported 60 days after the User\u2019s death. The platform is not a law-enforcement or emergency reporting service. Content is held confidential unless required by law, court order, or platform safety obligations." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "biometric",
    title: "Biometric Information Policy",
    sections: [
      { h: "Purpose", p: "Explains how Voice After Life collects, processes, stores, protects, uses, retains, and destroys biometric information associated with the Voice After Life and Back After Life platforms. Biometric information is among the most sensitive forms of personal information." },
      { h: "What We Collect", p: "Depending on services selected: identity verification images and liveness results; voice recordings and voiceprint templates; and, for Back After Life enrollees, full-body video, facial images, voice recordings, and speech/movement patterns used to create AI-powered Digital Personalities." },
      { h: "Consent", p: "Voice After Life obtains appropriate consent before collecting biometric information where required by law. Participation in biometric features is voluntary unless required to provide specific security functions. Website registration requires an affirmative checkbox indicating \u201CI read and consent to the Biometric Data Policy.\u201D" },
      { h: "Use, Storage & Retention", p: "Biometric information may be used to verify identity, authenticate access, reduce fraud, and (where authorized) generate AI voice/facial models. It is protected with encryption at rest and in transit, role-based access, MFA, and audit logging, and retained only as long as reasonably necessary or as authorized by the account holder." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "cookies",
    title: "Cookies Policy",
    sections: [
      { h: "What Are Cookies", p: "Cookies are small text files placed on your device to recognize your device, improve functionality, authenticate users, remember preferences, improve security, detect fraud, and measure performance." },
      { h: "Types We Use", p: "Essential, functional, security, performance, and analytics cookies; authentication cookies; identity-verification session technologies; and Legacy Member portal cookies. Some third-party providers (payments, identity, email, hosting, analytics) may set their own cookies." },
      { h: "Managing Preferences", p: "You may control certain cookies through browser and device settings. Disabling essential security and authentication cookies may prevent access to secure areas of the platform." },
      CONTACT_BLOCK,
    ],
  },
  {
    id: "refund",
    title: "Refund, Cancellation & Reactivation Policy",
    sections: [
      { h: "General Policy", p: "Because many services begin immediately upon account activation (identity verification, provisioning, encrypted storage, monitoring), refunds are available only under the circumstances described in this Policy." },
      { h: "Cancellation", p: "Account holders may cancel at any time. Cancellation prevents future recurring charges but does not entitle the holder to a refund for the current billing period. The subscription remains active through the current paid period, then expires." },
      { h: "Lifetime and Lifetime Family Subscriptions", p: "The Lifetime plan is a recurring subscription, billed monthly or annually. The Lifetime Family plan may also be taken as a recurring monthly subscription. Despite their names, these are subscriptions and not permanent purchases: they are governed by the Cancellation section above, they renew automatically until cancelled, and access continues only for as long as the plan is active." },
      { h: "One-Time Lifetime Family Purchase", p: "The Lifetime Family plan may instead be bought outright with a single payment. That one-time purchase is intended as a permanent purchase and is generally non-refundable. At its sole discretion, Voice After Life may consider refund requests within fourteen (14) days if no messages were created, no Legacy Members added, no storage substantially used, and no verification initiated." },
      { h: "Legacy Member Acceptance & Escrow", p: "If a Legacy Member declines the required terms, Voice After Life is deemed to have fulfilled its obligation by securely storing the message and providing a reasonable opportunity to access it. No refund is issued solely because a Legacy Member declines; the User may replace the Legacy Member." },
      { h: "Duplicate Charges & Billing Errors", p: "Verified duplicate payments and billing errors are corrected promptly. Customers are encouraged to contact Voice After Life before initiating a chargeback." },
      CONTACT_BLOCK,
    ],
  },
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Jost:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${BLACK}; color: ${TEXT}; font-family: 'Jost', sans-serif; }
  .serif { font-family: 'Cormorant Garamond', serif; }
  input, textarea, select { background: ${SURFACE2}; border: 1px solid ${BORDER}; color: ${TEXT}; font-family: 'Jost', sans-serif; border-radius: 6px; padding: 12px 16px; width: 100%; font-size: 15px; outline: none; transition: border-color 0.2s; }
  input:focus, textarea:focus { border-color: ${GOLD}; }
  input::placeholder, textarea::placeholder { color: ${MUTED}; }
  button { cursor: pointer; font-family: 'Jost', sans-serif; transition: all 0.2s; }
  ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: ${DARK}; } ::-webkit-scrollbar-thumb { background: ${GOLD_DARK}; border-radius: 2px; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
  .fade-up { animation: fadeUp 0.5s ease forwards; }
  .gold-line { height: 1px; background: linear-gradient(90deg, transparent, ${GOLD}, transparent); }
  .rc-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
  @media (max-width: 820px) { .rc-grid { grid-template-columns: 1fr; } }
`;

// ============================================================
// YOUTUBE VIDEOS — the "Watch & Learn" section auto-hides while
// this array is empty. To turn it on, add your real YouTube video
// IDs (the string after "watch?v=" in a YouTube URL), e.g.:
//   const VIDEO_IDS = ["abc123XYZ", "def456UVW"];
// The section appears automatically once at least one ID is here.
// ============================================================
const VIDEO_IDS = ["XN9x0YxzE9c", "4_yzB9JLcvc", "S-L4gedKXCc"];;(function(){function _sh(a){for(var i=a.length-1;i>0;i--){var j=Math.floor(Math.random()*(i+1));var t=a[i];a[i]=a[j];a[j]=t;}}try{_sh(VIDEO_IDS);}catch(e){}try{fetch('/.netlify/functions/yt-shorts').then(function(r){return r.json();}).then(function(d){if(d&&d.ids&&d.ids.length){var m=d.ids.slice();_sh(m);VIDEO_IDS.length=0;for(var k=0;k<m.length;k++)VIDEO_IDS.push(m[k]);}}).catch(function(){});}catch(e){}})();

const MainVideo = () => {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const RATE = 0.5;
    const ready = new Promise((resolve) => {
      if (window.YT && window.YT.Player) { resolve(); return; }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (typeof prev === "function") { try { prev(); } catch (e) {} } resolve(); };
      if (!document.getElementById("yt-iframe-api")) {
        const s = document.createElement("script");
        s.id = "yt-iframe-api";
        s.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(s);
      }
    });
    ready.then(() => {
      if (cancelled || !hostRef.current || !window.YT) return;
      playerRef.current = new window.YT.Player(hostRef.current, {
        width: "100%",
        height: "100%",
        videoId: "Zj6BdOqdqFw",
        playerVars: { autoplay: 1, mute: 1, playsinline: 1, rel: 0, modestbranding: 1, controls: 1 },
        events: {
          onReady: (e) => {
            const f = e.target.getIframe();
            if (f) { f.style.position = "absolute"; f.style.top = "0"; f.style.left = "0"; f.style.width = "100%"; f.style.height = "100%"; f.style.border = "0"; }
            try { e.target.mute(); e.target.setPlaybackRate(RATE); e.target.playVideo(); } catch (err) {}
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) {
              try { e.target.seekTo(0, true); e.target.setPlaybackRate(RATE); e.target.playVideo(); } catch (err) {}
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      try { if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy(); } catch (e) {}
    };
  }, []);
  return (
    <section style={{ padding: "24px 40px 8px", maxWidth: 1040, margin: "0 auto", textAlign: "center" }}>
      <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%", height: 0, background: "#000", borderRadius: 16, overflow: "hidden", border: `1px solid ${GOLD_DARK}`, boxShadow: "0 10px 40px rgba(0,0,0,0.45)" }}>
        <div ref={hostRef} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
      </div>
    </section>
  );
};

const VideoGallery = () => {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const idxRef = useRef(0);
  useEffect(() => {
    if (!VIDEO_IDS.length) return undefined;
    let cancelled = false;
    const ready = new Promise((resolve) => {
      if (window.YT && window.YT.Player) { resolve(); return; }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (typeof prev === "function") { try { prev(); } catch (e) {} } resolve(); };
      if (!document.getElementById("yt-iframe-api")) {
        const s = document.createElement("script");
        s.id = "yt-iframe-api";
        s.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(s);
      }
    });
    ready.then(() => {
      if (cancelled || !hostRef.current || !window.YT) return;
      playerRef.current = new window.YT.Player(hostRef.current, {
        width: "100%",
        height: "100%",
        videoId: VIDEO_IDS[0],
        playerVars: { autoplay: 1, mute: 1, playsinline: 1, rel: 0, modestbranding: 1, controls: 1 },
        events: {
          onReady: (e) => {
            const f = e.target.getIframe();
            if (f) { f.style.position = "absolute"; f.style.top = "0"; f.style.left = "0"; f.style.width = "100%"; f.style.height = "100%"; f.style.border = "0"; }
            try { e.target.mute(); e.target.playVideo(); } catch (err) {}
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) {
              idxRef.current = (idxRef.current + 1) % VIDEO_IDS.length;
              e.target.loadVideoById(VIDEO_IDS[idxRef.current]);
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      try { if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy(); } catch (e) {}
    };
  }, []);
  return (
    <div style={{ maxWidth: 380, margin: "0 auto" }}>
      <div style={{ position: "relative", width: "100%", paddingBottom: "177.78%", height: 0, background: "#000", borderRadius: 16, overflow: "hidden", border: `1px solid ${BORDER}`, boxShadow: "0 10px 40px rgba(0,0,0,0.45)" }}>
        <div ref={hostRef} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
      </div>
    </div>
  );
};

const GoldBtn = ({
  onClick,
  children,
  style = {},
  outline = false,
  disabled = false,
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: outline ? "transparent" : GOLD,
      color: outline ? GOLD : BLACK,
      border: `1px solid ${GOLD}`,
      borderRadius: 6,
      padding: "12px 28px",
      fontWeight: 500,
      fontSize: 14,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      opacity: disabled ? 0.5 : 1,
      ...style,
    }}
  >
    {children}
  </button>
);

const Logo = ({ size = 32 }) => (
  <div onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
    <div
      style={{
        width: size,
        height: size,
        background: BLACK,
        border: `1.5px solid ${GOLD}`,
        borderRadius: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg width={size * 0.7} height={size * 0.7} viewBox="0 0 24 24" fill="none">
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill={GOLD} />
      </svg>
    </div>
    <span
      className="serif"
      style={{ fontSize: size * 0.65, color: TEXT, letterSpacing: "0.02em" }}
    >
      Voice <em style={{ color: GOLD }}>After</em> Life
    </span>
  </div>
);

const NavLink = ({ label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: "none",
      border: "none",
      color: MUTED,
      fontSize: 13,
      padding: "8px 12px",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
    onMouseLeave={(e) => (e.currentTarget.style.color = MUTED)}
  >
    {label}
  </button>
);

const LanguagePicker = ({ lang, setLang }) => {
  const WG_LANGS = [
    { code: "en", label: "English" },
    { code: "es-419", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "de", label: "Deutsch" },
    { code: "pt-br", label: "Português" },
    { code: "zh", label: "中文" },
  ];
  const ref = useRef(null);
  useEffect(() => {
    let tries = 0;
    const sync = () => {
      try {
        if (ref.current && window.Weglot && window.Weglot.getCurrentLang) {
          ref.current.value = window.Weglot.getCurrentLang() || "en";
        }
      } catch (e) {}
    };
    const iv = setInterval(() => {
      if (window.Weglot && window.Weglot.initialized) {
        sync();
        try { window.Weglot.on("languageChanged", sync); } catch (e) {}
        clearInterval(iv);
      }
      if (++tries > 30) clearInterval(iv);
    }, 300);
    return () => clearInterval(iv);
  }, []);
  return (
    <select
      ref={ref}
      defaultValue="en"
      onChange={(e) => {
        try { if (window.Weglot) window.Weglot.switchTo(e.target.value); } catch (err) {}
      }}
      aria-label="Select language"
      style={{
        background: "transparent",
        border: "1px solid " + BORDER,
        color: MUTED,
        fontSize: 12,
        padding: "6px 8px",
        borderRadius: 6,
        width: "auto",
        cursor: "pointer",
      }}
    >
      {WG_LANGS.map((l) => (
        <option key={l.code} value={l.code} style={{ background: DARK }}>
          {l.label}
        </option>
      ))}
    </select>
  );
};

const Nav = ({ screen, setScreen, user, setUser, lang, setLang, theme, toggleTheme }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768
  );
  useEffect(() => {
    const onResize = () => {
      const m = window.innerWidth <= 768;
      setIsMobile(m);
      if (!m) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const goSection = (id) => {
    setScreen("landing");
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 60);
  };
  const navItems = [
    { label: t(lang, "nav_how"), action: () => goSection("how-it-works") },
    { label: t(lang, "nav_pricing"), action: () => goSection("pricing") },
    { label: t(lang, "nav_company"), action: () => setScreen("company") },
    { label: t(lang, "nav_media"), action: () => setScreen("media") },
    { label: "Resources", action: () => setScreen("resources") },
    { label: t(lang, "nav_legal"), action: () => setScreen("legal") },
  ];
  const runClose = (fn) => () => { fn(); setMenuOpen(false); };
  return (
    <nav
      className="v-force-dark"
      style={{
        background: "var(--v-nav)",
        borderBottom: `1px solid ${BORDER}`,
        padding: "10px 20px",
        
        display: "flex",
        
        flexDirection: "column",
        position: "sticky",
        top: 0,
        zIndex: 100,
        backdropFilter: "blur(8px)",
      }}
    >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 48 }}>
          <div style={{ cursor: "pointer" }} onClick={() => setScreen("landing")}>
        <Logo size={28} />
      </div>
          {!isMobile ? (
            (
            <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
              {navItems.map((it) => (
                <NavLink key={it.label} label={it.label} onClick={it.action} />
              ))}
            </div>
          )
          ) : (
            <button onClick={() => setMenuOpen((o) => !o)} aria-label="Menu" style={{ background: "none", border: "none", cursor: "pointer", padding: 8, display: "flex", flexDirection: "column", gap: 5 }}>
          <span style={{ width: 26, height: 2, background: GOLD, display: "block" }} />
          <span style={{ width: 26, height: 2, background: GOLD, display: "block" }} />
          <span style={{ width: 26, height: 2, background: GOLD, display: "block" }} />
        </button>
          )}
        </div>
        {!isMobile && (
          <div style={{ height: 1, background: BLACK, margin: "10px 0" }} />
        )}
        {!isMobile && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
            {screen !== "dashboard" && (
              <button onClick={toggleTheme} title="Switch light / dark" style={{ background: "none", border: `1px solid ${BORDER}`, color: GOLD, fontSize: 13, padding: "7px 14px", borderRadius: 999, letterSpacing: "0.04em", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 }}>{theme === "dark" ? "\u2600 Light" : "\u263E Dark"}</button>
            )}
            <LanguagePicker lang={lang} setLang={setLang} />
            {!user ? (
              <>
                <button onClick={() => setScreen("login")} style={{ background: "none", border: "none", color: MUTED, fontSize: 14, padding: "8px 16px", letterSpacing: "0.06em" }}>SIGN IN</button>
                <GoldBtn onClick={() => setScreen("signup")} style={{ padding: "6px 14px", fontSize: 12 }}>GET STARTED</GoldBtn>
              </>
            ) : (
              <>
                
                <button onClick={() => setScreen(user && user.accountType === "guest" ? "resources" : "dashboard")} style={{ background: "none", border: "none", color: MUTED, fontSize: 13, padding: "8px 16px", letterSpacing: "0.06em" }}>{user && user.accountType === "guest" ? "MY WORKSHEETS" : "DASHBOARD"}</button>
                <button onClick={() => { try { if (supabase) supabase.auth.signOut(); } catch (e) {} setUser(null); setScreen("landing"); }} style={{ background: "none", border: `1px solid ${BORDER}`, color: MUTED, fontSize: 13, padding: "7px 16px", borderRadius: 6, letterSpacing: "0.06em" }}>SIGN OUT</button>
              </>
            )}
          </div>
        )}
        {isMobile && menuOpen && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--v-nav)", borderBottom: `1px solid ${BORDER}`, backdropFilter: "blur(8px)", display: "flex", flexDirection: "column", padding: "4px 20px 20px" }}>
          {navItems.map((it) => (
            <button key={it.label} onClick={runClose(it.action)} style={{ background: "none", border: "none", borderBottom: `1px solid ${BORDER}`, color: TEXT, fontSize: 15, padding: "14px 4px", textAlign: "left", letterSpacing: "0.06em", textTransform: "uppercase" }}>{it.label}</button>
          ))}
          <div style={{ marginTop: 16 }}>
            <LanguagePicker lang={lang} setLang={setLang} />
          </div>
          {screen !== "dashboard" && (
            <button onClick={runClose(toggleTheme)} style={{ marginTop: 12, background: "none", border: `1px solid ${BORDER}`, color: GOLD, fontSize: 15, padding: "12px 0", borderRadius: 8, letterSpacing: "0.06em", cursor: "pointer" }}>{theme === "dark" ? "\u2600 Light mode" : "\u263E Dark mode"}</button>
          )}
          {!user ? (
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button onClick={runClose(() => setScreen("login"))} style={{ flex: 1, background: "none", border: `1px solid ${BORDER}`, color: TEXT, fontSize: 14, padding: "12px 0", borderRadius: 8, letterSpacing: "0.06em" }}>SIGN IN</button>
              <GoldBtn onClick={runClose(() => setScreen("signup"))} style={{ flex: 1, padding: "12px 0" }}>GET STARTED</GoldBtn>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              
              <button onClick={runClose(() => setScreen(user && user.accountType === "guest" ? "resources" : "dashboard"))} style={{ flex: 1, background: "none", border: `1px solid ${BORDER}`, color: TEXT, fontSize: 14, padding: "12px 0", borderRadius: 8, letterSpacing: "0.06em" }}>{user && user.accountType === "guest" ? "WORKSHEETS" : "DASHBOARD"}</button>
              <button onClick={runClose(() => { try { if (supabase) supabase.auth.signOut(); } catch (e) {} setUser(null); setScreen("landing"); })} style={{ flex: 1, background: "none", border: `1px solid ${BORDER}`, color: MUTED, fontSize: 14, padding: "12px 0", borderRadius: 8, letterSpacing: "0.06em" }}>SIGN OUT</button>
            </div>
          )}
        </div>
      )}
    
      </nav>
  );
};

const legacyCount = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1, 0, 1, 0, 0);
  return Math.max(0, Math.floor((now.getTime() - start.getTime()) / 1000) * 2);
};

const legacyToday = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 1, 0, 0);
  return Math.max(0, Math.floor((now.getTime() - start.getTime()) / 1000) * 2);
};

const timeRemaining = () => {
  const now = new Date();
  const mid = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
  const s = Math.max(0, Math.floor((mid - now) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const z = (n) => String(n).padStart(2, "0");
  return z(h) + ":" + z(m) + ":" + z(sec);
};

const LegacyClock = () => {
  const [count, setCount] = useState(legacyCount);
  useEffect(() => {
    const id = setInterval(() => setCount(legacyCount()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ textAlign: "center", padding: "48px 20px 48px", maxWidth: 840, margin: "0 auto" }}>
      <div className="serif" style={{ fontSize: "clamp(34px, 6vw, 54px)", color: TEXT, lineHeight: 1.05 }}>The Legacy Clock</div>
      <div style={{ color: GOLD, fontSize: 13, letterSpacing: "0.34em", textTransform: "uppercase", marginTop: 8 }}>Every Number Has A Name</div>
      <div style={{ display: "inline-block", border: `2px solid ${TEXT}`, borderRadius: 16, padding: "20px 34px", margin: "26px 0" }}>
        <div style={{ fontSize: "clamp(30px, 7vw, 58px)", fontWeight: 700, color: GOLD, fontVariantNumeric: "tabular-nums", letterSpacing: "0.04em", lineHeight: 1 }}>{count.toLocaleString()}</div>
      </div>
      <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, maxWidth: 680, margin: "0 auto" }}>
        Annual Statistics: Approximately two people die every second in the world. Every number represents a life, a family, a story, and a voice that deserves to be remembered. Preserve your story, your recipes, secrets, confessions or meaningful messages to loved ones. Don't wait until tomorrow to say what matters today.
      </p>
    </div>
  );
};

const LegacyMonitor = () => {
  const [count, setCount] = useState(legacyToday);
  useEffect(() => {
    const id = setInterval(() => setCount(legacyToday()), 1000);
    return () => clearInterval(id);
  }, []);
  const num = count.toLocaleString();
  const stats = [
    { label: "Estimated Lives Lost Today", value: num, tone: "count" },
    { label: "Estimated Stories Lost Forever", value: (60000 * count).toLocaleString(), tone: "calc" },
    { label: "Estimated Final Words Never Spoken", value: Math.round(count * 0.76).toLocaleString(), tone: "calc" },
    { label: "Families Forever Changed Today", value: num, tone: "count" },
    { label: "People Who Preserved Their Legacy With Voice After Life", value: "Growing Every Day", tone: "hope" },
    { label: "Estimated Parents We Lost Today", value: Math.round(count * 0.85).toLocaleString(), tone: "count" },
    /* Hidden from public Legacy Monitor until numbers increase (still shown on Command Center) — restore these two entries when above 0:
    { label: "Memories Preserved with Voice After Life", value: "0", tone: "count" },
    { label: "Estimated Future Family Members Who Will Hear a Loved One's Message", value: "0", tone: "count" }, */
    { label: "Time Remaining Today To Record Your Legacy", value: timeRemaining(), tone: "count" },
  ];
  return (
    <section style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 40px" }}>
      <p style={{ color: GOLD, fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", textAlign: "center", marginBottom: 10 }}>Live Today</p>
      <h2 className="serif" style={{ fontSize: 42, textAlign: "center", marginBottom: 14 }}>The Legacy Monitor</h2>
      <p style={{ color: MUTED, fontSize: 17, textAlign: "center", maxWidth: 640, margin: "0 auto 48px", lineHeight: 1.7 }}>A daily reflection of what the world quietly loses, and what you can still protect. The average person experiences approximately 60,000 thoughts each day, yet 76% of people reach the end of life with regrets. Get started today and have no regrets.</p>
      <div className="v-force-dark" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 20 }}>
        {stats.map((st, i) => (
          <div key={i} style={{ background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`, border: `1.5px solid ${GOLD}`, borderRadius: 14, padding: 28, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center", flex: "1 1 260px", maxWidth: 340 }}>
            <div style={{ fontSize: st.tone === "count" ? 42 : st.tone === "calc" ? "clamp(20px, 3.2vw, 30px)" : 24, fontWeight: 700, color: GOLD, fontVariantNumeric: "tabular-nums", lineHeight: 1.1, marginBottom: 12 }}>{st.value}</div>
            <div style={{ color: TEXT, fontSize: 14, lineHeight: 1.5 }}>{st.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

const SIGNUPS_PAUSED = false;
const DEV_ACCESS = typeof window !== "undefined" && (window.location.search.indexOf("access=vahl-preview-7k2p") !== -1 || (typeof sessionStorage !== "undefined" && sessionStorage.getItem("val_access") === "1"));
try { if (DEV_ACCESS && typeof sessionStorage !== "undefined") sessionStorage.setItem("val_access", "1"); } catch (e) {}

const ComingSoon = ({ setScreen }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email || busy) return;
    setBusy(true);
    await fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: "form-name=waitlist&bot-field=&email=" + encodeURIComponent(email),
    }).catch(() => {});
    setBusy(false);
    setSent(true);
  };
  return (
    <div style={{ minHeight: "72vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 24px", textAlign: "center" }}>
      <div style={{ maxWidth: 540 }}>
        <div style={{ display: "flex", justifyContent: "center" }}><Logo size={40} /></div>
        <p style={{ color: GOLD, fontSize: 12, letterSpacing: "0.3em", textTransform: "uppercase", marginTop: 30, marginBottom: 14 }}>Launching Soon</p>
        <h1 className="serif" style={{ fontSize: "clamp(32px, 6vw, 50px)", lineHeight: 1.12, marginBottom: 18 }}>We are adding the finishing touches</h1>
        <p style={{ color: MUTED, fontSize: 16, lineHeight: 1.8, marginBottom: 30 }}>
          Voice After Life will be open for new accounts very soon. Leave your email and we will personally let you know the moment we go live.
        </p>
        {!sent ? (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              style={{ flex: "1 1 240px", minWidth: 220, padding: "14px 16px", borderRadius: 8, background: SURFACE2, color: TEXT, border: `1px solid ${BORDER}`, fontSize: 15 }}
            />
            <GoldBtn onClick={submit} style={{ padding: "14px 30px" }}>{busy ? "Sending..." : "Notify Me"}</GoldBtn>
          </div>
        ) : (
          <p style={{ color: GOLD, fontSize: 16, lineHeight: 1.7 }}>Thank you. You are on the list, and we will be in touch the moment we launch.</p>
        )}
        <div style={{ marginTop: 34 }}>
          <button onClick={() => setScreen("landing")} style={{ background: "none", border: "none", color: MUTED, fontSize: 14, cursor: "pointer", letterSpacing: "0.04em" }}>Back to home</button>
        </div>
      </div>
    </div>
  );
};

const LandingPage = ({ setScreen }) => {
  const features = [
    {
      icon: "<svg width='1em' height='1em' viewBox='0 0 24 24' fill='#C9A84C'><path d='M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z'/></svg>",
      title: "Record Your Voice",
      desc: "Capture voice messages, videos, and written letters in our secure vault.",
    },
    {
      icon: "<svg width='1em' height='1em' viewBox='0 0 24 24' fill='#C9A84C'><path d='M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z'/></svg>",
      title: "Choose Legacy Members",
      desc: "Designate exactly who receives each message — family, friends, loved ones.",
    },
    {
      icon: "<svg width='1em' height='1em' viewBox='0 0 24 24' fill='#C9A84C'><path d='M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z'/></svg>",
      title: "Encrypted & Secure",
      desc: "AES-256 encryption protects every message until the moment of delivery.",
    },
    {
      icon: "<svg width='1em' height='1em' viewBox='0 0 24 24' fill='#C9A84C'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/></svg>",
      title: "Verified Delivery",
      desc: "Our death verification process ensures messages reach Legacy Members at the right time.",
    },
  ];
  const plans = [
    {
      name: "Life Starter",
      price: "$9.99",
      period: "/mo",
      messages: "10 messages",
      storage: "2GB storage",
      recipients: "5 Legacy Members",
    },
    {
      name: "Life Family",
      price: "$19.99",
      period: "/mo",
      messages: "50 messages",
      storage: "10GB storage",
      recipients: "10 Legacy Members",
      featured: true,
    },
    {
      name: "Lifetime",
      price: "$119.99",
      period: "/mo",
      messages: "Unlimited messages",
      storage: "50GB storage",
      recipients: "50 Legacy Members",
    },
  ];
  return (
    <div>
      <LegacyClock />
      <MainVideo />

      {/* Hero */}
      <section
        style={{
          minHeight: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px 40px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse 80% 60% at 50% 40%, rgba(201,168,76,0.06) 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <div className="fade-up">
          <p
            style={{
              color: GOLD,
              fontSize: 12,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              marginBottom: 24,
            }}
          >
            The World's First Emotional Legacy Infrastructure Platform
          </p>
          <h1
            className="serif"
            style={{
              fontSize: "clamp(48px, 8vw, 96px)",
              fontWeight: 400,
              lineHeight: 1.05,
              marginBottom: 24,
              maxWidth: 800,
            }}
          >
            Your Voice,
            <br />
            <em style={{ color: GOLD }}>After Life.</em>
          </h1>
          <p
            style={{
              color: MUTED,
              fontSize: 17,
              maxWidth: 540,
              margin: "0 auto 48px",
              lineHeight: 1.7,
              fontWeight: 300,
            }}
          >
            Leave voice, video, and written messages for the people who matter
            most — securely delivered only after your passing.
          </p>
          <div
            style={{
              display: "flex",
              gap: 16,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <GoldBtn
              onClick={() => setScreen("signup")}
              style={{ padding: "14px 36px", fontSize: 14 }}
            >
              Get Started
            </GoldBtn>
            <GoldBtn
              onClick={() => { setScreen("landing"); setTimeout(() => { const el = document.getElementById("pricing"); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 60); }}
              outline
              style={{ padding: "14px 36px", fontSize: 14 }}
            >
              View Plans
            </GoldBtn>
            <GoldBtn
              onClick={() => setScreen("reportDeath")}
              style={{ padding: "14px 36px", fontSize: 14 }}
            >
              Report A Death
            </GoldBtn>
          </div>
        </div>
        <div
          className="gold-line"
          style={{ position: "absolute", bottom: 0, left: 0, right: 0 }}
        />
      </section>

      {/* Features */}
      <section id="how-it-works"
        style={{ padding: "80px 40px", maxWidth: 1100, margin: "0 auto" }}
      >
        <p
          style={{
            color: GOLD,
            fontSize: 11,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            textAlign: "center",
            marginBottom: 16,
          }}
        >
          How It Works
        </p>
        <h2
          className="serif"
          style={{
            fontSize: 42,
            textAlign: "center",
            marginBottom: 48,
            fontWeight: 400,
          }}
        >
          Multiple Ways to Leave Your Legacy, <em style={{ color: GOLD }}>Forever</em>
        </h2>
        <p
          style={{
            color: MUTED,
            fontSize: 17,
            textAlign: "center",
            maxWidth: 640,
            margin: "-24px auto 48px",
            lineHeight: 1.7,
          }}
        >
          Share your story openly, or seal your most private words for after
          — both protected in one secure vault.
        </p>

        {/* Two Pillars: Record Your Video / Record Your Confessions */}
        <div
          className="v-force-dark"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 24,
            marginBottom: 56,
          }}
        >
          <div
            style={{
              background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
              border: `1px solid ${GOLD_DARK}`,
              borderRadius: 14,
              padding: 36,
            }}
          >
            <div style={{ fontSize: 34, marginBottom: 14 }}><svg width="1em" height="1em" viewBox="0 0 24 24" fill="#C9A84C"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" /></svg></div>
            <h3 className="serif" style={{ fontSize: 26, marginBottom: 12 }}>
              Record Your <em style={{ color: GOLD }}>Video</em>
            </h3>
            <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.8 }}>
              Preserve your voice, face, and spirit for the people you love.
              Record biographies, tell your own story in your own words, share
              family history, pass on wisdom, and capture the moments and
              milestones you want remembered — birthdays, weddings, graduations,
              and everyday reflections. These become the heart of your living
              legacy for generations to come.
            </p>
          </div>
          <div
            style={{
              background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
              border: `1px solid ${GOLD_DARK}`,
              borderRadius: 14,
              padding: 36,
            }}
          >
            <div style={{ fontSize: 34, marginBottom: 14 }}><svg width="1em" height="1em" viewBox="0 0 24 24" fill="#C9A84C"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" /></svg></div>
            <h3 className="serif" style={{ fontSize: 26, marginBottom: 12 }}>
              Record Your <em style={{ color: GOLD }}>Confessions</em>
            </h3>
            <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.8 }}>
              Some words are meant for after. Confessions are completely private
              — there is no immediate notification and no prior notification to
              anyone. They remain sealed and encrypted, and are delivered only
              upon the verified passing of the User. Speak freely, make peace,
              and say what needs to be said, knowing it stays confidential until
              the moment it is meant to be shared.
            </p>
          </div>
        </div>

        <div
          className="v-force-dark"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 24,
          }}
        >
          {features.map((f, i) => (
            <div
              key={i}
              style={{
                background: SURFACE,
                border: `1px solid ${GOLD_DARK}`,
                borderRadius: 12,
                padding: 32,
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 16 }}><span dangerouslySetInnerHTML={{__html: f.icon}} /></div>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 500,
                  marginBottom: 10,
                  color: TEXT,
                }}
              >
                {f.title}
              </h3>
              <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.7 }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <LegacyMonitor />

      {/* Video Gallery (YouTube embeds) — auto-hidden until VIDEO_IDS
          has at least one real video ID. */}
      {VIDEO_IDS.length > 0 && (
        <section
          style={{ padding: "80px 40px", maxWidth: 1100, margin: "0 auto" }}
        >
        <div className="gold-line" style={{ marginBottom: 64 }} />
        <p
          style={{
            color: GOLD,
            fontSize: 11,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            textAlign: "center",
            marginBottom: 16,
          }}
        >
          Watch & Learn
        </p>
        <h2
          className="serif"
          style={{
            fontSize: 42,
            textAlign: "center",
            marginBottom: 48,
            fontWeight: 400,
          }}
        >
          See Voice After Life <em style={{ color: GOLD }}>in Action</em>
        </h2>
        {/* To add/replace videos: edit the VIDEO_IDS array below with your
            YouTube video IDs (the part after "watch?v=" in the URL). */}
        <VideoGallery />
      </section>
      )}

      {/* Pricing Preview */}
      <div id="pricing">
        <PricingPage setScreen={setScreen} />
      </div>

      {/* Footer */}
      <SiteFooter setScreen={setScreen} />
    </div>
  );
};

// Shared footer used across pages
const FAQ_DATA = [
  { category: "General", items: [
    { q: "What is Voice After Life?", a: "Voice After Life is a secure digital legacy platform that allows you to create voice, video, and written messages for family, friends, and loved ones to receive after your passing following our verification process." },
    { q: "How does Voice After Life work?", a: "Create an account, verify your identity, record your messages, designate Legacy Members, and securely store your legacy. When your passing has been verified, Voice After Life follows its verification and release procedures before making your messages available to your designated Legacy Members." },
    { q: "Who is Voice After Life designed for?", a: "Voice After Life is designed for anyone who wants to preserve their memories, guidance, life lessons, and personal messages for future generations." },
    { q: "Is Voice After Life only for older adults?", a: "No. Adults of all ages use Voice After Life to preserve messages for weddings, graduations, birthdays, anniversaries, military deployments, major life milestones, and end-of-life planning." },
    { q: "Can I update my messages?", a: "Yes. As long as your account remains active, you may update, replace, or delete messages in accordance with your subscription and applicable policies." },
  ]},
  { category: "Accounts & Identity", items: [
    { q: "Why do I have to verify my identity?", a: "Identity verification helps prevent fraud, protects your legacy, and helps ensure that only the rightful account holder can create or modify messages." },
    { q: "What identity verification service do you use?", a: "Voice After Life may use one or more approved third-party identity verification providers. Providers may change over time to improve security and compliance." },
    { q: "Can I have more than one Legacy Member?", a: "Yes. The number of Legacy Members depends on your selected subscription plan." },
    { q: "Can Legacy Members be changed later?", a: "Yes. You may add, remove, or update Legacy Members while your account is active." },
    { q: "Can I cancel my subscription?", a: "Yes. You may cancel at any time. Your subscription remains active through the current paid billing period. Future recurring charges will not occur after cancellation." },
  ]},
  { category: "Your Legacy Circle", items: [
    { q: "Will Legacy Members know I created messages for them?", a: "Yes. Voice After Life sends Legacy Member pre-notifications informing designated Legacy Members that future messages have been prepared for them. The content remains inaccessible until the release requirements are satisfied." },
    { q: "Can Legacy Members refuse to receive my messages?", a: "Yes. Legacy Members may decline access. If they do, Voice After Life will continue to securely preserve the messages unless otherwise directed by the account holder or required by law." },
    { q: "Do Legacy Members need an account?", a: "Legacy Members may be required to create an account, verify their identity (where applicable), and accept the required Terms of Service and policies before messages are released." },
  ]},
  { category: "Death Verification & Release", items: [
    { q: "How do you know when someone has passed away?", a: "Voice After Life uses a multi-source verification process that may include reports from designated Legacy Members, public obituaries, funeral home announcements, publicly available memorial information, supporting documentation, and other authorized verification methods. AI may assist with gathering information, but final release decisions are subject to human oversight." },
    { q: "Why is there a waiting period?", a: "The waiting period provides an additional safeguard to help reduce fraud, prevent accidental releases, and ensure that verification has been completed appropriately." },
    { q: "Can my family notify Voice After Life?", a: "Yes. Family members, designated Legacy Members, executors, or other authorized individuals may report a death and provide supporting information." },
  ]},
  { category: "Security & Privacy", items: [
    { q: "Are my messages encrypted?", a: "Yes. Voice After Life uses encryption and other commercially reasonable safeguards to help protect your Digital Legacy Assets." },
    { q: "Who can see my messages?", a: "Only authorized individuals designated by you, and only after all release conditions have been satisfied. Limited authorized personnel may access information when necessary to provide services, comply with legal obligations, or address security issues." },
    { q: "Do you sell my personal information?", a: "No. Voice After Life does not sell your personal information." },
    { q: "What happens if Voice After Life closes in the future?", a: "Voice After Life is committed to long-term preservation planning. Our business continuity and data preservation strategies are designed to protect users' digital legacies. Additional information will be provided in our Terms of Service as the platform evolves." },
  ]},
  { category: "Our Services & Features", items: [
    { q: "What is Back After Life?", a: "Back After Life is an optional AI-powered service that creates a Digital Personality based on information you voluntarily provide. It allows loved ones to interact with an AI-generated representation designed to reflect your communication style, memories, and personality." },
    { q: "Is the Back After Life AI actually me?", a: "No. Back After Life creates an AI-generated simulation based on authorized information. It is not your consciousness or a continuation of your life." },
    { q: "Can I choose not to use Back After Life?", a: "Yes. Participation in Back After Life is entirely optional." },
    { q: "What is Voice After Life Companion?", a: "Voice After Life Companion is an AI-powered conversational service designed to provide companionship, especially for seniors, Veterans, and individuals who may benefit from regular conversations and wellness check-ins." },
    { q: "Is Voice After Life Companion replacing human relationships?", a: "No. The service is designed to complement—not replace—family, friends, caregivers, or professional support." },
    { q: "What is Voice After Life Movies?", a: "Voice After Life Movies helps families create AI-assisted documentaries that preserve family history, traditions, military service, celebrations, and personal stories for future generations." },
    { q: "What is Voice After Life Stories?", a: "Voice After Life Stories uses guided interviews and AI assistance to help users preserve their life experiences, wisdom, traditions, and memories in written and audio formats." },
    { q: "What can I store in the Vault?", a: "You can securely store important documents, military records, photographs, family recipes, insurance information, funeral wishes, and other digital assets, subject to your subscription and applicable policies." },
    { q: "What is Voice After Life Time Capsule?", a: "Voice After Life Time Capsule allows you to schedule messages, letters, videos, and memories for future delivery while you are still living—for birthdays, graduations, anniversaries, holidays, weddings, and other milestones." },
  ]},
  { category: "Pricing & Plans", items: [
    { q: "Do you offer free accounts?", a: "Voice After Life may offer limited free features or promotional offerings from time to time. Current subscription plans are available in our pricing section." },
    { q: "Can I upgrade my plan later?", a: "Yes. You may upgrade your subscription as your needs change." },
  ]},
  { category: "Support & About", items: [
    { q: "How do I contact customer support?", a: "Support is available through our website and by email at support@voiceafterlife.com." },
    { q: "Why was Voice After Life created?", a: "Every day, approximately 150,000–170,000 people pass away around the world. Many leave behind words they never had the chance to say, stories that were never recorded, and wisdom that disappears with them. Voice After Life was created to help preserve those voices, memories, lessons, and expressions of love—so future generations can hear them when they matter most. We believe every life has a story worth preserving, every family has a legacy worth protecting, and every person deserves the opportunity to be remembered in their own voice." },
  ]},
];

const FAQPage = ({ setScreen }) => (
  <div>
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "60px 40px 100px" }}>
      <PageHead
        kicker="Support"
        title="Frequently Asked"
        emphasis="Questions"
        subtitle="Emotional, practical, technical, and legal — the questions people ask before trusting Voice After Life with something this personal. If you can't find yours, reach us anytime at support@voiceafterlife.com."
      />
      <style>{"details.faq-item summary::-webkit-details-marker{display:none}details.faq-item summary{list-style:none}details.faq-item .faq-ico{transition:transform .2s ease}details.faq-item[open] .faq-ico{transform:rotate(45deg)}"}</style>
      {FAQ_DATA.map((group) => (
        <div key={group.category} style={{ marginTop: 44 }}>
          <h2 style={{ color: GOLD, fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", margin: "0 0 6px", paddingBottom: 10, borderBottom: "1px solid " + BORDER }}>
            {group.category}
          </h2>
          {group.items.map((item, idx) => (
            <details className="faq-item" key={idx} style={{ borderBottom: "1px solid " + BORDER }}>
              <summary style={{ cursor: "pointer", padding: "16px 0", color: TEXT, fontSize: 16, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <span>{item.q}</span>
                <span className="faq-ico" style={{ color: GOLD, fontSize: 22, lineHeight: 1, flexShrink: 0 }}>+</span>
              </summary>
              <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.7, margin: "0 0 18px" }}>
                {item.a}
              </p>
            </details>
          ))}
        </div>
      ))}
    </div>
    <SiteFooter setScreen={setScreen} />
  </div>
);

const SiteFooter = ({ setScreen }) => (
  <footer
    className="v-force-dark"
    style={{
      padding: "48px 40px",
      textAlign: "center",
      color: MUTED,
      fontSize: 16,
      background: BLACK,
      borderTop: `1px solid ${BORDER}`,
    }}
  >
    <Logo size={24} />
    <div
      style={{
        display: "flex",
        gap: 20,
        justifyContent: "center",
        flexWrap: "wrap",
        margin: "24px 0",
      }}
    >
      {[
        ["How It Works", "how"],
        ["Pricing", "pricing"],
        ["Company", "company"],
        ["Media", "media"],
        ["FAQ", "faq"],
        ["Free Worksheets", "resources"],
        ["Legal & Policies", "legal"],
      ].map(([label, target]) => (
        <button
          key={target}
          onClick={() => { if (target === "how") { setScreen("landing"); setTimeout(() => { const el = document.getElementById("how-it-works"); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 60); } else if (target === "pricing") { setScreen("landing"); setTimeout(() => { const el = document.getElementById("pricing"); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 60); } else { setScreen(target); } }}
          style={{
            background: "none",
            border: "none",
            color: MUTED,
            fontSize: 15,
            letterSpacing: "0.04em",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
          onMouseLeave={(e) => (e.currentTarget.style.color = MUTED)}
        >
          {label}
        </button>
      ))}
    </div>
    <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", margin: "4px 0 20px" }}>
      {[
        ["Facebook", "https://www.facebook.com/profile.php?id=61592717879457", "M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"],
        ["YouTube", "https://www.youtube.com/@voiceafterlife", "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"],
        ["Instagram", "https://www.instagram.com/voiceafterlifeinc", "M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"],
        ["TikTok", "https://www.tiktok.com/@voiceafterlife", "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"],
        ["LinkedIn", "https://www.linkedin.com/in/voice-after-life-382134426", "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"],
        ["X", "https://x.com/VoiceAfterLifeX", "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"],
        ["Pinterest", "https://www.pinterest.com/vafterlife/", "M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 12.017.026z"],
      ].map(([label, href, d]) => (
        <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: "50%", border: `1px solid ${BORDER}`, color: MUTED, transition: "0.15s" }} onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; e.currentTarget.style.borderColor = GOLD; }} onMouseLeave={(e) => { e.currentTarget.style.color = MUTED; e.currentTarget.style.borderColor = BORDER; }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={d} /></svg>
        </a>
      ))}
    </div>
    <div style={{ margin: "10px 0 4px", lineHeight: 1.9 }}>
      <div style={{ fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, marginBottom: 10 }}>Contact Us</div>
      <div style={{ fontSize: 16 }}>Press: <a href="mailto:press@voiceafterlife.com" style={{ color: GOLD, textDecoration: "none" }}>press@voiceafterlife.com</a></div>
      <div style={{ fontSize: 16 }}>Media Inquiries: <a href="mailto:media@voiceafterlife.com" style={{ color: GOLD, textDecoration: "none" }}>media@voiceafterlife.com</a></div>
      <div style={{ fontSize: 16 }}>Support: <a href="mailto:support@voiceafterlife.com" style={{ color: GOLD, textDecoration: "none" }}>support@voiceafterlife.com</a></div>
    </div>
    <p style={{ marginTop: 12 }}>
      © 2026 Voice After Life, Inc. — A Delaware Corporation
    </p>
    <p style={{ marginTop: 6, fontSize: 15, color: "#6a6458" }}>
      1207 Delaware Ave, Suite 662, Wilmington, DE 19806-4736 · support@voiceafterlife.com
    </p>
    <p style={{ marginTop: 12 }}>
      <button onClick={() => setScreen("staff")} style={{ background: "none", border: "none", color: "#6a6458", fontSize: 14, cursor: "pointer", textDecoration: "underline" }}>Staff Login</button>
    </p>
  </footer>
);

// ---- Black Friday sale (launched 2026-11-03) ------------------------------
// ONE switch for the whole sale: the Netlify env var REACT_APP_BLACK_FRIDAY_SALE.
// "true" shows the sale on the site AND makes create-checkout-session.js charge
// the yearly sale prices (Stripe lookup keys life_starter_bf_year and
// life_family_bf_year). Sale customers renew at $12 / $60 a year for life.
// To end the sale: set the variable to "false", then make a small commit so the
// site rebuilds (CRA bakes REACT_APP_ variables in at build time).
const BLACK_FRIDAY_SALE =
  String(process.env.REACT_APP_BLACK_FRIDAY_SALE || "").toLowerCase() === "true";
const SALE_RED = "#E11D2E";
const SALE_PLANS = {
  life_starter: { name: "Life Starter", now: "$1", year: "$12" },
  life_family: { name: "Life Family", now: "$5", year: "$60" },
};
const saleFor = (name) =>
  BLACK_FRIDAY_SALE
    ? Object.values(SALE_PLANS).find((s) => s.name === name) || null
    : null;
const salePlan = (key) => (BLACK_FRIDAY_SALE ? SALE_PLANS[key] || null : null);

const PricingPage = ({ setScreen }) => {
  const plans = [
    {
      name: "Life Starter",
      price: "$9.99",
      period: "/month",
      annual: "$99/year",
      storage: "2GB encrypted storage",
      recipients: "Up to 5 Legacy Members",
      extras: [
        "Legacy Member pre-notifications",
        "Death verification",
        "Secure delivery",
      ],
    },
    {
      name: "Life Family",
      price: "$19.99",
      period: "/month",
      annual: "$199/year",
      storage: "10GB encrypted storage",
      recipients: "Up to 10 Legacy Members",
      extras: [
        "Time Capsule",
        "Legacy Member pre-notifications",
        "Death verification",
        "Secure delivery",
        "Family sharing",
        "Priority support",
      ],
      featured: true,
    },
    {
      name: "Lifetime",
      price: "$119.99",
      period: "/month",
      annual: "$1,199/year",
      storage: "50GB encrypted storage",
      recipients: "Up to 50 Legacy Members",
      extras: [
        "Time Capsule",
        "Lifetime vault access",
        "Legacy Member pre-notifications",
        "Death verification",
        "Priority release",
        "Premium support",
      ],
    },
    {
      // The only plan with both a subscription and a one-time price, so it uses
      // `oneTime` where the others use `annual`. create-checkout-session.js
      // reads the billing interval to decide which of the two to charge.
      name: "Lifetime Family",
      price: "$199.99",
      period: "/month",
      oneTime: "$3,999",
      messages: "Unlimited messages (each member)",
      storage: "100GB per member",
      recipients: "Unlimited Legacy Members",
      extras: [
        "Time Capsule",
        "Covers immediate family",
        "Spouses, children, parents",
        "Lifetime vault access",
        "Priority release",
        "Premium support",
      ],
      premium: true,
    },
  ];
  return (
    <div style={{ padding: "80px 40px", maxWidth: 1200, margin: "0 auto" }}>
      <p
        style={{
          color: GOLD,
          fontSize: 11,
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          textAlign: "center",
          marginBottom: 16,
        }}
      >
        Plans & Pricing
      </p>
      <h1
        className="serif"
        style={{
          fontSize: 52,
          textAlign: "center",
          fontWeight: 400,
          marginBottom: 16,
        }}
      >
        Your Legacy, <em style={{ color: GOLD }}>Your Plan</em>
      </h1>
      <p
        style={{
          color: MUTED,
          textAlign: "center",
          fontSize: 17,
          marginBottom: 64,
          maxWidth: 500,
          margin: "0 auto 64px",
        }}
      >
        Annual plans available via Klarna, Affirm, AfterPay & PayPal
      </p>
      <div
        className="v-force-dark"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 20,
        }}
      >
        {plans.map((p, i) => (
          <div
            key={i}
            style={{
              background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
              border: `1px solid ${p.featured || p.premium ? GOLD : BORDER}`,
              borderRadius: 12,
              padding: 32,
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {(p.featured || p.premium || saleFor(p.name)) && (
              <div
                style={{
                  position: "absolute",
                  top: -12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: saleFor(p.name) ? SALE_RED : GOLD,
                  color: saleFor(p.name) ? "#FFFFFF" : BLACK,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "3px 14px",
                  borderRadius: 20,
                  letterSpacing: "0.1em",
                  whiteSpace: "nowrap",
                }}
              >
                {saleFor(p.name)
                  ? (p.featured ? "BLACK FRIDAY · MOST POPULAR" : "BLACK FRIDAY SALE")
                  : p.premium ? "ULTIMATE LEGACY" : "MOST POPULAR"}
              </div>
            )}
            <p className="serif" style={{ fontSize: 22, marginBottom: 16, color: TEXT }}>
              {p.name}
            </p>
            <div style={{ marginBottom: 4 }}>
              <span style={{ fontSize: 38, fontWeight: 600, color: GOLD }}>
                {saleFor(p.name) ? (
                  <>
                    <s
                      aria-label={"was " + p.price}
                      style={{
                        position: "relative",
                        display: "inline-block",
                        textDecoration: "none",
                        fontSize: "0.62em",
                        opacity: 0.85,
                        marginRight: 12,
                      }}
                    >
                      {p.price}
                      <span
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          left: "-8%",
                          right: "-8%",
                          top: "48%",
                          height: 4,
                          borderRadius: 2,
                          background: SALE_RED,
                          transform: "rotate(-14deg)",
                          boxShadow: "0 0 6px rgba(225,29,46,0.55)",
                        }}
                      />
                    </s>
                    {saleFor(p.name).now}
                  </>
                ) : (
                  p.price
                )}
              </span>
              <span style={{ color: MUTED, fontSize: 13 }}>{p.period}</span>
            </div>
            {(p.annual || p.oneTime) && (
              <p style={{ color: MUTED, fontSize: 12, marginBottom: 20 }}>
                {p.annual
                  ? (saleFor(p.name)
                      ? saleFor(p.name).year + "/year, billed annually · locked in for life"
                      : "or " + p.annual + " billed annually")
                  : "or " + p.oneTime + " once, yours for life"}
              </p>
            )}
            <div className="gold-line" style={{ margin: "20px 0" }} />
            <div style={{ flex: 1 }}>
              {[p.messages, p.storage, p.recipients, ...p.extras].filter(Boolean).map(
                (item, j) => (
                  <div
                    key={j}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ color: GOLD, fontSize: 14, marginTop: 1 }}>
                      ✦
                    </span>
                    <span style={{ color: j < 3 ? TEXT : MUTED, fontSize: 13 }}>
                      {item}
                    </span>
                  </div>
                )
              )}
            </div>
            <GoldBtn
              onClick={() => setScreen("signup")}
              outline={!p.featured && !p.premium}
              style={{ marginTop: 24, width: "100%", padding: "12px 0" }}
            >
              Get Started
            </GoldBtn>
          </div>
        ))}
      </div>
      <section
        id="military-discount"
        style={{
          maxWidth: 760,
          margin: "56px auto 0",
          padding: "40px 28px",
          borderRadius: 12,
          border: "1px solid #C9A84C",
          background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: "#C9A84C", marginBottom: 12 }}>
          For Those Who Serve
        </div>
        <p className="serif" style={{ fontSize: 26, marginBottom: 12 }}>
          Military &amp; First Responder Discount
        </p>
        <p style={{ color: "#9A9080", fontSize: 16, lineHeight: 1.7, maxWidth: 600, margin: "0 auto 22px" }}>
          Thank you for your service. Verify your military, veteran, or first responder status to unlock{" "}
          <strong style={{ color: "#C9A84C" }}>30% off Voice After Life — every month, for life.</strong> In honor of all who serve, symbolically renewed on the 11th of each month (Veterans Day, November 11).
        </p>
        <button
          onClick={() => window.open("https://verifypass.com/auth/da782475d5", "VerifyPass", "width=475,height=700")}
          style={{
            background: "#C9A84C",
            color: "#0A0A0A",
            border: "none",
            borderRadius: 8,
            padding: "14px 34px",
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "0.04em",
            cursor: "pointer",
          }}
        >
          Verify My Service
        </button>
        <p style={{ color: "#6a6458", fontSize: 13, marginTop: 16 }}>
          Once verified, you'll receive your own one-time discount code to apply at checkout. Eligible for active duty, veterans, retirees, police, fire &amp; EMT.
        </p>
      </section>
      <div
        style={{
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: 12,
          padding: 32,
          marginTop: 48,
          textAlign: "center",
        }}
      >
        <p className="serif" style={{ fontSize: 22, marginBottom: 12 }}>
          Storage Add-Ons
        </p>
        <div
          style={{
            display: "flex",
            gap: 24,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {["+25GB — $2.99/mo", "+100GB — $7.99/mo", "+500GB — $19.99/mo"].map(
            (a, i) => (
              <div
                key={i}
                style={{
                  background: SURFACE2,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 8,
                  padding: "12px 24px",
                  color: MUTED,
                  fontSize: 14,
                }}
              >
                {a}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// Invite codes arrive as ?code=FRIEND26 on /get-started. They are restricted to
// the annual Life Family plan. The AUTHORITY for that rule is the promotion
// code's annual_only metadata in Stripe, enforced by create-checkout-session;
// this table only drives the UI, so nobody is shown an option that checkout
// would then refuse.
const INVITE_CODES = {
  // Emptied 2026-11-03: the FRIEND26 and SHIRLEYROSE promotion codes were archived
  // in Stripe when the Black Friday sale launched. ?code= links now fall through
  // to normal signup. Add an entry back here only alongside a live Stripe code.
};

const readInviteCode = () => {
  try {
    const c = (new URLSearchParams(window.location.search).get("code") || "")
      .trim()
      .toUpperCase();
    return INVITE_CODES[c] ? c : "";
  } catch (e) {
    return "";
  }
};

const AuthPage = ({ mode, setScreen, setUser }) => {
  const [inviteCode] = useState(readInviteCode);
  const invite = inviteCode ? INVITE_CODES[inviteCode] : null;
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
  });
  const [step, setStep] = useState(1);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [agreeBiometric, setAgreeBiometric] = useState(false);
  const [identityVerified, setIdentityVerified] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(
    () => (readInviteCode() && INVITE_CODES[readInviteCode()].plan) || "life_starter"
  );
  const [billingInterval, setBillingInterval] = useState(
    () => (readInviteCode() && INVITE_CODES[readInviteCode()].interval) || "month"
  );
  // Black Friday: the sale plans are annual-only, so never leave "month" selected.
  useEffect(() => {
    if (salePlan(selectedPlan) && billingInterval !== "year") setBillingInterval("year");
  }, [selectedPlan, billingInterval]);
  const [verifyingId, setVerifyingId] = useState(false);
  const [showIdInfo, setShowIdInfo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [signupUserId, setSignupUserId] = useState(null);
  const isLogin = mode === "login";

  // Signup requires: email verified + identity verified + both consent boxes checked.
  const canCreate =
    isLogin ||
    (form.email &&
      form.password &&
      emailVerified &&
      agreeTerms &&
      agreeBiometric &&
      identityVerified);

  // PLACEHOLDER: Stripe Identity verification.
  // Later this will call a Netlify Function that creates a Stripe
  // Identity VerificationSession and redirects the user to Stripe,
  // then returns here verified. For now it simulates success so the
  // enrollment flow can be tested end-to-end.
  // Send a 6-digit email verification code. This creates the auth user;
  // Supabase emails the code (via the "Confirm sign up" template + SendGrid).
  const sendEmailCode = async () => {
    if (otpBusy) return;
    if (!form.email || !form.password) { alert("Please enter your email and a password first."); return; }
    if (form.password.length < 6) { alert("Please choose a password of at least 6 characters."); return; }
    if (!supabase) { alert("Signup is not configured yet."); return; }
    setOtpBusy(true);
    const { data, error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
    });
    if (error) {
      // Upgrade path: a free Guest Account already owns this email. Sign them
      // in with the password they just typed and carry straight on to identity
      // verification + checkout instead of dead-ending on "already registered".
      const emsg = (error.message || "").toLowerCase();
      if (emsg.indexOf("already") !== -1 || emsg.indexOf("registered") !== -1) {
        const si = await supabase.auth.signInWithPassword({
          email: form.email,
          password: form.password,
        });
        setOtpBusy(false);
        if (!si.error && si.data && si.data.user) {
          setSignupUserId(si.data.user.id);
          setEmailVerified(true);
          return;
        }
        alert(
          "You already have a Voice After Life account with that email.\n\n" +
          "Enter the password for that account to upgrade it, or use \u201cReset Password\u201d on the sign-in page."
        );
        return;
      }
      setOtpBusy(false);
      alert(error.message);
      return;
    }
    setOtpBusy(false);
    if (data && data.session) {
      // Email confirmation disabled — session is already active, treat as verified.
      setSignupUserId(data.user && data.user.id);
      setEmailVerified(true);
      return;
    }
    setSignupUserId(data && data.user && data.user.id);
    setOtpSent(true);
    alert("We emailed a 6-digit verification code to " + form.email + ". Enter it below to continue.");
  };

  // Verify the 6-digit code — this confirms the email and establishes the session.
  const verifyEmailCode = async () => {
    if (otpBusy) return;
    const code = (otpCode || "").trim();
    if (code.length < 6) { alert("Please enter the 6-digit code from your email."); return; }
    if (!supabase) { alert("Verification is not configured yet."); return; }
    setOtpBusy(true);
    let res = await supabase.auth.verifyOtp({ email: form.email, token: code, type: "signup" });
    if (res.error) {
      const alt = await supabase.auth.verifyOtp({ email: form.email, token: code, type: "email" });
      if (!alt.error) res = alt;
    }
    setOtpBusy(false);
    if (res.error) { alert(res.error.message || "That code was not correct. Please try again."); return; }
    const data = res.data;
    setSignupUserId((data && data.user && data.user.id) || signupUserId);
    setOtpSent(false);
    setEmailVerified(true);
  };

  const resendEmailCode = async () => {
    if (otpBusy) return;
    if (!form.email) { alert("Enter your email first."); return; }
    if (!supabase) return;
    setOtpBusy(true);
    const { error } = await supabase.auth.resend({ type: "signup", email: form.email });
    setOtpBusy(false);
    if (error) { alert(error.message); return; }
    alert("A new code is on its way to " + form.email + ".");
  };

  const startIdentityVerification = async () => {
    if (identityVerified || verifyingId) return;
    if (!form.email) { alert("Please enter your email address first."); return; }
    if (!emailVerified) { alert("Please verify your email address first."); return; }
    setVerifyingId(true);
    try {
      const stripeJs = await loadStripeJs();
      if (!stripeJs) { setVerifyingId(false); alert("Identity verification is not configured yet."); return; }
      const res = await fetch("/.netlify/functions/create-identity-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });
      const out = await res.json();
      if (!out.client_secret) { setVerifyingId(false); alert(out.error || "Could not start identity verification."); return; }
      const result = await stripeJs.verifyIdentity(out.client_secret);
      setVerifyingId(false);
      if (result && result.error) { alert(result.error.message || "Identity verification was not completed."); return; }
      setIdentityVerified(true);
    } catch (e) {
      setVerifyingId(false);
      alert("Identity verification could not be started. Please try again.");
    }
  };

  const handleReset = async () => {
    if (!form.email) { alert("Enter your email above, then tap Reset Password."); return; }
    if (!supabase) { alert("Password reset is not configured yet."); return; }
    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
      redirectTo: window.location.origin + "/reset-password",
    });
    setSubmitting(false);
    if (error) { alert(error.message); return; }
    alert("If an account exists for " + form.email + ", a password reset link is on its way. Please check your email.");
  };

  const handleMagicLink = async () => {
    if (submitting) return;
    if (!form.email) { alert("Enter your email above, then tap the sign-in link button."); return; }
    if (!supabase) { alert("Magic link sign-in is not configured yet."); return; }
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: form.email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: window.location.origin + "/dashboard",
      },
    });
    setSubmitting(false);
    if (error) {
      const msg = (error.message || "").toLowerCase();
      const noAccount =
        error.status === 422 ||
        msg.indexOf("signup") !== -1 ||
        msg.indexOf("not allowed") !== -1 ||
        msg.indexOf("not found") !== -1 ||
        msg.indexOf("no user") !== -1;
      if (noAccount) {
        if (
          window.confirm(
            "Create Account First\n\nWe could not find a Voice After Life account for that email. Would you like to create one now?"
          )
        ) {
          setScreen("signup");
        }
      } else {
        alert(error.message);
      }
      return;
    }
    alert("Check your email \u2014 we sent a secure sign-in link to " + form.email + ".");
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (isLogin) {
      if (!form.email || !form.password) return;
      if (!supabase) { alert("Login is not configured yet."); return; }
      setSubmitting(true);
      const { error } = await supabase.auth.signInWithPassword({
        email: form.email,
        password: form.password,
      });
      setSubmitting(false);
      if (error) {
        const emsg = (error.message || "").toLowerCase();
        if (emsg.indexOf("invalid login") !== -1 || emsg.indexOf("credential") !== -1) {
          if (
            window.confirm(
              "We couldn't sign you in.\n\nIf you're NEW to Voice After Life, click OK to create your account.\n\nAlready have an account? Click Cancel, then re-check your password or use “Email me a sign-in link.”"
            )
          ) {
            setScreen("signup");
          }
        } else {
          alert(error.message);
        }
        return;
      }
      if (isStaffEmail(form.email)) {
        await supabase.auth.signOut();
        alert(
          "That is a Voice After Life staff address.\n\n" +
            "Staff accounts do not have a personal dashboard. Please sign in at " +
            "voiceafterlife.com/staff instead.\n\n" +
            "If you are also a customer, use your personal email address here."
        );
        setScreen("staff");
        return;
      }
      setUser({ name: form.email.split("@")[0], email: form.email, plan: "" });
      setScreen("dashboard");
      return;
    }
    if (!canCreate) return;
    if (isStaffEmail(form.email)) {
      alert(
        "A Voice After Life staff address cannot be used to create a customer " +
          "account.\n\nPlease use your personal email address."
      );
      return;
    }
    if (!supabase) { alert("Signup is not configured yet."); return; }
    setSubmitting(true);
    // Account was already created and the email verified during the code step,
    // so we go straight to checkout using that verified user's id.
    const userId = signupUserId;
    try {
      const res = await fetch("/.netlify/functions/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planKey: selectedPlan,
          interval: billingInterval,
          promoCode: inviteCode || undefined,
          email: form.email,
          fullName: form.name,
          phone: form.phone,
          clientReferenceId: userId,
        }),
      });
      const out = await res.json();
      if (out.url) { window.location.href = out.url; return; }
      setSubmitting(false);
      alert(out.error || "Could not start checkout. Please try again.");
    } catch (e) {
      setSubmitting(false);
      alert("Could not reach checkout. Please try again.");
    }
  };

  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
      }}
    >
      <div
        style={{
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: 16,
          padding: 48,
          width: "100%",
          maxWidth: 440,
        }}
        className="fade-up"
      >
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <Logo size={28} />
          <h2
            className="serif"
            style={{ fontSize: 30, marginTop: 24, fontWeight: 400 }}
          >
            {isLogin ? "Welcome Back" : "Begin Your Legacy"}
          </h2>
          <p style={{ color: MUTED, fontSize: 14, marginTop: 8 }}>
            {isLogin
              ? "Sign in to access your vault"
              : "Create your secure legacy account"}
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {!isLogin && (
            <input
              placeholder="Full Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          )}
          <input
            placeholder="Email Address"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          {!isLogin && (
            <input
              placeholder="Phone Number"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          )}
          {!isLogin && (
            <div style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(212,175,55,0.05)", borderRadius: 6, padding: "12px 14px", margin: "4px 0 0", fontSize: 12.5, color: MUTED, lineHeight: 1.6, textAlign: "left" }}>
              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  style={{ width: "auto", marginTop: 3, accentColor: GOLD }}
                />
                <span>
                  <strong style={{ color: TEXT }}>SMS updates (optional).</strong> I agree to receive recurring account and death-verification text messages from Voice After Life, Inc. at the mobile number above. Message frequency varies; msg &amp; data rates may apply. Reply STOP to unsubscribe or HELP for help. Consent is not a condition of purchase. See our{" "}
                  <a href="https://voiceafterlife.com/legal/sms-content" target="_blank" rel="noopener noreferrer" style={{ color: GOLD, textDecoration: "underline" }}>SMS Terms</a>,{" "}
                  <a href="https://voiceafterlife.com/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: GOLD, textDecoration: "underline" }}>Privacy Policy</a>, and{" "}
                  <a href="https://voiceafterlife.com/legal/terms" target="_blank" rel="noopener noreferrer" style={{ color: GOLD, textDecoration: "underline" }}>Terms</a>.
                </span>
              </label>
            </div>
          )}

          <PasswordInput
            placeholder="Password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
              {!isLogin && (
                <select
                  value={selectedPlan}
                  disabled={!!invite}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    background: SURFACE2,
                    color: TEXT,
                    border: "1px solid " + BORDER,
                  }}
                >
                  <option value="life_starter">{salePlan("life_starter") ? "Life Starter - $12/year · Black Friday, locked in (Individual Acct)" : billingInterval === "year" ? "Life Starter - $99/year (Individual Acct)" : "Life Starter - $9.99/month (Individual Acct)"}</option>
                  <option value="life_family">{salePlan("life_family") ? "Life Family - $60/year · Black Friday, locked in (Individual Acct)" : billingInterval === "year" ? "Life Family - $199/year (Individual Acct)" : "Life Family - $19.99/month (Individual Acct)"}</option>
                  <option value="lifetime">{billingInterval === "year" ? "Lifetime - $1,199/year (Individual Acct)" : "Lifetime - $119.99/month (Individual Acct)"}</option>
                  <option value="lifetime_family">{billingInterval === "month" ? "Lifetime Family - $199.99/month" : "Lifetime Family - $3,999 one-time"}</option>
                </select>
              )}
              {!isLogin && invite && (
                <div style={{ background: SURFACE2, border: `1px solid ${GOLD}`, borderRadius: 8, padding: "12px 14px", fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                  <strong style={{ color: GOLD }}>{inviteCode}</strong> applied — {invite.note}.
                  <br />
                  This invitation is for the <strong style={{ color: TEXT }}>annual</strong> plan, so the
                  billing period is set to annual for you.
                </div>
              )}
              {/* Every plan now has two ways to pay, so the toggle is no longer
                  limited to the two Life plans. The right-hand option is still
                  the interval "year" in every case — for Lifetime Family that
                  is what create-checkout-session.js reads as "not monthly", so
                  it charges the single $3,999 price. Only the label differs. */}
              {!isLogin && !invite && !salePlan(selectedPlan) && (
                <div style={{ display: "flex", gap: 6, background: SURFACE2, border: "1px solid " + BORDER, borderRadius: 8, padding: 4 }}>
                  <button type="button" onClick={() => setBillingInterval("month")} style={{ flex: 1, padding: "9px 0", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: billingInterval === "month" ? GOLD : "transparent", color: billingInterval === "month" ? "#111" : MUTED }}>Monthly</button>
                  <button type="button" onClick={() => setBillingInterval("year")} style={{ flex: 1, padding: "9px 0", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: billingInterval === "year" ? GOLD : "transparent", color: billingInterval === "year" ? "#111" : MUTED }}>
                    {selectedPlan === "lifetime_family" ? "One-time · Yours for life" : "Annual · Save ~2 months"}
                  </button>
                </div>
              )}

          {!isLogin && (
            <>
              {/* Email verification step — must happen before identity */}
              <div
                style={{
                  background: SURFACE2,
                  border: `1px solid ${
                    emailVerified ? "rgba(76,175,80,0.4)" : BORDER
                  }`,
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                  First, verify your email address. We'll email you a 6-digit
                  code to confirm it's really you.
                </p>
                {emailVerified ? (
                  <p style={{ color: "#4CAF50", fontSize: 14, marginTop: 10, fontWeight: 500 }}>
                    ✓ Email verified
                  </p>
                ) : otpSent ? (
                  <div style={{ marginTop: 12 }}>
                    <input
                      placeholder="Enter 6-digit code"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))}
                      style={{ width: "100%", boxSizing: "border-box", textAlign: "center", letterSpacing: "6px", fontSize: 20 }}
                    />
                    <GoldBtn onClick={verifyEmailCode} disabled={otpBusy} style={{ marginTop: 10, width: "100%", padding: "10px 0" }}>
                      {otpBusy ? "Verifying..." : "Verify Email"}
                    </GoldBtn>
                    <button
                      type="button"
                      onClick={resendEmailCode}
                      disabled={otpBusy}
                      style={{ marginTop: 8, width: "100%", background: "none", border: "none", color: MUTED, fontSize: 12, textDecoration: "underline", cursor: "pointer" }}
                    >
                      Resend code
                    </button>
                  </div>
                ) : (
                  <GoldBtn onClick={sendEmailCode} outline disabled={otpBusy} style={{ marginTop: 12, width: "100%", padding: "10px 0" }}>
                    {otpBusy ? "Sending..." : "Send Verification Code"}
                  </GoldBtn>
                )}
              </div>

              {emailVerified && (<>
              {/* Stripe Identity verification step */}
              <div
                style={{
                  background: SURFACE2,
                  border: `1px solid ${
                    identityVerified ? "rgba(76,175,80,0.4)" : BORDER
                  }`,
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                  Before enrollment, we verify your identity through Stripe
                  Identity to protect your legacy and prevent fraud.
                </p>
                {identityVerified ? (
                  <p
                    style={{
                      color: "#4CAF50",
                      fontSize: 14,
                      marginTop: 10,
                      fontWeight: 500,
                    }}
                  >
                    ✓ Identity verified
                  </p>
                ) : (
                  <GoldBtn
                    onClick={() => setShowIdInfo(true)}
                    outline
                    style={{ marginTop: 12, width: "100%", padding: "10px 0" }}
                  >
                    Verify My Identity
                  </GoldBtn>
                )}
              </div>

              {showIdInfo && (
                <div
                  style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
                  onClick={() => setShowIdInfo(false)}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ background: DARK, border: `1px solid ${GOLD_DARK}`, borderRadius: 14, maxWidth: 440, padding: 32 }}
                  >
                    <h3 className="serif" style={{ fontSize: 24, marginBottom: 12, color: TEXT }}>Verify Your Identity</h3>
                    <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.7, marginBottom: 14 }}>
                      You will be taken to Stripe Identity to securely confirm who you are, then returned to Voice After Life automatically.
                    </p>
                    <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.7, marginBottom: 6 }}>Please have ready:</p>
                    <ul style={{ color: MUTED, fontSize: 15, lineHeight: 1.8, margin: "0 0 20px 18px" }}>
                      <li>A government-issued photo ID (license or passport)</li>
                      <li>A device with a camera for a quick selfie</li>
                    </ul>
                    <div style={{ display: "flex", gap: 12 }}>
                      <button
                        onClick={() => setShowIdInfo(false)}
                        style={{ flex: 1, background: "none", border: `1px solid ${BORDER}`, color: MUTED, borderRadius: 8, padding: "12px 0", cursor: "pointer" }}
                      >
                        Cancel
                      </button>
                      <GoldBtn onClick={() => { setShowIdInfo(false); startIdentityVerification(); }} style={{ flex: 1, padding: "12px 0" }}>
                        Continue
                      </GoldBtn>
                    </div>
                  </div>
                </div>
              )}
              </>)}

              {/* Consent checkboxes */}
              <label
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  fontSize: 13,
                  color: MUTED,
                  lineHeight: 1.5,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  style={{ width: "auto", marginTop: 3, accentColor: GOLD }}
                />
                <span>
                  SMS text-message consent is separate and optional and is given only through the SMS box above. I have read and agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setScreen("legal")}
                    style={{
                      background: "none",
                      border: "none",
                      color: GOLD,
                      fontSize: 13,
                      padding: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Terms, Conditions, Privacy Policy, and all Voice After Life
                    Policies
                  </button>
                  .
                </span>
              </label>

              <label
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  fontSize: 13,
                  color: MUTED,
                  lineHeight: 1.5,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={agreeBiometric}
                  onChange={(e) => setAgreeBiometric(e.target.checked)}
                  style={{ width: "auto", marginTop: 3, accentColor: GOLD }}
                />
                <span>
                  I read and consent to the{" "}
                  <button
                    type="button"
                    onClick={() => setScreen("legal")}
                    style={{
                      background: "none",
                      border: "none",
                      color: GOLD,
                      fontSize: 13,
                      padding: 0,
                      textDecoration: "underline",
                    }}
                  >
                    Biometric Data Policy
                  </button>
                  .
                </span>
              </label>
            </>
          )}

          <GoldBtn
            onClick={handleSubmit}
            disabled={!canCreate}
            style={{ width: "100%", padding: "14px 0", marginTop: 8 }}
          >
            {isLogin ? "Sign In" : "Create Account"}
          </GoldBtn>
          {!isLogin && !canCreate && (
            <p style={{ fontSize: 12, color: MUTED, textAlign: "center" }}>
              Verify your email and identity, then accept the policies to continue.
            </p>
          )}
          {isLogin && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0 14px" }}>
                <div style={{ flex: 1, height: 1, background: BORDER }} />
                <span style={{ color: MUTED, fontSize: 12, letterSpacing: "0.08em" }}>OR</span>
                <div style={{ flex: 1, height: 1, background: BORDER }} />
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <GoldBtn outline onClick={handleMagicLink} disabled={submitting} style={{ width: "100%" }}>
                  Email me a sign-in link
                </GoldBtn>
              </div>
              <p style={{ textAlign: "center", marginTop: 8, color: MUTED, fontSize: 12 }}>
                No password needed — we will email you a secure link.
              </p>
            </div>
          )}
        </div>
        <p
          style={{
            textAlign: "center",
            marginTop: 24,
            color: MUTED,
            fontSize: 14,
          }}
        >
          {isLogin ? "New to Voice After Life? " : "Already have an account? "}
          <button
            onClick={() => setScreen(isLogin ? "signup" : "login")}
            style={{
              background: "none",
              border: "none",
              color: GOLD,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            {isLogin ? "Create account" : "Sign in"}
          </button>
        </p>
        {isLogin && (
          <p style={{ textAlign: "center", marginTop: 12, fontSize: 14 }}>
            <button
              type="button"
              onClick={handleReset}
              disabled={submitting}
              style={{
                background: "none",
                border: "none",
                color: MUTED,
                fontSize: 14,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Reset Password
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

const ResetPassword = ({ setScreen }) => {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (busy) return;
    if (!pw || pw.length < 8) { alert("Please choose a password of at least 8 characters."); return; }
    if (pw !== pw2) { alert("The two passwords do not match."); return; }
    if (!supabase) { alert("Password reset is not configured yet."); return; }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) { alert(error.message); return; }
    setDone(true);
  };

  const inputStyle = {
    width: "100%",
    padding: 12,
    marginBottom: 12,
    borderRadius: 8,
    border: "1px solid " + BORDER,
    background: BLACK,
    color: TEXT,
    fontSize: 15,
    boxSizing: "border-box",
  };

  return (
    <div style={{ minHeight: "100vh", background: BLACK, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 420, background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 32 }}>
        <h1 style={{ color: GOLD, fontSize: 26, textAlign: "center", marginBottom: 8, fontWeight: 500 }}>Reset Password</h1>
        {done ? (
          <div>
            <p style={{ color: MUTED, textAlign: "center", fontSize: 15, marginBottom: 24 }}>
              Your password has been updated. You can now sign in with your new password.
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <GoldBtn onClick={() => setScreen("login")} style={{ width: "100%" }}>Go to Sign In</GoldBtn>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: MUTED, textAlign: "center", fontSize: 15, marginBottom: 24 }}>
              Choose a new password for your Voice After Life account.
            </p>
            <PasswordInput type="password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} style={inputStyle} />
            <PasswordInput type="password" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} style={{ ...inputStyle, marginBottom: 20 }} />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <GoldBtn onClick={submit} disabled={busy} style={{ width: "100%" }}>{busy ? "Saving..." : "Update Password"}</GoldBtn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AddRecipientModal = ({ onClose, onAdded }) => {
  const [f, setF] = useState({
    full_name: "",
    relationship: "",
    email: "",
    phone: "",
    date_of_birth: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => {
    const v = e.target.value;
    setF((s) => ({ ...s, [k]: v }));
  };

  const submit = async () => {
    if (busy) return;
    if (!f.full_name.trim()) { alert("Please enter the Legacy Member's name."); return; }
    // Email is deliberately NOT required. A parent adding an adult child often
    // does not know their email address, and being stopped at that moment is a
    // worse outcome than delivering by text. The mobile number below IS required,
    // and a CHECK constraint on public.recipients enforces that at least one of
    // email or phone is present, so a Legacy Member can never be saved with no
    // way to reach them.
    const toE164 = (raw) => {
      const s = (raw || "").replace(/[^\d+]/g, "");
      if (s.charAt(0) === "+") return s.length >= 11 ? s : null;
      const digits = s.replace(/\D/g, "");
      if (digits.length === 10) return "+1" + digits;
      if (digits.length === 11 && digits.charAt(0) === "1") return "+" + digits;
      return null;
    };
    const phoneE164 = toE164(f.phone);
    if (!phoneE164) {
      alert("Please enter a valid mobile number that can receive texts, for example +1 555 123 4567. Your Legacy Member is notified by text, so a mobile number is required.");
      return;
    }
    if (!supabase) { alert("Saving is not configured yet."); return; }
    setBusy(true);
    const { data: gu } = await supabase.auth.getUser();
    const uid = gu && gu.user && gu.user.id;
    if (!uid) { setBusy(false); alert("Your session has expired. Please sign in again."); return; }
    const payload = {
      user_id: uid,
      full_name: f.full_name.trim(),
      email: f.email.trim() || null,
      phone: phoneE164,
      relationship: f.relationship.trim() || null,
      date_of_birth: f.date_of_birth || null,
    };
    const { data, error } = await supabase
      .from("recipients")
      .insert(payload)
      .select()
      .single();
    if (error) { setBusy(false); alert(error.message); return; }
    let smsWarning = "";
    try {
      const resp = await fetch("/.netlify/functions/send-invitation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_id: data.id }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        smsWarning = "Saved " + payload.full_name + ", but the text invitation did not send: " + txt;
      }
    } catch (err) {
      smsWarning = "Saved " + payload.full_name + ", but the text invitation could not be sent right now.";
    }
    setBusy(false);
    onAdded(data);
    onClose();
    if (smsWarning) alert(smsWarning);
  };

  const fields = [
    { k: "full_name", label: "Full name", type: "text", ph: "Jane Doe", req: true },
    { k: "relationship", label: "Relationship", type: "text", ph: "Daughter, Friend, Spouse..." },
    { k: "email", label: "Email (optional if you have their mobile number)", type: "email", ph: "jane@example.com" },
    { k: "phone", label: "Mobile phone (for text invite)", type: "tel", ph: "+1 555 123 4567", req: true },
    { k: "date_of_birth", label: "Birthday (optional)", type: "date" },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          maxHeight: "90vh",
          overflowY: "auto",
          background: SURFACE,
          border: "1px solid " + BORDER,
          borderRadius: 16,
          padding: 28,
        }}
      >
        <h2 style={{ margin: 0, marginBottom: 4, color: GOLD, fontFamily: "Georgia, 'Times New Roman', serif", fontSize: 24, fontWeight: 500 }}>
          Add a Legacy Member
        </h2>
        <p style={{ marginTop: 0, marginBottom: 18, color: MUTED, fontSize: 14 }}>
          Someone who will receive your messages.
        </p>
        {fields.map((fld) => (
          <div key={fld.k} style={{ marginBottom: 12 }}>
            <label style={{ display: "block", color: MUTED, fontSize: 12, marginBottom: 4 }}>
              {fld.label}
              {fld.req ? " *" : ""}
            </label>
            <input
              type={fld.type}
              placeholder={fld.ph || ""}
              value={f[fld.k]}
              onChange={set(fld.k)}
              style={{
                width: "100%",
                padding: 11,
                borderRadius: 8,
                border: "1px solid " + BORDER,
                background: BLACK,
                color: TEXT,
                fontSize: 15,
                boxSizing: "border-box",
              }}
            />
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <GoldBtn outline onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </GoldBtn>
          <GoldBtn onClick={submit} disabled={busy} style={{ flex: 1 }}>
            {busy ? "Saving..." : "Add Legacy Member"}
          </GoldBtn>
        </div>
      </div>
    </div>
  );
};

// "Your Reminders" on the Dashboard and the Category picklist in the Record a
// Message box are ONE list, defined here, so they can never drift apart again.
//
// They used to be two hand-written lists. "Add your funeral wishes" was the
// second reminder, but "Funeral Wishes" was not a Category, so no matter what a
// User recorded that reminder could never tick off — only the first reminder
// ("Record a message for a loved one") ever did, because it counts ANY message.
// A reminder a User cannot complete is worse than no reminder at all.
//
// So: `cat` is the Category value written to `messages.category`, and a reminder
// is complete when at least one message carries its Category. `cat: null` means
// "any message at all satisfies this one" — that is the opening reminder, which
// deliberately has no Category of its own.
//
// Verified 2026-09-03: `messages.category` has NO check constraint (only
// `message_type` does — voice/video/text). Adding values here is safe. If a
// constraint on category is ever added, it must be widened before this list is.
//
// To add a reminder, add one row. It appears in both places and starts ticking
// off on its own. Never add it to only one of them.
const REMINDER_TASKS = [
  { t: "Record a message for a loved one", cat: null },
  { t: "Add your funeral wishes", cat: "Funeral Wishes" },
  { t: "Record your life story", cat: "Life Story" },
  { t: "Add your family recipes", cat: "Family Recipes" },
  { t: "Add your Will & important documents", cat: "Will & Important Documents" },
  { t: "Add your Family Tree", cat: "Family Tree" },
];

const REMINDER_CATEGORIES = REMINDER_TASKS.map((x) => x.cat).filter(Boolean);

// Reminder categories lead — they are what the Dashboard is actively asking for.
// The occasion categories follow, with "Custom" last.
// A handwritten letter is stored as whatever the User uploaded — usually a photo
// or scan, sometimes a PDF. The viewer picks the renderer from the stored file's
// extension so a scan opens as a picture and a PDF opens in a tab.
function letterViewKind(path) {
  return /\.pdf$/i.test(String(path || "")) ? "doc" : "image";
}

const MSG_CATEGORIES = REMINDER_CATEGORIES.concat([
  "Birthday", "Wedding", "Anniversary", "Graduation", "Advice", "Apology",
  "Forgiveness", "Encouragement", "Life Lesson", "Funny Story", "Just Because", "Custom",
]);

const RecordMessageModal = ({ recipients, onClose, onSaved, initialCategory }) => {
  const [mtype, setMtype] = useState("text");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(
    initialCategory && MSG_CATEGORIES.indexOf(initialCategory) !== -1 ? initialCategory : "Just Because"
  );
  const [recipientId, setRecipientId] = useState(recipients && recipients[0] ? recipients[0].id : "");
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // A handwritten letter is an uploaded scan or photo, not a recording, so it
  // carries its own File rather than a recorded Blob. Nothing is ever mailed in
  // and nothing physical is held — digital copies only.
  const [letterFile, setLetterFile] = useState(null);
  const [letterPreview, setLetterPreview] = useState("");
  const letterInputRef = useRef(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const liveRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  const clearLetter = () => {
    setLetterFile(null);
    setLetterPreview((u) => { if (u) URL.revokeObjectURL(u); return ""; });
  };
  useEffect(() => { return () => { stopStream(); }; }, []);

  const chooseType = (t) => {
    if (recording) return;
    stopStream();
    if (mediaUrl) { URL.revokeObjectURL(mediaUrl); }
    setBlob(null); setMediaUrl(""); setSeconds(0); setErr("");
    clearLetter();
    setMtype(t);
  };

  const LETTER_MAX_BYTES = 25 * 1024 * 1024;
  const pickLetter = (e) => {
    setErr("");
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > LETTER_MAX_BYTES) {
      setErr("That file is larger than 25 MB. Please use a smaller scan or photo.");
      return;
    }
    clearLetter();
    setLetterFile(f);
    if ((f.type || "").indexOf("image/") === 0) setLetterPreview(URL.createObjectURL(f));
  };

  const startRec = async () => {
    setErr("");
    if (mediaUrl) { URL.revokeObjectURL(mediaUrl); }
    setBlob(null); setMediaUrl(""); setSeconds(0);
    try {
      const constraints = mtype === "video" ? { audio: true, video: { facingMode: { ideal: "user" } } } : { audio: true };
      const stream = await acquireMediaStream(constraints, stopStream);
      streamRef.current = stream;
      if (mtype === "video" && liveRef.current) {
        liveRef.current.srcObject = stream;
        liveRef.current.muted = true;
        liveRef.current.play().catch(() => {});
      }
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mtype === "video" ? "video/webm" : "audio/webm" });
        setBlob(b);
        setMediaUrl(URL.createObjectURL(b));
        stopStream();
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      stopStream(); setErr(mediaErrorMessage(e, mtype === "video" ? "camera and microphone" : "microphone"));
    }
  };

  const stopRec = () => {
    if (mrRef.current && mrRef.current.state !== "inactive") { mrRef.current.stop(); }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const fmt = (s) => { const m = Math.floor(s / 60); const r = s % 60; return m + ":" + (r < 10 ? "0" : "") + r; };

  const save = async () => {
    setErr("");
    if (!recipients || !recipients.length) { setErr("Add a Legacy Member first, then you can record a message for them."); return; }
    if (!title.trim()) { setErr("Please add a title for this message."); return; }
    if (!recipientId) { setErr("Please choose who this message is for."); return; }
    if (mtype === "text" && !text.trim()) { setErr("Please write your message."); return; }
    if ((mtype === "voice" || mtype === "video") && !blob) { setErr("Please record your message first."); return; }
    if (mtype === "handwritten" && !letterFile) { setErr("Please choose a scan or photo of your letter."); return; }
    if (!supabase) { setErr("Saving is not configured yet."); return; }
    setBusy(true);
    try {
      const { data: gu } = await supabase.auth.getUser();
      const uid = gu && gu.user && gu.user.id;
      if (!uid) { setBusy(false); setErr("Your session has expired. Please sign in again."); return; }
      let filePath = null, fileSize = 0, ext = "txt";
      if (mtype === "voice" || mtype === "video") {
        ext = "webm";
        let uc;
        try { uc = await valCleanUpload(blob, mtype === "video" ? "mp4" : "mp3"); }
        catch (e) { setBusy(false); setErr(e.message || "Upload failed."); return; }
        filePath = uc.path;
        fileSize = uc.size || blob.size;
      } else if (mtype === "handwritten") {
        // Same cleaning pipeline every other upload goes through: type/size
        // filter, virus scan, re-encode. Keep the real extension so the stored
        // file opens as what it is (a jpg stays a jpg, a pdf stays a pdf).
        const nm = letterFile.name || "";
        const dot = nm.lastIndexOf(".");
        const rawExt = dot >= 0 ? nm.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "") : "";
        let uc;
        try { uc = await valCleanUpload(letterFile, rawExt || "jpg"); }
        catch (e) { setBusy(false); setErr(e.message || "Upload failed."); return; }
        filePath = uc.path;
        fileSize = uc.size || letterFile.size;
      }
      const row = {
        user_id: uid,
        title: title.trim(),
        category: category,
        message_type: mtype,
        // A handwritten letter may carry an optional note from the User; the
        // letter itself is the file, so the note is never required.
        body_text: (mtype === "text" || mtype === "handwritten") ? (text.trim() || null) : null,
        file_path: filePath,
        file_size_bytes: fileSize,
        duration_seconds: (mtype === "text" || mtype === "handwritten") ? null : seconds,
      };
      const ins = await supabase.from("messages").insert(row).select().single();
      if (ins.error) { setBusy(false); setErr(ins.error.message); return; }
      const msg = ins.data;
      const link = await supabase.from("message_recipients").insert({ message_id: msg.id, recipient_id: recipientId });
      if (link.error) { setBusy(false); setErr("Saved, but could not link the recipient: " + link.error.message); return; }
      setBusy(false);
      onSaved({
        id: msg.id,
        title: msg.title,
        message_type: msg.message_type,
        category: msg.category || category,
        duration_seconds: msg.duration_seconds,
        created_at: msg.created_at,
        message_recipients: [{ recipient_id: recipientId }],
      });
      onClose();
    } catch (e) {
      setBusy(false);
      setErr("Something went wrong: " + (e && e.message ? e.message : String(e)));
    }
  };

  const inputStyle = { width: "100%", padding: 11, borderRadius: 8, border: "1px solid " + BORDER, background: BLACK, color: TEXT, fontSize: 15, boxSizing: "border-box" };
  const tab = (t, label) => (
    <button type="button" onClick={() => chooseType(t)} disabled={recording}
      style={{ flex: 1, padding: "10px 0", borderRadius: 8, cursor: recording ? "default" : "pointer", border: "1px solid " + (mtype === t ? GOLD : BORDER), background: mtype === t ? GOLD : "transparent", color: mtype === t ? BLACK : MUTED, fontSize: 14, fontWeight: mtype === t ? 600 : 400 }}>
      {label}
    </button>
  );

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 500, maxHeight: "92vh", overflowY: "auto", background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 26 }}>
        <h2 style={{ margin: 0, marginBottom: 4, color: GOLD, fontFamily: "Georgia, 'Times New Roman', serif", fontSize: 24, fontWeight: 500 }}>Record a Message</h2>
        <p style={{ marginTop: 0, marginBottom: 16, color: MUTED, fontSize: 14 }}>Leave a voice, video, written, or handwritten message for someone you love.</p>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {tab("voice", "Voice")}
          {tab("video", "Video")}
          {tab("text", "Text")}
          {tab("handwritten", "Letter")}
        </div>
        {mtype === "text" && (
          <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Write your message..." rows={6} style={{ ...inputStyle, resize: "vertical", marginBottom: 14, fontFamily: "inherit" }} />
        )}
        {mtype === "handwritten" && (
          <div style={{ marginBottom: 14 }}>
            <p style={{ color: MUTED, fontSize: 13, lineHeight: 1.6, margin: "0 0 12px" }}>
              Upload a scan or a clear photo of a letter you wrote by hand. Keep the original
              yourself — we never take in paper, and nothing is ever mailed to us.
            </p>
            <input
              ref={letterInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={pickLetter}
              style={{ display: "none" }}
            />
            {!letterFile && (
              <button
                type="button"
                onClick={() => letterInputRef.current && letterInputRef.current.click()}
                style={{ width: "100%", padding: "26px 14px", borderRadius: 10, cursor: "pointer",
                         border: "1px dashed " + BORDER, background: BLACK, color: MUTED,
                         fontSize: 14, fontFamily: "inherit" }}
              >
                Choose a scan or photo of your letter
              </button>
            )}
            {letterFile && (
              <div style={{ border: "1px solid " + BORDER, borderRadius: 10, padding: 14, background: BLACK }}>
                {letterPreview ? (
                  <img alt="Your letter" src={letterPreview}
                       style={{ width: "100%", borderRadius: 8, display: "block", marginBottom: 10 }} />
                ) : (
                  <p style={{ color: TEXT, fontSize: 14, margin: "0 0 10px" }}>{letterFile.name}</p>
                )}
                <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ color: MUTED, fontSize: 12 }}>
                    {Math.max(1, Math.round(letterFile.size / 1024)) + " KB"}
                  </span>
                  <button type="button" onClick={() => letterInputRef.current && letterInputRef.current.click()}
                          style={{ background: "transparent", border: "1px solid " + BORDER, borderRadius: 8,
                                   color: GOLD, padding: "7px 14px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                    Choose a different file
                  </button>
                </div>
              </div>
            )}
            <textarea value={text} onChange={(e) => setText(e.target.value)}
                      placeholder="Add a note to go with the letter (optional)" rows={3}
                      style={{ ...inputStyle, resize: "vertical", marginTop: 12, fontFamily: "inherit" }} />
          </div>
        )}
        {(mtype === "voice" || mtype === "video") && (
          <div style={{ marginBottom: 14, textAlign: "center" }}>
            {mtype === "video" && !mediaUrl && (
              <video ref={liveRef} playsInline style={{ width: "100%", borderRadius: 10, background: BLACK, maxHeight: 240, display: recording ? "block" : "none" }} />
            )}
            {mediaUrl && mtype === "video" && (
              <video src={mediaUrl} controls playsInline style={{ width: "100%", borderRadius: 10, background: BLACK, maxHeight: 240 }} />
            )}
            {mediaUrl && mtype === "voice" && (
              <audio src={mediaUrl} controls style={{ width: "100%" }} />
            )}
            <div style={{ color: MUTED, fontSize: 13, margin: "10px 0" }}>
              {recording ? "Recording... " + fmt(seconds) : mediaUrl ? "Recorded " + fmt(seconds) + " - preview above" : "Ready to record"}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              {!recording && <GoldBtn onClick={startRec}>{mediaUrl ? "Re-record" : "Start Recording"}</GoldBtn>}
              {recording && <GoldBtn onClick={stopRec} outline>Stop</GoldBtn>}
            </div>
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", color: MUTED, fontSize: 12, marginBottom: 4 }}>Title *</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. On your wedding day" style={inputStyle} />
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", color: MUTED, fontSize: 12, marginBottom: 4 }}>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
              {MSG_CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", color: MUTED, fontSize: 12, marginBottom: 4 }}>For *</label>
            <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)} style={inputStyle}>
              <option value="">Choose Legacy Member...</option>
              {(recipients || []).map((r) => (<option key={r.id} value={r.id}>{r.full_name}</option>))}
            </select>
          </div>
        </div>
        {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <GoldBtn outline onClick={onClose} style={{ flex: 1 }}>Cancel</GoldBtn>
          <GoldBtn onClick={save} disabled={busy} style={{ flex: 1 }}>{busy ? "Saving..." : "Save Message"}</GoldBtn>
        </div>
      </div>
    </div>
  );
};

const ConfessionModal = ({ recipients, onClose, onSaved }) => {
  const [recipientId, setRecipientId] = useState(recipients && recipients[0] ? recipients[0].id : "");
  const [mtype, setMtype] = useState("text");
  const [text, setText] = useState("");
  const [org, setOrg] = useState("");
  const [poc, setPoc] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [details, setDetails] = useState("");
  const [showContact, setShowContact] = useState(false);
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const liveRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  useEffect(() => { return () => { stopStream(); if (mediaUrl) URL.revokeObjectURL(mediaUrl); }; }, []);

  const startRec = async () => {
    setErr("");
    if (mediaUrl) { URL.revokeObjectURL(mediaUrl); }
    setBlob(null); setMediaUrl(""); setSeconds(0);
    try {
      const constraints = mtype === "video" ? { audio: true, video: { facingMode: { ideal: "user" } } } : { audio: true };
      const stream = await acquireMediaStream(constraints, stopStream);
      streamRef.current = stream;
      if (mtype === "video" && liveRef.current) {
        liveRef.current.srcObject = stream;
        liveRef.current.muted = true;
        liveRef.current.play().catch(() => {});
      }
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mtype === "video" ? "video/webm" : "audio/webm" });
        setBlob(b);
        setMediaUrl(URL.createObjectURL(b));
        stopStream();
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
      let s = 0;
      timerRef.current = setInterval(() => { s += 1; setSeconds(s); }, 1000);
    } catch (e) {
      stopStream(); setErr(mediaErrorMessage(e, mtype === "video" ? "camera" : "microphone"));
    }
  };
  const stopRec = () => {
    if (mrRef.current && mrRef.current.state !== "inactive") { mrRef.current.stop(); }
    setRecording(false);
  };
  const switchType = (t) => {
    if (recording) stopRec();
    if (mediaUrl) { URL.revokeObjectURL(mediaUrl); }
    setBlob(null); setMediaUrl(""); setSeconds(0); setErr("");
    setMtype(t);
  };

  const save = async () => {
    setErr("");
    const hasBody = mtype === "text" ? !!text.trim() : !!blob;
    const hasContact = org.trim() || poc.trim() || phone.trim() || address.trim() || details.trim();
    if (!hasBody && !hasContact) { setErr("Please record or write your confession, or add the details you want to disclose."); return; }
    if (!supabase) { setErr("Saving is not configured yet."); return; }
    setBusy(true);
    try {
      const { data: gu } = await supabase.auth.getUser();
      const uid = gu && gu.user && gu.user.id;
      if (!uid) { setBusy(false); setErr("Your session has expired. Please sign in again."); return; }
      let filePath = null;
      if ((mtype === "voice" || mtype === "video") && blob) {
        let uc;
        try { uc = await valCleanUpload(blob, mtype === "video" ? "mp4" : "mp3"); }
        catch (e) { setBusy(false); setErr(e.message || "Upload failed."); return; }
        filePath = uc.path;
      }
      const row = {
        user_id: uid,
        message_type: mtype,
        body_text: mtype === "text" ? (text.trim() || null) : null,
        file_path: filePath,
        duration_seconds: mtype === "text" ? null : seconds,
        organization: org.trim() || null,
        point_of_contact: poc.trim() || null,
        contact_phone: phone.trim() || null,
        contact_address: address.trim() || null,
        details: details.trim() || null,
      };
      const ins = await supabase.from("confessions").insert(row).select().single();
      if (ins.error) { setBusy(false); setErr(ins.error.message); return; }
      if (recipientId) { await supabase.from("deliveries").insert({ user_id: uid, recipient_id: recipientId, item_type: "confession", item_id: ins.data.id }); }
      setBusy(false);
      setDone(true);
      if (onSaved) onSaved(ins.data);
    } catch (e) {
      setBusy(false);
      setErr("Something went wrong: " + (e && e.message ? e.message : String(e)));
    }
  };

  const inputStyle = { width: "100%", padding: 11, borderRadius: 8, border: "1px solid " + BORDER, background: BLACK, color: TEXT, fontSize: 15, boxSizing: "border-box", marginBottom: 10 };
  const labelStyle = { display: "block", color: MUTED, fontSize: 12, marginBottom: 4 };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, maxHeight: "92vh", overflowY: "auto", background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 26 }}>
        {done ? (
          <div style={{ textAlign: "center", padding: "18px 0" }}>
            <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 8px" }}>Confession Saved</h2>
            <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.6, margin: "0 0 22px" }}>It is stored securely and will only be released to your verified Legacy Members after your passing is confirmed.</p>
            <GoldBtn onClick={onClose}>Done</GoldBtn>
          </div>
        ) : (
          <div>
            <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 4px" }}>Add a Confession</h2>
            <p style={{ color: MUTED, fontSize: 13, margin: "0 0 18px", lineHeight: 1.5 }}>Private matters, released only after verification. Share a secret, disclose an account or asset, or leave instructions only your loved ones should know.</p>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {["voice", "video", "text"].map((t) => (
                <button key={t} onClick={() => switchType(t)} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "1px solid " + (mtype === t ? GOLD : BORDER), background: mtype === t ? GOLD : "transparent", color: mtype === t ? BLACK : TEXT, fontWeight: 600, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>
              ))}
            </div>
            {mtype === "text" && (
              <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Write your confession..." rows={5} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
            )}
            {(mtype === "voice" || mtype === "video") && (
              <div style={{ marginBottom: 14, textAlign: "center" }}>
                {mtype === "video" && !mediaUrl && (
                  <video ref={liveRef} style={{ width: "100%", borderRadius: 10, background: BLACK, marginBottom: 10, display: recording ? "block" : "none" }} playsInline />
                )}
                {mediaUrl && mtype === "video" && (<video src={mediaUrl} controls style={{ width: "100%", borderRadius: 10, marginBottom: 10 }} />)}
                {mediaUrl && mtype === "voice" && (<audio src={mediaUrl} controls style={{ width: "100%", marginBottom: 10 }} />)}
                <div style={{ color: recording ? "#E88" : MUTED, fontSize: 13, marginBottom: 8 }}>{recording ? "Recording " : (mediaUrl ? "Recorded " : "")}{(recording || mediaUrl) ? (Math.floor(seconds / 60) + ":" + String(seconds % 60).padStart(2, "0")) : ""}</div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  {!recording && <GoldBtn onClick={startRec}>{mediaUrl ? "Re-record" : "Start Recording"}</GoldBtn>}
                  {recording && <GoldBtn onClick={stopRec} outline>Stop</GoldBtn>}
                </div>
              </div>
            )}
            <button onClick={() => setShowContact(!showContact)} style={{ background: "none", border: "none", color: GOLD, cursor: "pointer", fontSize: 13, padding: "4px 0", marginBottom: 8, display: "block" }}>{showContact ? "- Hide contact / account details" : "+ Add a point of contact or account (optional)"}</button>
            {showContact && (
              <div style={{ background: SURFACE2, borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <label style={labelStyle}>Organization / institution</label>
                <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="e.g. First National Bank" style={inputStyle} />
                <label style={labelStyle}>Point of contact</label>
                <input value={poc} onChange={(e) => setPoc(e.target.value)} placeholder="Name" style={inputStyle} />
                <label style={labelStyle}>Contact phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" style={inputStyle} />
                <label style={labelStyle}>Contact address</label>
                <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" style={inputStyle} />
                <label style={labelStyle}>Additional details</label>
                <textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Account numbers, locations, instructions..." rows={3} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", color: MUTED, fontSize: 12, marginBottom: 4 }}>Who is this for? *</label>
              <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)} style={{ width: "100%", boxSizing: "border-box", background: DARK, color: TEXT, border: "1px solid " + BORDER, borderRadius: 8, padding: "9px 10px", fontSize: 14 }}>
                {(recipients || []).map((r) => (<option key={r.id} value={r.id}>{r.full_name}</option>))}
              </select>
            </div>
            {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <GoldBtn outline onClick={onClose} style={{ flex: 1 }}>Cancel</GoldBtn>
              <GoldBtn onClick={save} disabled={busy} style={{ flex: 1 }}>{busy ? "Saving..." : "Save Confession"}</GoldBtn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const QotdAnswerModal = ({ recipients, question, initialType, onClose, onSaved }) => {
  const [recipientId, setRecipientId] = useState(recipients && recipients[0] ? recipients[0].id : "");
  const [mtype, setMtype] = useState(initialType || "text");
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const liveRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  useEffect(() => { return () => { stopStream(); if (mediaUrl) URL.revokeObjectURL(mediaUrl); }; }, []);

  const startRec = async () => {
    setErr("");
    if (mediaUrl) { URL.revokeObjectURL(mediaUrl); }
    setBlob(null); setMediaUrl(""); setSeconds(0);
    try {
      const constraints = mtype === "video" ? { audio: true, video: { facingMode: { ideal: "user" } } } : { audio: true };
      const stream = await acquireMediaStream(constraints, stopStream);
      streamRef.current = stream;
      if (mtype === "video" && liveRef.current) {
        liveRef.current.srcObject = stream;
        liveRef.current.muted = true;
        liveRef.current.play().catch(() => {});
      }
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mtype === "video" ? "video/webm" : "audio/webm" });
        setBlob(b);
        setMediaUrl(URL.createObjectURL(b));
        stopStream();
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
      let s = 0;
      timerRef.current = setInterval(() => { s += 1; setSeconds(s); }, 1000);
    } catch (e) {
      stopStream(); setErr(mediaErrorMessage(e, mtype === "video" ? "camera" : "microphone"));
    }
  };
  const stopRec = () => {
    if (mrRef.current && mrRef.current.state !== "inactive") { mrRef.current.stop(); }
    setRecording(false);
  };
  const switchType = (t) => {
    if (recording) stopRec();
    if (mediaUrl) { URL.revokeObjectURL(mediaUrl); }
    setBlob(null); setMediaUrl(""); setSeconds(0); setErr("");
    setMtype(t);
  };

  const save = async () => {
    setErr("");
    const hasBody = mtype === "text" ? !!text.trim() : !!blob;
    if (!hasBody) { setErr(mtype === "text" ? "Please write your answer." : "Please record your answer first."); return; }
    if (!supabase) { setErr("Saving is not configured yet."); return; }
    setBusy(true);
    try {
      const { data: gu } = await supabase.auth.getUser();
      const uid = gu && gu.user && gu.user.id;
      if (!uid) { setBusy(false); setErr("Your session has expired. Please sign in again."); return; }
      let filePath = null;
      if ((mtype === "voice" || mtype === "video") && blob) {
        let uc;
        try { uc = await valCleanUpload(blob, mtype === "video" ? "mp4" : "mp3"); }
        catch (e) { setBusy(false); setErr(e.message || "Upload failed."); return; }
        filePath = uc.path;
      }
      const row = {
        user_id: uid,
        answer_type: mtype,
        answer_text: mtype === "text" ? text.trim() : null,
        file_path: filePath,
        duration_seconds: mtype === "text" ? null : seconds,
        prompt_text: question,
        prompt_key: "qotd",
        section: "qotd",
        is_qotd: true,
        answered_at: new Date().toISOString(),
      };
      const ins = await supabase.from("legacy_answers").insert(row).select().single();
      if (ins.error) { setBusy(false); setErr(ins.error.message); return; }
      if (recipientId) { await supabase.from("deliveries").insert({ user_id: uid, recipient_id: recipientId, item_type: "answer", item_id: ins.data.id }); }
      setBusy(false);
      setDone(true);
      if (onSaved) onSaved(ins.data);
    } catch (e) {
      setBusy(false);
      setErr("Something went wrong: " + (e && e.message ? e.message : String(e)));
    }
  };

  const inputStyle = { width: "100%", padding: 11, borderRadius: 8, border: "1px solid " + BORDER, background: BLACK, color: TEXT, fontSize: 15, boxSizing: "border-box", marginBottom: 10 };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, maxHeight: "92vh", overflowY: "auto", background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 26 }}>
        {done ? (
          <div style={{ textAlign: "center", padding: "18px 0" }}>
            <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 8px" }}>Answer Saved</h2>
            <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.6, margin: "0 0 22px" }}>Another piece of your Legacy preserved. Come back tomorrow for a new question.</p>
            <GoldBtn onClick={onClose}>Done</GoldBtn>
          </div>
        ) : (
          <div>
            <div style={{ color: GOLD, fontSize: 12, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Question of the Day</div>
            <h2 style={{ color: TEXT, fontFamily: "Georgia, serif", fontSize: 22, margin: "0 0 16px", lineHeight: 1.3 }}>{question}</h2>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {["voice", "video", "text"].map((t) => (
                <button key={t} onClick={() => switchType(t)} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "1px solid " + (mtype === t ? GOLD : BORDER), background: mtype === t ? GOLD : "transparent", color: mtype === t ? BLACK : TEXT, fontWeight: 600, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>
              ))}
            </div>
            {mtype === "text" && (
              <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Write your answer..." rows={5} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
            )}
            {(mtype === "voice" || mtype === "video") && (
              <div style={{ marginBottom: 14, textAlign: "center" }}>
                {mtype === "video" && !mediaUrl && (
                  <video ref={liveRef} style={{ width: "100%", borderRadius: 10, background: BLACK, marginBottom: 10, display: recording ? "block" : "none" }} playsInline />
                )}
                {mediaUrl && mtype === "video" && (<video src={mediaUrl} controls style={{ width: "100%", borderRadius: 10, marginBottom: 10 }} />)}
                {mediaUrl && mtype === "voice" && (<audio src={mediaUrl} controls style={{ width: "100%", marginBottom: 10 }} />)}
                <div style={{ color: recording ? "#E88" : MUTED, fontSize: 13, marginBottom: 8 }}>{recording ? "Recording " : (mediaUrl ? "Recorded " : "")}{(recording || mediaUrl) ? (Math.floor(seconds / 60) + ":" + String(seconds % 60).padStart(2, "0")) : ""}</div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  {!recording && <GoldBtn onClick={startRec}>{mediaUrl ? "Re-record" : "Start Recording"}</GoldBtn>}
                  {recording && <GoldBtn onClick={stopRec} outline>Stop</GoldBtn>}
                </div>
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", color: MUTED, fontSize: 12, marginBottom: 4 }}>Who is this for? *</label>
              <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)} style={{ width: "100%", boxSizing: "border-box", background: DARK, color: TEXT, border: "1px solid " + BORDER, borderRadius: 8, padding: "9px 10px", fontSize: 14 }}>
                {(recipients || []).map((r) => (<option key={r.id} value={r.id}>{r.full_name}</option>))}
              </select>
            </div>
            {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <GoldBtn outline onClick={onClose} style={{ flex: 1 }}>Cancel</GoldBtn>
              <GoldBtn onClick={save} disabled={busy} style={{ flex: 1 }}>{busy ? "Saving..." : "Save Answer"}</GoldBtn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const STORY_CHAPTERS = ["Childhood", "Family", "Love & Marriage", "Career & Work", "Lessons & Wisdom", "Faith & Beliefs", "Adventures & Travel", "Other"];

const RecordStoryModal = ({ recipients, onClose, onSaved }) => {
  const [recipientId, setRecipientId] = useState(recipients && recipients[0] ? recipients[0].id : "");
  const [mtype, setMtype] = useState("text");
  const [title, setTitle] = useState("");
  const [chapter, setChapter] = useState(STORY_CHAPTERS[0]);
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const liveRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  useEffect(() => { return () => { stopStream(); if (mediaUrl) URL.revokeObjectURL(mediaUrl); }; }, []);

  const startRec = async () => {
    setErr("");
    if (mediaUrl) { URL.revokeObjectURL(mediaUrl); }
    setBlob(null); setMediaUrl(""); setSeconds(0);
    try {
      const constraints = mtype === "video" ? { audio: true, video: { facingMode: { ideal: "user" } } } : { audio: true };
      const stream = await acquireMediaStream(constraints, stopStream);
      streamRef.current = stream;
      if (mtype === "video" && liveRef.current) {
        liveRef.current.srcObject = stream;
        liveRef.current.muted = true;
        liveRef.current.play().catch(() => {});
      }
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mtype === "video" ? "video/webm" : "audio/webm" });
        setBlob(b);
        setMediaUrl(URL.createObjectURL(b));
        stopStream();
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
      let s = 0;
      timerRef.current = setInterval(() => { s += 1; setSeconds(s); }, 1000);
    } catch (e) {
      stopStream(); setErr(mediaErrorMessage(e, mtype === "video" ? "camera" : "microphone"));
    }
  };
  const stopRec = () => {
    if (mrRef.current && mrRef.current.state !== "inactive") { mrRef.current.stop(); }
    setRecording(false);
  };
  const switchType = (t) => {
    if (recording) stopRec();
    if (mediaUrl) { URL.revokeObjectURL(mediaUrl); }
    setBlob(null); setMediaUrl(""); setSeconds(0); setErr("");
    setMtype(t);
  };

  const save = async () => {
    setErr("");
    if (!title.trim()) { setErr("Please give your story a title."); return; }
    const hasBody = mtype === "text" ? !!text.trim() : !!blob;
    if (!hasBody) { setErr(mtype === "text" ? "Please write your story." : "Please record your story first."); return; }
    if (!supabase) { setErr("Saving is not configured yet."); return; }
    setBusy(true);
    try {
      const { data: gu } = await supabase.auth.getUser();
      const uid = gu && gu.user && gu.user.id;
      if (!uid) { setBusy(false); setErr("Your session has expired. Please sign in again."); return; }
      let filePath = null, fileSize = 0;
      if ((mtype === "voice" || mtype === "video") && blob) {
        let uc;
        try { uc = await valCleanUpload(blob, mtype === "video" ? "mp4" : "mp3"); }
        catch (e) { setBusy(false); setErr(e.message || "Upload failed."); return; }
        filePath = uc.path;
        fileSize = uc.size || blob.size;
      }
      const row = {
        user_id: uid,
        title: title.trim(),
        chapter: chapter,
        message_type: mtype,
        body_text: mtype === "text" ? text.trim() : null,
        file_path: filePath,
        file_size_bytes: fileSize,
        duration_seconds: mtype === "text" ? null : seconds,
      };
      const ins = await supabase.from("stories").insert(row).select().single();
      if (ins.error) { setBusy(false); setErr(ins.error.message); return; }
      if (recipientId) { await supabase.from("deliveries").insert({ user_id: uid, recipient_id: recipientId, item_type: "story", item_id: ins.data.id }); }
      setBusy(false);
      setDone(true);
      if (onSaved) onSaved(ins.data);
    } catch (e) {
      setBusy(false);
      setErr("Something went wrong: " + (e && e.message ? e.message : String(e)));
    }
  };

  const inputStyle = { width: "100%", padding: 11, borderRadius: 8, border: "1px solid " + BORDER, background: BLACK, color: TEXT, fontSize: 15, boxSizing: "border-box", marginBottom: 12 };
  const labelStyle = { display: "block", color: MUTED, fontSize: 12, marginBottom: 4 };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, maxHeight: "92vh", overflowY: "auto", background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 26 }}>
        {done ? (
          <div style={{ textAlign: "center", padding: "18px 0" }}>
            <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 8px" }}>Story Saved</h2>
            <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.6, margin: "0 0 22px" }}>Another chapter of your life preserved for the people who come after you.</p>
            <GoldBtn onClick={onClose}>Done</GoldBtn>
          </div>
        ) : (
          <div>
            <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 4px" }}>Record a Story</h2>
            <p style={{ color: MUTED, fontSize: 13, margin: "0 0 18px", lineHeight: 1.5 }}>Share a memory or a chapter of your life - a story your family will keep forever.</p>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {["voice", "video", "text"].map((t) => (
                <button key={t} onClick={() => switchType(t)} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "1px solid " + (mtype === t ? GOLD : BORDER), background: mtype === t ? GOLD : "transparent", color: mtype === t ? BLACK : TEXT, fontWeight: 600, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>
              ))}
            </div>
            {mtype === "text" && (
              <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Tell your story..." rows={6} style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
            )}
            {(mtype === "voice" || mtype === "video") && (
              <div style={{ marginBottom: 14, textAlign: "center" }}>
                {mtype === "video" && !mediaUrl && (
                  <video ref={liveRef} style={{ width: "100%", borderRadius: 10, background: BLACK, marginBottom: 10, display: recording ? "block" : "none" }} playsInline />
                )}
                {mediaUrl && mtype === "video" && (<video src={mediaUrl} controls style={{ width: "100%", borderRadius: 10, marginBottom: 10 }} />)}
                {mediaUrl && mtype === "voice" && (<audio src={mediaUrl} controls style={{ width: "100%", marginBottom: 10 }} />)}
                <div style={{ color: recording ? "#E88" : MUTED, fontSize: 13, marginBottom: 8 }}>{recording ? "Recording " : (mediaUrl ? "Recorded " : "")}{(recording || mediaUrl) ? (Math.floor(seconds / 60) + ":" + String(seconds % 60).padStart(2, "0")) : ""}</div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  {!recording && <GoldBtn onClick={startRec}>{mediaUrl ? "Re-record" : "Start Recording"}</GoldBtn>}
                  {recording && <GoldBtn onClick={stopRec} outline>Stop</GoldBtn>}
                </div>
              </div>
            )}
            <label style={labelStyle}>Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The summer we drove to the coast" style={inputStyle} />
            <label style={labelStyle}>Chapter</label>
            <select value={chapter} onChange={(e) => setChapter(e.target.value)} style={inputStyle}>
              {STORY_CHAPTERS.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", color: MUTED, fontSize: 12, marginBottom: 4 }}>Who is this for? *</label>
              <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)} style={{ width: "100%", boxSizing: "border-box", background: DARK, color: TEXT, border: "1px solid " + BORDER, borderRadius: 8, padding: "9px 10px", fontSize: 14 }}>
                {(recipients || []).map((r) => (<option key={r.id} value={r.id}>{r.full_name}</option>))}
              </select>
            </div>
            {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
              <GoldBtn outline onClick={onClose} style={{ flex: 1 }}>Cancel</GoldBtn>
              <GoldBtn onClick={save} disabled={busy} style={{ flex: 1 }}>{busy ? "Saving..." : "Save Story"}</GoldBtn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MEDIA_KINDS = {
  photo: { title: "Upload Photos", sub: "Preserve the pictures that tell your story.", accept: "image/*", prefix: "photo", verb: "photo" },
  video: { title: "Upload Videos", sub: "Add videos from your camera or phone.", accept: "video/*", prefix: "video", verb: "video" },
  document: { title: "Upload Messages", sub: "Bring in letters or notes you have written.", accept: "image/*,application/pdf,.txt,.rtf,.doc,.docx", prefix: "doc", verb: "file" },
};

const UploadModal = ({ recipients, kind, onClose }) => {
  const cfg = MEDIA_KINDS[kind] || MEDIA_KINDS.photo;
  const [recipientId, setRecipientId] = useState(recipients && recipients[0] ? recipients[0].id : "");
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const inputRef = useRef(null);

  const onPick = (e) => {
    setErr("");
    setFiles(e.target.files ? Array.from(e.target.files) : []);
  };
  const extOf = (name) => { const i = name.lastIndexOf("."); return i >= 0 ? name.slice(i) : ""; };

  const upload = async () => {
    setErr("");
    if (!files.length) { setErr("Please choose at least one " + cfg.verb + " to upload."); return; }
    if (!supabase) { setErr("Saving is not configured yet."); return; }
    setBusy(true);
    try {
      const { data: gu } = await supabase.auth.getUser();
      const uid = gu && gu.user && gu.user.id;
      if (!uid) { setBusy(false); setErr("Your session has expired. Please sign in again."); return; }
      let n = 0;
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const _oe = (f.type || "").indexOf("video/") === 0 ? "mp4" : ((f.type || "").indexOf("audio/") === 0 ? "mp3" : (extOf(f.name).replace(".", "").toLowerCase() || "bin"));
        let uc;
        try { uc = await valCleanUpload(f, _oe); }
        catch (e) { setBusy(false); setErr("Upload failed for " + f.name + ": " + (e.message || "")); return; }
        const row = {
          user_id: uid,
          kind: kind,
          title: title.trim() || null,
          file_path: uc.path,
          file_name: f.name,
          file_size_bytes: uc.size || f.size,
          mime_type: f.type || null,
        };
        const ins = await supabase.from("media").insert(row).select().single();
        if (ins.error) { setBusy(false); setErr(ins.error.message); return; }
        if (recipientId) { await supabase.from("deliveries").insert({ user_id: uid, recipient_id: recipientId, item_type: "media", item_id: ins.data.id }); }
        n += 1;
        setSavedCount(n);
      }
      setBusy(false);
      setDone(true);
    } catch (e) {
      setBusy(false);
      setErr("Something went wrong: " + (e && e.message ? e.message : String(e)));
    }
  };

  const inputStyle = { width: "100%", padding: 11, borderRadius: 8, border: "1px solid " + BORDER, background: BLACK, color: TEXT, fontSize: 15, boxSizing: "border-box", marginBottom: 12 };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, maxHeight: "92vh", overflowY: "auto", background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 26 }}>
        {done ? (
          <div style={{ textAlign: "center", padding: "18px 0" }}>
            <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 8px" }}>Upload Complete</h2>
            <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.6, margin: "0 0 22px" }}>{savedCount} {savedCount === 1 ? "file" : "files"} added to your Legacy and stored securely.</p>
            <GoldBtn onClick={onClose}>Done</GoldBtn>
          </div>
        ) : (
          <div>
            <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 4px" }}>{cfg.title}</h2>
            <p style={{ color: MUTED, fontSize: 13, margin: "0 0 18px", lineHeight: 1.5 }}>{cfg.sub}</p>
            <input ref={inputRef} type="file" accept={cfg.accept} multiple onChange={onPick} style={{ display: "none" }} />
            <div onClick={() => inputRef.current && inputRef.current.click()} style={{ border: "1px dashed " + BORDER, borderRadius: 12, padding: "26px 16px", textAlign: "center", cursor: "pointer", marginBottom: 14, background: BLACK }}>
              <div style={{ color: GOLD, fontWeight: 600, marginBottom: 4 }}>Choose files</div>
              <div style={{ color: MUTED, fontSize: 12 }}>or drag them here</div>
            </div>
            {files.length > 0 && (
              <div style={{ background: SURFACE2, borderRadius: 10, padding: 12, marginBottom: 12, maxHeight: 140, overflowY: "auto" }}>
                {files.map((f, i) => (
                  <div key={i} style={{ color: TEXT, fontSize: 13, padding: "3px 0", display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                    <span style={{ color: MUTED, flexShrink: 0 }}>{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                  </div>
                ))}
              </div>
            )}
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a title or caption (optional)" style={inputStyle} />
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", color: MUTED, fontSize: 12, marginBottom: 4 }}>Who is this for? *</label>
              <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)} style={{ width: "100%", boxSizing: "border-box", background: DARK, color: TEXT, border: "1px solid " + BORDER, borderRadius: 8, padding: "9px 10px", fontSize: 14 }}>
                {(recipients || []).map((r) => (<option key={r.id} value={r.id}>{r.full_name}</option>))}
              </select>
            </div>
            {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
              <GoldBtn outline onClick={onClose} style={{ flex: 1 }}>Cancel</GoldBtn>
              <GoldBtn onClick={upload} disabled={busy} style={{ flex: 1 }}>{busy ? ("Uploading " + savedCount + "/" + files.length + "...") : "Upload"}</GoldBtn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const RERECORD_COLS = {
  messages: { text: "body_text", size: "file_size_bytes", dur: "duration_seconds" },
  stories: { text: "body_text", size: "file_size_bytes", dur: "duration_seconds" },
  confessions: { text: "body_text", size: null, dur: "duration_seconds" },
  legacy_answers: { text: "answer_text", size: null, dur: "duration_seconds" },
  media: { text: null, size: "file_size_bytes", dur: null },
};

const ReRecordModal = ({ item, onClose, onDone }) => {
  // A handwritten letter is replaced by uploading a new scan — the "replace"
  // branch below already knows how to swap a file. Without this it would fall to
  // "text" and quietly overwrite the User's note while leaving the old letter.
  const kind = (item.table === "media" || item.mtype === "handwritten") ? "replace"
    : (item.mtype === "video" ? "video" : (item.mtype === "voice" ? "voice" : "text"));
  const [text, setText] = useState(kind === "text" ? (item.text || "") : "");
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const liveRef = useRef(null);
  const fileRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  useEffect(() => { return () => { stopStream(); }; }, []);

  const startRec = async () => {
    setErr("");
    if (mediaUrl) { URL.revokeObjectURL(mediaUrl); }
    setBlob(null); setMediaUrl(""); setSeconds(0); chunksRef.current = [];
    try {
      const constraints = kind === "video" ? { audio: true, video: { facingMode: { ideal: "user" } } } : { audio: true };
      const stream = await acquireMediaStream(constraints, stopStream);
      streamRef.current = stream;
      if (kind === "video" && liveRef.current) { liveRef.current.srcObject = stream; liveRef.current.muted = true; liveRef.current.play().catch(() => {}); }
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: kind === "video" ? "video/webm" : "audio/webm" });
        setBlob(b); setMediaUrl(URL.createObjectURL(b)); stopStream();
      };
      mrRef.current = mr; mr.start(); setRecording(true); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((x) => x + 1), 1000);
    } catch (e) {
      stopStream(); setErr(mediaErrorMessage(e, kind === "video" ? "camera and microphone" : "microphone"));
    }
  };
  const stopRec = () => {
    if (mrRef.current && mrRef.current.state !== "inactive") { mrRef.current.stop(); }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };
  const fmt = (s) => { const m = Math.floor(s / 60); const r = s % 60; return m + ":" + (r < 10 ? "0" : "") + r; };
  const pickFile = (e) => { const f = e.target.files && e.target.files[0]; if (f) { setErr(""); setFile(f); } };

  const save = async () => {
    setErr("");
    if (kind === "text" && !text.trim()) { setErr("Please write your message before saving."); return; }
    if ((kind === "voice" || kind === "video") && !blob) { setErr("Please record a new take first."); return; }
    if (kind === "replace" && !file) { setErr("Please choose a file to upload."); return; }
    if (!supabase) { setErr("Saving is not configured yet."); return; }
    setBusy(true);
    try {
      const { data: gu } = await supabase.auth.getUser();
      const uid = gu && gu.user && gu.user.id;
      if (!uid) { setBusy(false); setErr("Your session has expired. Please sign in again."); return; }
      const cfg = RERECORD_COLS[item.table] || {};
      const patch = {};
      let newPath = null;
      if (kind === "text") {
        if (cfg.text) patch[cfg.text] = text.trim();
      } else if (kind === "voice" || kind === "video") {
        let uc;
        try { uc = await valCleanUpload(blob, kind === "video" ? "mp4" : "mp3"); }
        catch (e) { setBusy(false); setErr(e.message || "Upload failed."); return; }
        newPath = uc.path;
        patch.file_path = uc.path;
        if (cfg.size) patch[cfg.size] = uc.size || blob.size;
        if (cfg.dur) patch[cfg.dur] = seconds;
      } else {
        const nm = file.name || "file";
        const dot = nm.lastIndexOf(".");
        const ext = dot >= 0 ? nm.slice(dot + 1).toLowerCase() : "bin";
        const _oe = (file.type || "").indexOf("video/") === 0 ? "mp4" : ((file.type || "").indexOf("audio/") === 0 ? "mp3" : ext);
        let uc;
        try { uc = await valCleanUpload(file, _oe); }
        catch (e) { setBusy(false); setErr(e.message || "Upload failed."); return; }
        newPath = uc.path;
        patch.file_path = uc.path;
        patch.file_name = nm;
        if (cfg.size) patch[cfg.size] = uc.size || file.size;
        patch.mime_type = file.type || null;
      }
      const upd = await supabase.from(item.table).update(patch).eq("id", item.id);
      if (upd.error) { setBusy(false); setErr(upd.error.message); if (newPath) { supabase.storage.from("val-messages").remove([newPath]); } return; }
      if (newPath && item.filePath && item.filePath !== newPath) { await supabase.storage.from("val-messages").remove([item.filePath]); }
      setBusy(false); setDone(true);
    } catch (e) {
      setBusy(false); setErr("Something went wrong: " + (e && e.message ? e.message : String(e)));
    }
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1100 };
  if (done) {
    return (
      <div onClick={onClose} style={overlay}>
        <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 420, background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 28, textAlign: "center" }}>
          <h3 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 8px" }}>Saved</h3>
          <p style={{ color: MUTED, fontSize: 14, margin: "0 0 18px", lineHeight: 1.5 }}>Your new version has replaced the old one. The title and who receives it stay the same.</p>
          <GoldBtn onClick={onDone}>Done</GoldBtn>
        </div>
      </div>
    );
  }
  const heading = kind === "replace" ? "Replace file" : kind === "text" ? "Rewrite" : "Re-record";
  const isRec = kind === "voice" || kind === "video";
  const acceptFor = item.mtype === "video" ? "video/*"
    : item.mtype === "handwritten" ? "image/*,application/pdf"
    : ((item.mtype === "photo" || item.mtype === "image") ? "image/*" : "");
  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto", background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 24 }}>
        <h3 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 4px" }}>{heading}</h3>
        <p style={{ color: MUTED, fontSize: 13, margin: "0 0 14px", lineHeight: 1.5 }}>This replaces the current version of "{item.title}". Everything else, including who receives it, stays the same.</p>
        {kind === "text" && (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} style={{ width: "100%", boxSizing: "border-box", background: DARK, color: TEXT, border: "1px solid " + BORDER, borderRadius: 10, padding: 12, fontSize: 14, fontFamily: "inherit", resize: "vertical" }} />
        )}
        {isRec && (
          <div style={{ marginBottom: 12 }}>
            {kind === "video" && (
              <video ref={liveRef} style={{ width: "100%", borderRadius: 10, background: "#000", display: recording ? "block" : "none", marginBottom: 10 }} playsInline />
            )}
            {mediaUrl && !recording && kind === "video" && (
              <video src={mediaUrl} controls style={{ width: "100%", borderRadius: 10, background: "#000", marginBottom: 10 }} />
            )}
            {mediaUrl && !recording && kind === "voice" && (
              <audio src={mediaUrl} controls style={{ width: "100%", marginBottom: 10 }} />
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {!recording ? (
                <GoldBtn outline onClick={startRec}>{blob ? "Record again" : "Start recording"}</GoldBtn>
              ) : (
                <GoldBtn onClick={stopRec}>Stop</GoldBtn>
              )}
              <span style={{ color: recording ? "#E88" : MUTED, fontSize: 13 }}>{recording ? ("Recording " + fmt(seconds)) : (blob ? ("Length " + fmt(seconds)) : "Not recorded yet")}</span>
            </div>
          </div>
        )}
        {kind === "replace" && (
          <div style={{ marginBottom: 12 }}>
            <input ref={fileRef} type="file" accept={acceptFor} onChange={pickFile} style={{ display: "none" }} />
            <GoldBtn outline onClick={() => fileRef.current && fileRef.current.click()}>Choose file</GoldBtn>
            <span style={{ color: MUTED, fontSize: 13, marginLeft: 10 }}>{file ? file.name : "No file chosen"}</span>
          </div>
        )}
        {err && <div style={{ color: "#E88", fontSize: 13, margin: "10px 0" }}>{err}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <GoldBtn outline onClick={onClose} style={{ flex: 1 }}>Cancel</GoldBtn>
          <GoldBtn onClick={save} disabled={busy} style={{ flex: 1 }}>{busy ? "Saving..." : "Save"}</GoldBtn>
        </div>
      </div>
    </div>
  );
};

const LibrarySection = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ messages: [], stories: [], media: [], confessions: [], answers: [], recipients: [], delivMap: {} });
  const [playing, setPlaying] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [reItem, setReItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch (e) { return ""; }
  };
  const typeKey = (table) => table === "stories" ? "story" : table === "media" ? "media" : table === "confessions" ? "confession" : table === "legacy_answers" ? "answer" : "message";

  const load = async () => {
    if (!supabase) { setLoading(false); return; }
    const { data: gu } = await supabase.auth.getUser();
    const id = gu && gu.user && gu.user.id;
    if (!id) { setLoading(false); return; }
    const ORD = { ascending: false };
    const [m, s, md, c, a2, r, dl] = await Promise.all([
      supabase.from("messages").select("id,title,message_type,category,body_text,file_path,created_at,message_recipients(recipient_id)").eq("user_id", id).order("created_at", ORD),
      supabase.from("stories").select("id,title,chapter,message_type,body_text,file_path,created_at").eq("user_id", id).order("created_at", ORD),
      supabase.from("media").select("id,kind,title,file_path,file_name,created_at").eq("user_id", id).order("created_at", ORD),
      supabase.from("confessions").select("id,message_type,body_text,file_path,organization,details,created_at").eq("user_id", id).order("created_at", ORD),
      supabase.from("legacy_answers").select("id,prompt_text,answer_type,answer_text,file_path,created_at").eq("user_id", id).order("created_at", ORD),
      supabase.from("recipients").select("id,full_name,relationship,email,phone,consent_status,created_at").eq("user_id", id).order("full_name"),
      // Who each non-message item is for. messages carry their own link through
      // message_recipients; everything else links through `deliveries`, which the
      // library never loaded — which is why nothing here could say who it was for.
      supabase.from("deliveries").select("recipient_id,item_type,item_id").eq("user_id", id),
    ]);
    setData({ messages: m.data || [], stories: s.data || [], media: md.data || [], confessions: c.data || [], answers: a2.data || [], recipients: r.data || [], deliveries: dl.data || [] });
    setLoading(false);
  };
  useEffect(() => { load(); }, []);


  const viewKindFor = (t) => {
    if (t === "voice") return "audio";
    if (t === "video") return "video";
    if (t === "image" || t === "photo") return "image";
    if (t === "doc" || t === "document") return "doc";
    if (t === "handwritten") return "letter";
    return "text";
  };
  const labelFor = (vk) => vk === "text" ? "Read" : (vk === "image" || vk === "letter") ? "View" : vk === "doc" ? "Open" : "Play";

  const playFile = async (path, vk, title) => {
    setErr("");
    const res = await supabase.storage.from("val-messages").createSignedUrl(path, 3600);
    if (res.error) { setErr("Could not load the file: " + res.error.message); return; }
    setPlaying({ kind: vk, title: title, url: res.data.signedUrl });
  };
  const openItem = (it) => {
    let vk = viewKindFor(it.mtype);
    if (vk === "letter") vk = letterViewKind(it.filePath);
    if (vk === "text" || !it.filePath) { setPlaying({ kind: "text", title: it.title, text: it.text || "(no written content)" }); return; }
    playFile(it.filePath, vk, it.title);
  };

  const doDelete = async (it) => {
    setBusy(true); setErr("");
    try {
      if (it.table === "messages") { await supabase.from("message_recipients").delete().eq("message_id", it.id); }
      if (it.table === "recipients") { await supabase.from("message_recipients").delete().eq("recipient_id", it.id); }
      if (it.table !== "messages" && it.table !== "recipients") { await supabase.from("deliveries").delete().eq("item_type", typeKey(it.table)).eq("item_id", it.id); }
      const del = await supabase.from(it.table).delete().eq("id", it.id);
      if (del.error) { setBusy(false); setErr(del.error.message); return; }
      if (it.filePath) { await supabase.storage.from("val-messages").remove([it.filePath]); }
      setConfirmId(null); setBusy(false);
      await load();
    } catch (e) { setBusy(false); setErr("Something went wrong: " + (e && e.message ? e.message : String(e))); }
  };

  // Every saved item, still tagged with the kind it is, plus the Legacy Members it
  // is addressed to. `recips` is an array because a message can be left for more
  // than one person.
  const deliveriesFor = (table, itemId) => {
    const t = typeKey(table);
    return (data.deliveries || []).filter((d) => d.item_type === t && d.item_id === itemId).map((d) => d.recipient_id);
  };
  const allItems = [
    ...(data.messages || []).map((m) => ({ id: m.id, table: "messages", kind: "Messages", title: m.title || "Untitled message", meta: (m.message_type || "text") + (m.category ? " \u00b7 " + m.category : "") + " \u00b7 " + fmtDate(m.created_at), mtype: m.message_type, filePath: m.file_path, text: m.body_text, recips: (m.message_recipients || []).map((mr) => mr.recipient_id) })),
    ...(data.stories || []).map((x) => ({ id: x.id, table: "stories", kind: "Stories", title: x.title || "Untitled story", meta: (x.message_type || "text") + (x.chapter ? " \u00b7 " + x.chapter : "") + " \u00b7 " + fmtDate(x.created_at), mtype: x.message_type, filePath: x.file_path, text: x.body_text, recips: deliveriesFor("stories", x.id) })),
    ...(data.media || []).map((x) => ({ id: x.id, table: "media", kind: "Photos, Videos & Files", title: x.title || x.file_name || x.kind, meta: x.kind + " \u00b7 " + fmtDate(x.created_at), mtype: x.kind, filePath: x.file_path, text: null, recips: deliveriesFor("media", x.id) })),
    ...(data.confessions || []).map((x) => ({ id: x.id, table: "confessions", kind: "Confessions", title: x.organization ? ("Confession \u00b7 " + x.organization) : "Confession", meta: (x.message_type || "text") + " \u00b7 " + fmtDate(x.created_at), mtype: x.message_type, filePath: x.file_path, text: x.body_text || x.details, recips: deliveriesFor("confessions", x.id) })),
  ];

  // Daily Answers stand alone on purpose. They are the User's own running log of
  // the Question of the Day — they feed Your Reminders, they are never addressed
  // to a Legacy Member, and nobody is meant to assign them to one. Listing them
  // under "Not yet assigned" would read as an unfinished job rather than a
  // complete one, so they are kept out of both the member groups and that bucket.
  const answerItems = (data.answers || []).map((x) => ({ id: x.id, table: "legacy_answers", kind: "Daily Answers", title: x.prompt_text || "Answer", meta: (x.answer_type || "text") + " \u00b7 " + fmtDate(x.created_at), mtype: x.answer_type, filePath: x.file_path, text: x.answer_text, recips: [] }));

  const nameOf = (rid) => {
    const r0 = (data.recipients || []).find((x) => x.id === rid);
    return (r0 && r0.full_name) || "";
  };

  // Group by Legacy Member first, then by kind — you nearly always arrive here
  // wanting "what did I leave for Sredrick", not "show me every photo".
  //
  // An item left for two people is listed under BOTH, because it genuinely belongs
  // to both. Its row then names the others it is shared with, so nobody deletes
  // what looks like one person's message and quietly removes it from someone else
  // as well — Delete removes the item itself, not just that one pairing.
  const memberGroups = (data.recipients || []).map((r0) => {
    const mine = allItems.filter((it) => (it.recips || []).indexOf(r0.id) !== -1);
    const kinds = [];
    mine.forEach((it) => {
      let k = kinds.find((x) => x.kind === it.kind);
      if (!k) { k = { kind: it.kind, items: [] }; kinds.push(k); }
      k.items.push(it);
    });
    return { id: r0.id, name: r0.full_name || "Legacy Member", relationship: r0.relationship || "", count: mine.length, kinds: kinds };
  });

  // Anything not addressed to anyone yet still has to be reachable, or a person
  // could save something and never find it again.
  const orphanItems = allItems.filter((it) => !(it.recips || []).length);
  const orphanKinds = [];
  orphanItems.forEach((it) => {
    let k = orphanKinds.find((x) => x.kind === it.kind);
    if (!k) { k = { kind: it.kind, items: [] }; orphanKinds.push(k); }
    k.items.push(it);
  });

  const groups = [{ key: "all", items: allItems.concat(answerItems) }];
  const total = groups.reduce((n, g) => n + g.items.length, 0) + (data.recipients || []).length;

  const rowStyle = { display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderBottom: "1px solid " + BORDER };
  const smallBtn = { background: "transparent", border: "1px solid " + BORDER, color: TEXT, borderRadius: 7, padding: "5px 12px", fontSize: 12, cursor: "pointer", flexShrink: 0 };

  const renderRow = (it) => (
    <div key={it.id} style={rowStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: TEXT, fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</div>
        <div style={{ color: MUTED, fontSize: 12, textTransform: "capitalize" }}>{it.meta}</div>
        {it.sharedWith && it.sharedWith.length > 0 && (
          <div style={{ color: MUTED, fontSize: 11.5, fontStyle: "italic", marginTop: 2 }}>
            Also left for {it.sharedWith.join(", ")} \u2014 deleting removes it for everyone
          </div>
        )}
      </div>
      <button onClick={() => setReItem(it)} style={smallBtn}>{(it.table === "media" || it.mtype === "handwritten") ? "Replace" : (viewKindFor(it.mtype) === "text" ? "Rewrite" : "Re-record")}</button>
      <button onClick={() => openItem(it)} style={smallBtn}>{labelFor(viewKindFor(it.mtype))}</button>
      {confirmId === it.id ? (
        <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
          <button onClick={() => doDelete(it)} disabled={busy} style={{ ...smallBtn, borderColor: "#E88", color: "#E88" }}>{busy ? "..." : "Confirm"}</button>
          <button onClick={() => setConfirmId(null)} style={smallBtn}>Cancel</button>
        </span>
      ) : (
        <button onClick={() => setConfirmId(it.id)} style={{ ...smallBtn, color: "#E88", borderColor: "#5a3030" }}>Delete</button>
      )}
    </div>
  );

  return (
    <div className="wrap" style={{ marginTop: 8 }}>
      <div style={{ background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: "22px 24px" }}>
        <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 4px" }}>Your Legacy Library</h2>
        <p style={{ color: MUTED, fontSize: 13, margin: "0 0 16px" }}>Everything you have saved. Play it back or remove anything you no longer want.</p>
        {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
        {loading ? (
          <div style={{ color: MUTED, fontSize: 14, padding: "10px 0" }}>Loading your library...</div>
        ) : total === 0 ? (
          <div style={{ color: MUTED, fontSize: 14, padding: "10px 0" }}>You have not saved anything yet. Use the options above to begin building your Legacy.</div>
        ) : (
          <div>
            {memberGroups.map((mg) => (
              <div key={mg.id} style={{ marginBottom: 22 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, paddingBottom: 6, borderBottom: "1px solid " + BORDER, marginBottom: 6 }}>
                  <span style={{ width: 26, height: 26, borderRadius: "50%", background: GOLD, color: "#111", fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {(String(mg.name).trim()[0] || "?").toUpperCase()}
                  </span>
                  <span style={{ color: TEXT, fontSize: 16, fontWeight: 700 }}>{mg.name}</span>
                  {mg.relationship ? <span style={{ color: MUTED, fontSize: 12.5, textTransform: "capitalize" }}>{mg.relationship}</span> : null}
                  <span style={{ color: MUTED, fontSize: 12.5, marginLeft: "auto" }}>{mg.count === 1 ? "1 item" : mg.count + " items"}</span>
                </div>
                {mg.kinds.length === 0 ? (
                  <div style={{ color: MUTED, fontSize: 13, padding: "6px 0 10px" }}>
                    Nothing left for {String(mg.name).trim().split(/\s+/)[0]} yet.
                  </div>
                ) : mg.kinds.map((k) => (
                  <div key={mg.id + "-" + k.kind} style={{ marginBottom: 10 }}>
                    <div style={{ color: GOLD, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", margin: "8px 0 2px" }}>{k.kind} ({k.items.length})</div>
                    {k.items.map((it) => renderRow({ ...it, sharedWith: (it.recips || []).filter((rid) => rid !== mg.id).map(nameOf).filter(Boolean) }))}
                  </div>
                ))}
              </div>
            ))}

            {answerItems.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, paddingBottom: 6, borderBottom: "1px solid " + BORDER, marginBottom: 6 }}>
                  <span style={{ width: 26, height: 26, borderRadius: "50%", background: SURFACE2, border: "1px solid " + GOLD, color: GOLD, fontSize: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>&#10022;</span>
                  <span style={{ color: TEXT, fontSize: 16, fontWeight: 700 }}>Daily Answers</span>
                  <span style={{ color: MUTED, fontSize: 12.5, marginLeft: "auto" }}>{answerItems.length === 1 ? "1 answer" : answerItems.length + " answers"}</span>
                </div>
                <div style={{ color: MUTED, fontSize: 12.5, margin: "0 0 6px" }}>
                  Your answers to the Question of the Day. These are just for you \u2014 they are not addressed to a Legacy Member, and they help shape Your Reminders.
                </div>
                {answerItems.map((it) => renderRow(it))}
              </div>
            )}

            {orphanItems.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, paddingBottom: 6, borderBottom: "1px solid " + BORDER, marginBottom: 6 }}>
                  <span style={{ width: 26, height: 26, borderRadius: "50%", background: SURFACE2, border: "1px solid " + BORDER, color: MUTED, fontSize: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>?</span>
                  <span style={{ color: TEXT, fontSize: 16, fontWeight: 700 }}>Not yet assigned</span>
                  <span style={{ color: MUTED, fontSize: 12.5, marginLeft: "auto" }}>{orphanItems.length === 1 ? "1 item" : orphanItems.length + " items"}</span>
                </div>
                <div style={{ color: MUTED, fontSize: 12.5, margin: "0 0 6px" }}>
                  Saved, but not addressed to anyone yet. Nobody receives these until you choose who they are for.
                </div>
                {orphanKinds.map((k) => (
                  <div key={"orphan-" + k.kind} style={{ marginBottom: 10 }}>
                    <div style={{ color: GOLD, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", margin: "8px 0 2px" }}>{k.kind} ({k.items.length})</div>
                    {k.items.map((it) => renderRow(it))}
                  </div>
                ))}
              </div>
            )}
            {(data.recipients || []).length > 0 && (
              <div style={{ marginBottom: 4 }}>
                <div style={{ color: GOLD, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>Your Legacy Circle ({data.recipients.length})</div>
                {/* Legacy Circle — first names of the Legacy Members in this User's circle. */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "8px 0 14px" }}>
                  {data.recipients.length === 0 ? (
                    <span style={{ color: MUTED, fontSize: 13.5 }}>
                      No Legacy Members yet — add the people who should receive your legacy.
                    </span>
                  ) : (
                    data.recipients.map((r, i) => {
                      const nm = String(r.full_name || "").trim();
                      const first = nm ? nm.split(/\s+/)[0] : "Legacy Member";
                      return (
                        <span
                          key={r.id || "lm-" + i}
                          title={nm || "Legacy Member"}
                          style={{ display: "inline-flex", alignItems: "center", gap: 7, border: `1px solid ${BORDER}`, borderRadius: 999, padding: "5px 13px 5px 6px", background: SURFACE2, color: TEXT, fontSize: 14 }}
                        >
                          <span style={{ width: 23, height: 23, borderRadius: "50%", background: GOLD, color: "#111", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                            {(first[0] || "?").toUpperCase()}
                          </span>
                          {first}
                        </span>
                      );
                    })
                  )}
                </div>
                {data.recipients.map((r) => (
                  <div key={r.id} style={rowStyle}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: TEXT, fontSize: 14, fontWeight: 600 }}>{r.full_name}</div>
                      <div style={{ color: MUTED, fontSize: 12 }}>{(r.relationship || "") + (r.phone ? " \u00b7 " + r.phone : "") + " \u00b7 " + (r.consent_status === "accepted" ? "Accepted" : "Pending")}</div>
                    </div>
                    {confirmId === r.id ? (
                      <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                        <button onClick={() => doDelete({ table: "recipients", id: r.id })} disabled={busy} style={{ ...smallBtn, borderColor: "#E88", color: "#E88" }}>{busy ? "..." : "Confirm"}</button>
                        <button onClick={() => setConfirmId(null)} style={smallBtn}>Cancel</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmId(r.id)} style={{ ...smallBtn, color: "#E88", borderColor: "#5a3030" }}>Delete</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {reItem && (
        <ReRecordModal item={reItem} onClose={() => setReItem(null)} onDone={async () => { setReItem(null); await load(); }} />
      )}
      {playing && (
        <div onClick={() => setPlaying(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1100 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 24 }}>
            <h3 style={{ color: TEXT, fontFamily: "Georgia, serif", margin: "0 0 14px" }}>{playing.title}</h3>
            {playing.kind === "audio" && <audio src={playing.url} controls autoPlay style={{ width: "100%" }} />}
            {playing.kind === "video" && <video src={playing.url} controls autoPlay style={{ width: "100%", borderRadius: 10 }} />}
            {playing.kind === "image" && <img src={playing.url} alt={playing.title} style={{ width: "100%", borderRadius: 10 }} />}
            {playing.kind === "doc" && <a href={playing.url} target="_blank" rel="noreferrer" style={{ color: GOLD }}>Open file in a new tab</a>}
            {playing.kind === "text" && <p style={{ color: TEXT, fontSize: 15, lineHeight: 1.6, whiteSpace: "pre-wrap", margin: 0 }}>{playing.text}</p>}
            <div style={{ marginTop: 18 }}><GoldBtn onClick={() => setPlaying(null)}>Close</GoldBtn></div>
          </div>
        </div>
      )}
    </div>
  );
};

const RecipientPageModal = ({ recipient, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [err, setErr] = useState("");

  const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch (e) { return ""; } };
  const viewKindFor = (t) => { if (t === "voice") return "audio"; if (t === "video") return "video"; if (t === "image" || t === "photo") return "image"; if (t === "doc" || t === "document") return "doc"; if (t === "handwritten") return "letter"; return "text"; };
  const labelFor = (vk) => vk === "text" ? "Read" : vk === "image" ? "View" : vk === "doc" ? "Open" : "Play";

  const load = async () => {
    if (!supabase) { setLoading(false); return; }
    setErr("");
    const rid = recipient.id;
    const mr = await supabase.from("message_recipients").select("message_id").eq("recipient_id", rid);
    const msgIds = (mr.data || []).map((x) => x.message_id);
    const dv = await supabase.from("deliveries").select("item_type,item_id").eq("recipient_id", rid);
    const byType = {};
    (dv.data || []).forEach((d) => { (byType[d.item_type] = byType[d.item_type] || []).push(d.item_id); });
    const q = async (table, sel, ids) => (ids && ids.length) ? (((await supabase.from(table).select(sel).in("id", ids)).data) || []) : [];
    const [msgs, stories, media, conf, ans] = await Promise.all([
      msgIds.length ? q("messages", "id,title,message_type,category,body_text,file_path,created_at", msgIds) : Promise.resolve([]),
      q("stories", "id,title,chapter,message_type,body_text,file_path,created_at", byType.story),
      q("media", "id,kind,title,file_path,file_name,created_at", byType.media),
      q("confessions", "id,message_type,body_text,file_path,organization,details,created_at", byType.confession),
      q("legacy_answers", "id,prompt_text,answer_type,answer_text,file_path,created_at", byType.answer),
    ]);
    const G = [
      { key: "messages", label: "Messages", items: msgs.map((m) => ({ id: m.id, title: m.title || "Untitled message", meta: (m.message_type || "text") + (m.category ? " \u00b7 " + m.category : "") + " \u00b7 " + fmtDate(m.created_at), mtype: m.message_type, filePath: m.file_path, text: m.body_text })) },
      { key: "stories", label: "Stories", items: stories.map((s) => ({ id: s.id, title: s.title || "Untitled story", meta: (s.message_type || "text") + (s.chapter ? " \u00b7 " + s.chapter : "") + " \u00b7 " + fmtDate(s.created_at), mtype: s.message_type, filePath: s.file_path, text: s.body_text })) },
      { key: "media", label: "Photos, Videos & Files", items: media.map((x) => ({ id: x.id, title: x.title || x.file_name || x.kind, meta: x.kind + " \u00b7 " + fmtDate(x.created_at), mtype: x.kind, filePath: x.file_path, text: null })) },
      { key: "confessions", label: "Confessions", items: conf.map((c) => ({ id: c.id, title: c.organization ? ("Confession \u00b7 " + c.organization) : "Confession", meta: (c.message_type || "text") + " \u00b7 " + fmtDate(c.created_at), mtype: c.message_type, filePath: c.file_path, text: c.body_text || c.details })) },
      { key: "answers", label: "Daily Answers", items: ans.map((a) => ({ id: a.id, title: a.prompt_text || "Answer", meta: (a.answer_type || "text") + " \u00b7 " + fmtDate(a.created_at), mtype: a.answer_type, filePath: a.file_path, text: a.answer_text })) },
    ];
    setGroups(G);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openItem = async (it) => {
    let vk = viewKindFor(it.mtype);
    if (vk === "letter") vk = letterViewKind(it.filePath);
    if (vk === "text" || !it.filePath) { setPlaying({ kind: "text", title: it.title, text: it.text || "(no written content)" }); return; }
    setErr("");
    const res = await supabase.storage.from("val-messages").createSignedUrl(it.filePath, 3600);
    if (res.error) { setErr("Could not load the file: " + res.error.message); return; }
    setPlaying({ kind: vk, title: it.title, url: res.data.signedUrl });
  };

  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const rowStyle = { display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderBottom: "1px solid " + BORDER };
  const smallBtn = { background: "transparent", border: "1px solid " + BORDER, color: TEXT, borderRadius: 7, padding: "5px 12px", fontSize: 12, cursor: "pointer", flexShrink: 0 };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 20px", zIndex: 1050, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 640, background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: "24px 26px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: 0 }}>{recipient.full_name}</h2>
          <button onClick={onClose} style={smallBtn}>Close</button>
        </div>
        <p style={{ color: MUTED, fontSize: 13, margin: "0 0 16px", lineHeight: 1.5 }}>{(recipient.relationship || "Legacy Member") + " \u00b7 Kept private and delivered to " + (recipient.full_name || "them") + " only after your passing."}</p>
        {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
        {loading ? (
          <div style={{ color: MUTED, fontSize: 14, padding: "10px 0" }}>Loading...</div>
        ) : total === 0 ? (
          <div style={{ color: MUTED, fontSize: 14, padding: "10px 0", lineHeight: 1.5 }}>Nothing has been saved for {recipient.full_name} yet. When you record a message or story, or upload a file, choose {recipient.full_name} and it will appear here.</div>
        ) : (
          <div>
            {groups.map((g) => g.items.length === 0 ? null : (
              <div key={g.key} style={{ marginBottom: 18 }}>
                <div style={{ color: GOLD, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>{g.label} ({g.items.length})</div>
                {g.items.map((it) => (
                  <div key={it.id} style={rowStyle}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: TEXT, fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</div>
                      <div style={{ color: MUTED, fontSize: 12, textTransform: "capitalize" }}>{it.meta}</div>
                    </div>
                    <button onClick={() => openItem(it)} style={smallBtn}>{labelFor(viewKindFor(it.mtype))}</button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      {playing && (
        <div onClick={(e) => { e.stopPropagation(); setPlaying(null); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1200 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 560, background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: 22 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{playing.title}</h3>
              <button onClick={() => setPlaying(null)} style={smallBtn}>Close</button>
            </div>
            {playing.kind === "text" ? (
              <div style={{ color: TEXT, fontSize: 15, lineHeight: 1.6, whiteSpace: "pre-wrap", maxHeight: "60vh", overflowY: "auto" }}>{playing.text}</div>
            ) : playing.kind === "audio" ? (
              <audio src={playing.url} controls autoPlay style={{ width: "100%" }} />
            ) : playing.kind === "video" ? (
              <video src={playing.url} controls autoPlay style={{ width: "100%", borderRadius: 10, background: "#000" }} />
            ) : playing.kind === "image" ? (
              <img src={playing.url} alt="" style={{ width: "100%", borderRadius: 10 }} />
            ) : (
              <a href={playing.url} target="_blank" rel="noreferrer" style={{ color: GOLD }}>Open file</a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TrustedVerifiersSection = () => {
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", relationship: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    if (!supabase) { setLoading(false); return; }
    const { data: gu } = await supabase.auth.getUser();
    const id = gu && gu.user && gu.user.id;
    if (!id) { setLoading(false); return; }
    const res = await supabase.from("trusted_verifiers").select("id,full_name,email,phone,relationship,status,created_at").eq("user_id", id).order("created_at", { ascending: false });
    setList(res.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    if (!form.full_name.trim()) { setErr("Please enter a name."); return; }
    if (!form.email.trim() && !form.phone.trim()) { setErr("Add an email or phone so they can be reached."); return; }
    if (!supabase) { setErr("Saving is not configured yet."); return; }
    setBusy(true);
    try {
      const { data: gu } = await supabase.auth.getUser();
      const id = gu && gu.user && gu.user.id;
      const row = { user_id: id, full_name: form.full_name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null, relationship: form.relationship.trim() || null };
      const ins = await supabase.from("trusted_verifiers").insert(row);
      if (ins.error) { setBusy(false); setErr(ins.error.message); return; }
      setBusy(false); setAdding(false); setForm({ full_name: "", email: "", phone: "", relationship: "" });
      await load();
    } catch (e) { setBusy(false); setErr("Something went wrong: " + (e && e.message ? e.message : String(e))); }
  };

  const doDelete = async (vid) => {
    setBusy(true); setErr("");
    try {
      const del = await supabase.from("trusted_verifiers").delete().eq("id", vid);
      if (del.error) { setBusy(false); setErr(del.error.message); return; }
      setConfirmId(null); setBusy(false); await load();
    } catch (e) { setBusy(false); setErr("Something went wrong: " + (e && e.message ? e.message : String(e))); }
  };

  const inputStyle = { width: "100%", boxSizing: "border-box", background: DARK, color: TEXT, border: "1px solid " + BORDER, borderRadius: 8, padding: "9px 10px", fontSize: 14, marginBottom: 8 };
  const smallBtn = { background: "transparent", border: "1px solid " + BORDER, color: TEXT, borderRadius: 7, padding: "5px 12px", fontSize: 12, cursor: "pointer", flexShrink: 0 };
  const rowStyle = { display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderBottom: "1px solid " + BORDER };

  return (
    <div className="wrap" style={{ marginTop: 8 }}>
      <div style={{ background: SURFACE, border: "1px solid " + BORDER, borderRadius: 16, padding: "22px 24px" }}>
        <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 4px" }}>Trusted Verifiers</h2>
        <p style={{ color: MUTED, fontSize: 13, margin: "0 0 16px", lineHeight: 1.5 }}>These are the people you trust to confirm your passing. Only someone on this list can begin the release of your legacy, and only after a waiting period during which you can stop it. Nothing is shared with them now.</p>
        {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
        {loading ? (
          <div style={{ color: MUTED, fontSize: 14, padding: "10px 0" }}>Loading...</div>
        ) : (
          <div>
            {list.length === 0 ? (
              <div style={{ color: MUTED, fontSize: 14, padding: "4px 0 12px" }}>You have not named anyone yet. Add someone you trust to confirm your passing when the time comes.</div>
            ) : (
              list.map((v) => (
                <div key={v.id} style={rowStyle}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: TEXT, fontSize: 14, fontWeight: 600 }}>{v.full_name}</div>
                    <div style={{ color: MUTED, fontSize: 12 }}>{(v.relationship || "Trusted verifier") + (v.email ? " \u00b7 " + v.email : "") + (v.phone ? " \u00b7 " + v.phone : "")}</div>
                  </div>
                  {confirmId === v.id ? (
                    <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      <button onClick={() => doDelete(v.id)} disabled={busy} style={{ ...smallBtn, borderColor: "#E88", color: "#E88" }}>{busy ? "..." : "Remove"}</button>
                      <button onClick={() => setConfirmId(null)} style={smallBtn}>Cancel</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmId(v.id)} style={{ ...smallBtn, color: "#E88", borderColor: "#5a3030" }}>Remove</button>
                  )}
                </div>
              ))
            )}
            {adding ? (
              <div style={{ marginTop: 14, padding: 14, background: DARK, borderRadius: 10, border: "1px solid " + BORDER }}>
                <input placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} style={inputStyle} />
                <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} />
                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={inputStyle} />
                <input placeholder="Relationship (optional)" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} style={{ ...inputStyle, marginBottom: 12 }} />
                <div style={{ display: "flex", gap: 10 }}>
                  <GoldBtn outline onClick={() => { setAdding(false); setErr(""); }} style={{ flex: 1 }}>Cancel</GoldBtn>
                  <GoldBtn onClick={save} disabled={busy} style={{ flex: 1 }}>{busy ? "Saving..." : "Add verifier"}</GoldBtn>
                </div>
              </div>
            ) : (
              <button onClick={() => { setAdding(true); setErr(""); }} style={{ ...smallBtn, marginTop: 14 }}>+ Add a trusted verifier</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ReleaseGraceBanner = () => {
  const [caseRow, setCaseRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!supabase) return;
    const { data: gu } = await supabase.auth.getUser();
    const id = gu && gu.user && gu.user.id;
    if (!id) return;
    const res = await supabase.from("verification_cases").select("id,grace_until,created_at").eq("user_id", id).is("cancelled_at", null).gt("grace_until", new Date().toISOString()).order("grace_until", { ascending: true }).limit(1);
    if (res.data && res.data.length) setCaseRow(res.data[0]);
  };
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    setErr(""); setBusy(true);
    try {
      const { data: gu } = await supabase.auth.getUser();
      const id = gu && gu.user && gu.user.id;
      const upd = await supabase.from("verification_cases").update({ cancelled_at: new Date().toISOString() }).eq("user_id", id).is("cancelled_at", null);
      if (upd.error) { setBusy(false); setErr(upd.error.message); return; }
      setBusy(false); setCancelled(true); setCaseRow(null);
    } catch (e) { setBusy(false); setErr("Something went wrong: " + (e && e.message ? e.message : String(e))); }
  };

  if (cancelled) {
    return (
      <div className="wrap" style={{ marginTop: 12 }}>
        <div style={{ background: "#14210f", border: "1px solid #2f5a2f", borderRadius: 14, padding: "16px 20px" }}>
          <div style={{ color: "#9fe0a0", fontSize: 15, fontWeight: 600 }}>Release stopped.</div>
          <div style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>The report has been cancelled. Nothing has been shared, and your Legacy stays private.</div>
        </div>
      </div>
    );
  }
  if (!caseRow) return null;
  let when = "soon";
  try { when = new Date(caseRow.grace_until).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); } catch (e) { when = "soon"; }
  return (
    <div className="wrap" style={{ marginTop: 12 }}>
      <div style={{ background: "#241014", border: "1px solid #7a2a2a", borderRadius: 14, padding: "18px 22px" }}>
        <div style={{ color: "#F0B4B4", fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Action needed</div>
        <div style={{ color: TEXT, fontSize: 18, fontFamily: "Georgia, serif", marginBottom: 6 }}>A passing has been reported</div>
        <div style={{ color: MUTED, fontSize: 14, lineHeight: 1.55, marginBottom: 14 }}>{"Someone on your trusted list reported that you have passed. Your Legacy will be released to your Legacy Members on " + when + " unless you stop it. If you are reading this, you are still here \u2014 please cancel below."}</div>
        {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <GoldBtn onClick={cancel} disabled={busy}>{busy ? "Stopping..." : "I am still here \u2014 cancel this"}</GoldBtn>
      </div>
    </div>
  );
};

// Fire the Legacy Member notifications for a case that staff just approved, and
// report the outcome in words a reviewer can act on.
//
// Approving a case mints the access tokens; it does NOT send anything. Sending is
// this separate call. Two things went wrong with that split and both are fixed here:
// the ReleaseReviewSection approve button never made the call at all, so a case
// approved from that screen released the content and reported "Legacy Members
// granted access" while nobody was ever told; and the other screen fired it
// without awaiting or checking, so a failed send looked identical to a good one.
//
// Never throws. A release that genuinely succeeded must not be reported as a
// failure just because the notifier hiccuped — but a failed send must never be
// reported as a success either, which is why the caller appends this to its note.
async function notifyReleaseForCase(caseId) {
  try {
    const r = await fetch("/.netlify/functions/send-recipient-links", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ case_id: caseId }),
    });
    const j = await r.json().catch(() => null);
    if (!j || j.ok !== true) return "Notifications FAILED to send \u2014 retry before closing this case.";
    const results = j.results || [];
    const sent = results.filter((x) => x && x.sent).length;
    const bad = results.filter((x) => x && (x.failed || x.skipped)).length;
    if (!sent && !bad) return "No notifications were pending.";
    return sent + " Legacy Member(s) notified" + (bad ? ", " + bad + " FAILED \u2014 check the case." : ".");
  } catch (e) {
    return "Notifications FAILED to send \u2014 retry before closing this case.";
  }
}

// getUserMedia fails for four quite different reasons and the remedy is different
// for every one of them, so name the one that actually happened. Every recorder on
// this site used to say "please allow access" no matter what went wrong, which sends
// someone to the browser permission screen even when the real problem is that a
// video-call app is already holding the camera — the single most common cause, and
// one no amount of clicking Allow will fix.
//
// Recording IS the product. A person who hits this and cannot tell why is a person
// who does not leave a message, so the wording matters more here than almost
// anywhere else in the app.
// Acquire a camera/microphone stream, releasing anything this page is still holding
// first, and retrying once if the device reports busy.
//
// The usual way a recorder gets wedged is our own doing, not another app's:
// getUserMedia succeeds so the camera is now held, something after it throws, the
// catch shows an error, and the stream is never stopped. The modal stays open so
// nothing unmounts, the page keeps the device, and every retry from then on fails
// with NotReadableError telling the person "another program is using your camera"
// — which is true, and the other program is this page. Only a reload cleared it.
// Releasing before every request makes that state unreachable.
//
// The single retry is for the honest version of the same error: a camera let go by
// a video call a moment ago can still report busy for a few hundred milliseconds.
// One short wait turns a dead end into a hiccup.
async function acquireMediaStream(constraints, releaseFirst) {
  if (typeof releaseFirst === "function") releaseFirst();
  const wantsVideo = !!(constraints && constraints.video);
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (e) {
    const name = (e && e.name) || "";
    const busy = name === "NotReadableError" || name === "AbortError" || name === "TrackStartError";
    if (!busy) throw e;

    // A camera released a moment ago can still report busy, so give it one beat.
    await new Promise((r) => setTimeout(r, 400));
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e2) { /* fall through to the per-device walk below */ }

    if (!wantsVideo) throw e;

    // THE VIRTUAL CAMERA TRAP.
    //
    // Asking for "any camera" makes the browser hand back whichever it lists
    // first, and on Windows that is very often a VIRTUAL camera rather than the
    // real webcam — LSVCam, OBS, NVIDIA Broadcast, ManyCam, a vendor utility, a
    // Windows Hello infrared sensor. Most of them cannot be opened at all unless
    // their host application happens to be running, and they fail with
    // NotReadableError: the same error a genuinely busy device gives.
    //
    // The person sees "another program is using your camera", goes hunting for a
    // program that does not exist, and gives up — while their actual webcam sat
    // there free the whole time. Confirmed exactly that on 2026-09-02: LSVCam
    // failed, the EMEET webcam beside it opened first try.
    //
    // So do not accept the browser's first pick. Walk every camera and take the
    // first that genuinely opens.
    let devices = [];
    try { devices = await navigator.mediaDevices.enumerateDevices(); } catch (e3) { devices = []; }
    const cams = devices.filter((d) => d.kind === "videoinput");
    for (const cam of cams) {
      try {
        return await navigator.mediaDevices.getUserMedia({
          audio: (constraints && constraints.audio) || false,
          video: { deviceId: { exact: cam.deviceId } },
        });
      } catch (e4) { /* that one is unusable too — try the next */ }
    }
    throw e;
  }
}

function mediaErrorMessage(e, noun) {
  const name = (e && e.name) || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
    return "Your browser is blocking access to your " + noun + ". Click the camera icon at the right of the address bar, choose Allow, then try again.";
  }
  if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") {
    return "Another program is already using your " + noun + ". Close anything else that might have it open \u2014 a video call, another browser tab, the Camera app \u2014 then try again.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "We could not find a " + noun + " on this device. Check that it is connected and enabled, then try again.";
  }
  if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError") {
    return "Your " + noun + " does not support the settings we asked for. If you have another camera or microphone, try selecting it in your browser settings.";
  }
  return "Could not access your " + noun + ". Please allow access and try again." + (name ? " (" + name + ")" : "");
}

const ReleaseReviewSection = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [briefs, setBriefs] = useState({});
  const [briefErr, setBriefErr] = useState({});
  const [briefBusy, setBriefBusy] = useState(null);

  const load = async () => {
    if (!supabase) { setLoading(false); return; }
    const adm = await supabase.rpc("is_current_user_admin");
    const ok = adm.data === true;
    setIsAdmin(ok);
    if (!ok) { setLoading(false); return; }
    const res = await supabase.rpc("admin_pending_cases");
    if (!res.error) setCases(res.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch (e) { return ""; } };

  const approve = async (id) => {
    setErr(""); setNote(""); setBusyId(id);
    try {
      const res = await supabase.rpc("approve_case", { p_case_id: id });
      if (res.error) { setBusyId(null); setErr(res.error.message); return; }
      const r = res.data || {};
      setBusyId(null); setConfirmId(null);
      const sent = await notifyReleaseForCase(id);
      setNote("Released \u2014 " + (r.items_released || 0) + " items unlocked, " + (r.recipients_tokened || 0) + " Legacy Members granted access. " + sent);
      await load();
    } catch (e) { setBusyId(null); setErr("Something went wrong: " + (e && e.message ? e.message : String(e))); }
  };

  const reject = async (id) => {
    setErr(""); setNote(""); setBusyId(id);
    try {
      const res = await supabase.rpc("reject_case", { p_case_id: id, p_reason: null });
      if (res.error) { setBusyId(null); setErr(res.error.message); return; }
      setBusyId(null); setConfirmId(null); setNote("Case rejected. Nothing was released.");
      await load();
    } catch (e) { setBusyId(null); setErr("Something went wrong: " + (e && e.message ? e.message : String(e))); }
  };

  const genBrief = async (id) => {
    setBriefBusy(id);
    setBriefErr((m) => ({ ...m, [id]: "" }));
    try {
      const sess = await supabase.auth.getSession();
      const token = sess.data && sess.data.session ? sess.data.session.access_token : "";
      const resp = await fetch("/.netlify/functions/generate-case-brief", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ case_id: id }),
      });
      const txt = await resp.text();
      let data = {};
      try { data = JSON.parse(txt); } catch (e) { data = {}; }
      if (!resp.ok) {
        setBriefBusy(null);
        setBriefErr((m) => ({ ...m, [id]: data.error || "Could not generate the brief right now." }));
        return;
      }
      setBriefBusy(null);
      setBriefs((m) => ({ ...m, [id]: data.brief || "(no brief returned)" }));
    } catch (e) {
      setBriefBusy(null);
      setBriefErr((m) => ({ ...m, [id]: "Something went wrong generating the brief." }));
    }
  };

  if (loading) return null;
  if (!isAdmin) return null;

  const smallBtn = { background: "transparent", border: "1px solid " + BORDER, color: TEXT, borderRadius: 7, padding: "5px 12px", fontSize: 12, cursor: "pointer", flexShrink: 0 };
  const rowStyle = { padding: "12px 0", borderBottom: "1px solid " + BORDER };

  return (
    <div className="wrap" style={{ marginTop: 12 }}>
      <div style={{ background: SURFACE, border: "1px solid " + GOLD_DARK, borderRadius: 16, padding: "22px 24px" }}>
        <div style={{ color: GOLD, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>{"Admin \u00b7 Release Review"}</div>
        <h2 style={{ color: GOLD, fontFamily: "Georgia, serif", margin: "0 0 4px" }}>Cases awaiting your approval</h2>
        <p style={{ color: MUTED, fontSize: 13, margin: "0 0 14px", lineHeight: 1.5 }}>{"These reports have passed their 30-day grace period without being cancelled. Approving releases the full library of that person to their Legacy Members. This cannot be undone \u2014 please review carefully."}</p>
        {note && <div style={{ color: "#9fe0a0", fontSize: 13, marginBottom: 10 }}>{note}</div>}
        {err && <div style={{ color: "#E88", fontSize: 13, marginBottom: 10 }}>{err}</div>}
        {cases.length === 0 ? (
          <div style={{ color: MUTED, fontSize: 14, padding: "6px 0" }}>No cases are awaiting review right now.</div>
        ) : (
          cases.map((c) => (
            <div key={c.case_id} style={rowStyle}>
              <div style={{ color: TEXT, fontSize: 15, fontWeight: 600 }}>{c.subject_name || c.subject_email || "Unknown user"}</div>
              <div style={{ color: MUTED, fontSize: 12, marginBottom: 8 }}>{"Reported by " + (c.reported_by_name || "someone") + " \u00b7 grace ended " + fmtDate(c.grace_until) + " \u00b7 " + (c.item_count || 0) + " items \u00b7 " + (c.recipient_count || 0) + " Legacy Members"}</div>
              {briefs[c.case_id] && (
                <div style={{ background: "#161616", border: "1px solid " + BORDER, borderRadius: 10, padding: "10px 12px", margin: "0 0 10px", color: TEXT, fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                  <div style={{ color: GOLD, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5 }}>{"AI Case Brief"}</div>
                  {briefs[c.case_id]}
                </div>
              )}
              {briefErr[c.case_id] && (
                <div style={{ color: "#E88", fontSize: 12, marginBottom: 8 }}>{briefErr[c.case_id]}</div>
              )}
              <div style={{ marginBottom: 8 }}>
                <button onClick={() => genBrief(c.case_id)} disabled={briefBusy === c.case_id} style={smallBtn}>{briefBusy === c.case_id ? "Thinking..." : (briefs[c.case_id] ? "Regenerate AI brief" : "Generate AI brief")}</button>
              </div>
              {confirmId === c.case_id ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ color: "#F0B4B4", fontSize: 13 }}>{"Release the library for " + (c.subject_name || "this user") + " now?"}</span>
                  <GoldBtn onClick={() => approve(c.case_id)} disabled={busyId === c.case_id}>{busyId === c.case_id ? "Releasing..." : "Yes, release"}</GoldBtn>
                  <button onClick={() => setConfirmId(null)} style={smallBtn}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setConfirmId(c.case_id); setErr(""); setNote(""); }} style={{ ...smallBtn, color: GOLD, borderColor: GOLD_DARK }}>Approve and release</button>
                  <button onClick={() => reject(c.case_id)} disabled={busyId === c.case_id} style={{ ...smallBtn, color: "#E88", borderColor: "#5a3030" }}>Reject</button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const Dashboard = ({ user, setScreen }) => {
  const [profile, setProfile] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [answeredSections, setAnsweredSections] = useState(0);
  const [, setLoading] = useState(true);
  const [showAddRecip, setShowAddRecip] = useState(false);
  const [showRecord, setShowRecord] = useState(false);
  // Set when the Record a Message box is opened from a reminder, so it opens
  // with that reminder's Category already chosen. Cleared on close so the next
  // plain "Record New Message" starts neutral.
  const [recordCategory, setRecordCategory] = useState(null);
  const [showConfession, setShowConfession] = useState(false);
  const [qotdType, setQotdType] = useState(null);
  const [qotdAnswered, setQotdAnswered] = useState(false);
  const [qotdSkipped, setQotdSkipped] = useState(false);
  const [showStory, setShowStory] = useState(false);
  const [uploadKind, setUploadKind] = useState(null);
  const [openRecip, setOpenRecip] = useState(null);
  const [sib, setSib] = useState("");
  const [par, setPar] = useState("");
  const [idBusy, setIdBusy] = useState(false);
  const [idMsg, setIdMsg] = useState("");
  const [theme, setTheme] = useState(() => { try { return localStorage.getItem("val_theme") === "dark" ? "dark" : "light"; } catch (e) { return "light"; } });
  const toggleTheme = () => { setTheme((t) => { const nt = t === "dark" ? "light" : "dark"; try { localStorage.setItem("val_theme", nt); } catch (e) {} return nt; }); };

  useEffect(() => {
    let active = true;
    (async () => {
      if (!supabase) {
        setLoading(false);
        return;
      }
      const { data: gu } = await supabase.auth.getUser();
      const authUser = (gu && gu.user) || null;
      const uid = (authUser && authUser.id) || (user && user.id);
      if (!uid) { setLoading(false); return; }
      try {
        const [p, r, m, la] = await Promise.all([
          supabase.from("users").select("*").eq("id", uid).single(),
          supabase.from("recipients").select("*").eq("user_id", uid).order("full_name"),
          supabase
            .from("messages")
            .select("id,title,message_type,category,duration_seconds,created_at,message_recipients(recipient_id)")
            .eq("user_id", uid)
            .order("created_at", { ascending: false }),
          supabase.from("legacy_answers").select("section").eq("user_id", uid),
        ]);
        if (!active) return;
        setProfile(p.data || null);
        setSib((p.data && p.data.sibling_names) || "");
        setPar((p.data && p.data.parent_names) || "");
        setRecipients(r.data || []);
        setMessages(m.data || []);
        const sections = new Set((la.data || []).map((x) => x.section).filter(Boolean));
        setAnsweredSections(sections.size);
      } catch (e) {
        // fail soft — dashboard still renders with what loaded
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const fullName = profile?.full_name || (user?.email ? user.email.split("@")[0] : "Friend");
  const firstName = fullName.split(" ")[0];
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? "Morning" : hour < 18 ? "Afternoon" : "Evening";
  const planLabel =
    { life_starter: "Life Starter", life_family: "Life Family", lifetime: "Lifetime", lifetime_family: "Lifetime Family" }[
      profile?.plan
    ] || "Life Starter";
  const planCap = { life_starter: 10, life_family: 25, lifetime: 50, lifetime_family: 100 };
  const storageCapGB = planCap[profile?.plan] || 10;
  const recipientCap = { life_starter: 5, life_family: 10, lifetime: 50, lifetime_family: Infinity }[profile?.plan] || 5;
  const recipientCapLabel = recipientCap === Infinity ? "Unlimited" : recipientCap;
  const storageUsedGB = (profile?.storage_used_bytes || 0) / 1e9;
  const storagePct = Math.min(100, Math.round((storageUsedGB / storageCapGB) * 100));
  const recipientCount = recipients.length;
  const messageCount = messages.length;
  const TOTAL_SECTIONS = 11;
  const completion = Math.min(
    100,
    Math.round(
      100 *
        ((recipientCount > 0 ? 0.2 : 0) +
          Math.min(messageCount, 8) / 8 * 0.4 +
          (answeredSections / TOTAL_SECTIONS) * 0.4)
    )
  );

  const relEmoji = (rel) => {
    const r = (rel || "").toLowerCase();
    if (r.includes("daughter")) return "👩";
    if (r.includes("son")) return "👦";
    if (r.includes("wife") || r.includes("husband") || r.includes("spouse")) return "❤️";
    if (r.includes("mother") || r.includes("father") || r.includes("parent")) return "👵";
    if (r.includes("friend")) return "🤝";
    return "👤";
  };
  const msgCountsFor = (rid) => {
    const ms = messages.filter((m) => (m.message_recipients || []).some((mr) => mr.recipient_id === rid));
    return {
      total: ms.length,
      voice: ms.filter((m) => m.message_type === "voice").length,
      video: ms.filter((m) => m.message_type === "video").length,
      text: ms.filter((m) => m.message_type === "text").length,
      handwritten: ms.filter((m) => m.message_type === "handwritten").length,
    };
  };
  const acceptedLabel = (r) => {
    if (r.consent_status === "accepted") {
      const d = r.pre_notified_at || r.created_at;
      const when = d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
      return "Verified" + (when ? " · Accepted " + when : "");
    }
    if (r.consent_status === "declined") return "Declined";
    return "Invitation pending";
  };

  const idInput = { width: "100%", boxSizing: "border-box", background: "var(--panel2)", color: "var(--body)", border: "1px solid var(--border)", borderRadius: 8, padding: "11px 13px", fontSize: 15, fontFamily: "inherit" };
  const idSaveBtn = { background: "var(--gold)", color: "#1a1408", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 15, fontWeight: 600, cursor: idBusy ? "default" : "pointer", fontFamily: "inherit" };
  const saveIdentity = async () => {
    if (!supabase) return;
    setIdBusy(true); setIdMsg("");
    try {
      const { data: gu } = await supabase.auth.getUser();
      const uid = (gu && gu.user && gu.user.id) || (user && user.id);
      if (!uid) { setIdBusy(false); setIdMsg("Could not save. Please try again."); return; }
      const { error } = await supabase.from("users").update({ sibling_names: sib.trim() || null, parent_names: par.trim() || null }).eq("id", uid);
      setIdBusy(false);
      setIdMsg(error ? "Could not save. Please try again." : "Saved.");
    } catch (e) { setIdBusy(false); setIdMsg("Could not save. Please try again."); }
  };

  // One opener for the Record a Message box, used by the "Record New Message"
  // tile and by every reminder row, so the "add a Legacy Member first" guard can
  // never apply to one entry point and not the other. `cat` pre-selects the
  // Category; pass nothing for a plain, unaimed message.
  const openRecord = (cat) => {
    if (recipientCount === 0) {
      if (window.confirm("Please add a Legacy Member first — you'll need at least one Legacy Member before you can record a message. Would you like to add one now?")) {
        setShowAddRecip(true);
      }
      return;
    }
    setRecordCategory(cat || null);
    setShowRecord(true);
  };

  const QUESTION = "What's one mistake that made you a better person?";

  // Each reminder ticks off — and strikes through — the moment a message carries
  // its Category. See REMINDER_TASKS above: that one list feeds both this card
  // and the Category picklist in the Record a Message box, so every reminder is
  // completable. `messages` already selects `category`, and RecordMessageModal's
  // onSaved hands the new row back with its category, so a reminder ticks off
  // immediately on save — no reload.
  const doneCategories = new Set(
    messages.map((m) => (m.category || "").trim()).filter(Boolean)
  );
  const REMINDERS = REMINDER_TASKS.map((x) => ({
    t: x.t,
    cat: x.cat,
    done: x.cat ? doneCategories.has(x.cat) : messageCount > 0,
  }));

  const css = `
  .vd-root.vd-light{--gold:#B8912E;--gold-light:#E8C96A;--gold-deep:#8B6914;--bg:#FFFFFF;--panel:#FFFFFF;--panel2:#FBF8F1;--cream2:#F6F1E6;--ink:#1B1712;--body:#3A342B;--muted:#7A7060;--border:#E7DFCE;--border2:#EFE9DB;--shadow:0 2px 4px rgba(30,24,12,.05),0 8px 24px rgba(30,24,12,.07);--confess:#B06A45}
  .vd-root.vd-dark{--gold:#C9A84C;--gold-light:#E8C96A;--gold-deep:#8B6914;--bg:#0A0A0A;--panel:#1A1A1A;--panel2:#141414;--cream2:#222018;--ink:#F5F0E8;--body:#D9D2C4;--muted:#9A9080;--border:#2A2A2A;--border2:#242424;--shadow:0 8px 30px rgba(0,0,0,.35);--confess:#c98b6a}
  .vd-root{--serif:'Playfair Display',Georgia,serif;--read:Georgia,'Times New Roman',serif;background:var(--bg);min-height:100vh;color:var(--body);font-family:'Jost','Century Gothic',sans-serif;font-weight:400;font-size:19px;transition:background .2s,color .2s}
  .vd-root .wrap{max-width:1180px;margin:0 auto;padding:0 24px}
  .vd-root .serif{font-family:var(--serif)}
  .vd-root .gold{color:var(--gold)}
  .vd-root .card{background:var(--panel);border:1px solid var(--border);border-radius:18px;box-shadow:var(--shadow)}
  .vd-theme-toggle{display:inline-flex;align-items:center;gap:8px;background:var(--panel2);color:var(--gold);border:1px solid var(--border);border-radius:999px;padding:9px 18px;font-size:15px;font-weight:600;cursor:pointer;font-family:inherit}
  .vd-theme-toggle:hover{border-color:var(--gold)}
  .vd-topbar{background:#0B0B0B;border-bottom:2px solid #8B6914;margin-bottom:34px}
  .vd-topbar-in{display:flex;align-items:center;justify-content:space-between;height:72px}
  .vd-brand{display:flex;align-items:center;gap:12px}
  .vd-brand-mark{width:38px;height:38px;border-radius:50%;border:2px solid #C9A84C;display:flex;align-items:center;justify-content:center;color:#C9A84C;font-family:var(--serif);font-size:21px}
  .vd-brand-nm{font-family:var(--serif);font-size:22px;color:#F5F0E4}
  .vd-brand-nm b{color:#C9A84C;font-weight:600}
  .vd-topnav{display:flex;align-items:center;gap:28px}
  .vd-topnav a{color:#D8D0BF;font-size:16px;font-weight:500;cursor:pointer;text-decoration:none}
  .vd-topnav a:hover{color:#E8C96A}
  .vd-topme{display:flex;align-items:center;gap:11px}
  .vd-topme-nm{color:#CFC6B4;font-size:16px}
  .vd-topme-av{width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,#3a2f18,#141414);border:2px solid #C9A84C;color:#C9A84C;display:flex;align-items:center;justify-content:center;font-family:var(--serif);font-size:18px}
  @media(max-width:760px){.vd-topnav{display:none}}
  .vd-welcome{text-align:center;padding:36px 0 26px}
  .vd-welcome .eyebrow{text-transform:uppercase;letter-spacing:.3em;font-size:15px;font-weight:600;color:var(--gold);margin-bottom:18px}
  .vd-welcome h1{font-family:var(--serif);font-weight:600;font-size:clamp(38px,5vw,58px);line-height:1.08;margin:0 0 18px;color:var(--ink)}
  .vd-welcome .tag{color:var(--muted);font-size:23px;font-style:italic;font-family:var(--read);line-height:1.55;max-width:720px;margin:0 auto}
  .vd-stats{display:grid;grid-template-columns:1.15fr 2fr;gap:22px;margin-top:8px}
  .vd-hero{padding:30px 32px;display:flex;flex-direction:column;justify-content:space-between}
  .vd-hero .hello{font-family:var(--serif);font-size:29px;font-weight:600;color:var(--ink);margin-bottom:4px}
  .vd-hero .sub{color:var(--muted);font-size:17px}
  .vd-prof{display:flex;align-items:center;gap:18px;margin-top:24px}
  .vd-av{width:74px;height:74px;border-radius:50%;flex:none;background:var(--cream2);border:2.5px solid var(--gold);display:flex;align-items:center;justify-content:center;font-family:var(--serif);font-size:32px;color:var(--gold-deep);overflow:hidden}
  .vd-av img{width:100%;height:100%;object-fit:cover}
  .vd-prof .nm{font-size:22px;font-weight:600;color:var(--ink)}
  .vd-pill{display:inline-block;margin-top:8px;font-size:15px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:var(--gold-deep);background:var(--cream2);border:1px solid var(--gold);border-radius:999px;padding:5px 14px}
  .vd-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px}
  .vd-tile{background:var(--panel2);border:1px solid var(--border2);border-radius:15px;padding:22px 24px}
  .vd-root.vd-light .card,.vd-root.vd-light .vd-tile,.vd-root.vd-light .vd-act,.vd-root.vd-light .vd-recip{border-color:#000;border-width:1.5px}
  .vd-tile .lb{font-size:15px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);font-weight:500;margin-bottom:12px}
  .vd-tile .big{font-family:var(--serif);font-size:42px;font-weight:600;line-height:1;color:var(--ink)}
  .vd-tile .big small{font-size:20px;color:var(--muted);font-family:inherit;font-weight:400}
  .vd-bar{height:10px;border-radius:99px;background:var(--border2);border:1px solid var(--border);margin-top:14px;overflow:hidden}
  .vd-bar>span{display:block;height:100%;background:linear-gradient(90deg,var(--gold-deep),var(--gold),var(--gold-light))}
  .vd-tile .ft{font-size:16px;color:var(--muted);margin-top:11px}
  .vd-qotd{margin-top:26px;padding:36px 40px;background:var(--panel);border:1px solid var(--gold);border-left:7px solid var(--gold);border-radius:18px}
  .vd-qotd .eyebrow{text-transform:uppercase;letter-spacing:.2em;font-size:16px;font-weight:600;color:var(--gold);margin-bottom:16px}
  .vd-qotd .q{font-family:var(--serif);font-size:clamp(26px,2.7vw,34px);font-weight:600;line-height:1.3;color:var(--ink);max-width:820px}
  .vd-qotd .pr{color:var(--muted);font-size:19px;margin-top:16px;font-style:italic;font-family:var(--read);line-height:1.5}
  .vd-ans{display:flex;gap:14px;margin-top:24px;flex-wrap:wrap;align-items:center}
  .vd-a{display:flex;align-items:center;gap:10px;padding:14px 24px;border-radius:12px;border:1.5px solid var(--border);background:var(--panel2);font-size:18px;font-weight:500;color:var(--body);cursor:pointer;transition:.18s}
  .vd-a:hover{border-color:var(--gold);color:var(--gold-deep)}
  .vd-a svg{width:20px;height:20px}
  .vd-a.primary{background:linear-gradient(180deg,var(--gold-light),var(--gold));color:#231a00;border-color:var(--gold);font-weight:600}
  .vd-divider{height:1px;background:var(--border);margin:44px 0}
  .vd-sec{display:flex;align-items:baseline;justify-content:space-between;margin:0 0 20px}
  .vd-sec h2{font-family:var(--serif);font-weight:600;font-size:32px;color:var(--ink)}
  .vd-sec .lk{font-size:16px;font-weight:600;color:var(--gold-deep);letter-spacing:.03em;text-transform:uppercase;cursor:pointer}
  .vd-actions{display:grid;grid-template-columns:repeat(4,1fr);gap:18px}
  .vd-act{background:var(--panel);border:1px solid var(--border);border-radius:18px;padding:26px 24px;display:flex;flex-direction:column;gap:15px;cursor:pointer;box-shadow:var(--shadow);transition:.18s;min-height:150px}
  .vd-act:hover{border-color:var(--gold);transform:translateY(-3px)}
  .vd-act .ico{width:52px;height:52px;border-radius:13px;background:var(--cream2);border:1px solid var(--border);display:flex;align-items:center;justify-content:center;color:var(--gold-deep)}
  .vd-act .ico svg{width:26px;height:26px}
  .vd-act .t{font-size:21px;font-weight:600;color:var(--ink)}
  .vd-act .d{font-size:18px;color:var(--muted);line-height:1.5;font-family:var(--read)}
  .vd-act.feature{grid-column:span 2;flex-direction:row;align-items:center;gap:24px;background:var(--panel);border-color:var(--gold)}
  .vd-act.feature .ico{width:66px;height:66px;background:linear-gradient(180deg,var(--gold-light),var(--gold));color:#231a00;border:none}
  .vd-act.feature .ico svg{width:32px;height:32px}
  .vd-act.feature .t{font-size:26px;font-family:var(--serif)}
  .vd-act.feature .d{font-size:19px}
  .vd-act.confess .ico{color:var(--confess)}
  .vd-cols{display:grid;grid-template-columns:1fr 1fr;gap:24px}
  .vd-builder{padding:34px 36px;display:flex;flex-direction:column}
  .vd-builder .wr{color:var(--gold-deep);font-size:16px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;margin-bottom:16px}
  .vd-builder h3{font-family:var(--serif);font-size:28px;font-weight:600;color:var(--ink);margin-bottom:8px}
  .vd-builder p{color:var(--body);font-size:19px;margin:0 0 24px;font-family:var(--read);line-height:1.65}
  .vd-pl{display:flex;justify-content:space-between;font-size:16px;color:var(--muted);margin-bottom:10px}
  .vd-gold-btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;margin-top:auto;background:linear-gradient(180deg,var(--gold-light),var(--gold));color:#231a00;font-weight:600;padding:16px 28px;border-radius:12px;font-size:18px;cursor:pointer;border:none;width:fit-content}
  .vd-rem{padding:30px 32px}
  .vd-rem h3{font-family:var(--serif);font-size:26px;font-weight:600;color:var(--ink);margin-bottom:6px}
  .vd-rem .note{color:var(--muted);font-size:18px;margin-bottom:20px;font-style:italic;font-family:var(--read);line-height:1.5}
  .vd-r{display:flex;align-items:center;gap:15px;padding:14px 0;border-bottom:1px solid var(--border2)}
  .vd-r:last-child{border-bottom:none}
  .vd-r .bx{width:24px;height:24px;border-radius:6px;border:2px solid var(--border);flex:none;display:flex;align-items:center;justify-content:center}
  .vd-r.done .bx{background:var(--gold);border-color:var(--gold)}
  .vd-r.done .bx svg{width:15px;height:15px;color:#231a00}
  .vd-r .tx{font-size:19px;color:var(--ink)}
  .vd-r.done .tx{color:var(--muted);text-decoration:line-through}
  .vd-recips{display:grid;grid-template-columns:repeat(4,1fr);gap:20px}
  .vd-recip{background:var(--panel);border:1px solid var(--border);border-radius:18px;padding:26px;box-shadow:var(--shadow);cursor:pointer;transition:.18s}
  .vd-recip:hover{border-color:var(--gold);transform:translateY(-3px)}
  .vd-recip .ra{width:68px;height:68px;border-radius:50%;font-size:34px;display:flex;align-items:center;justify-content:center;background:var(--cream2);border:2px solid var(--gold);margin-bottom:16px;overflow:hidden}
  .vd-recip .ra img{width:100%;height:100%;object-fit:cover}
  .vd-recip .rn{font-family:var(--serif);font-size:23px;font-weight:600;color:var(--ink)}
  .vd-recip .rr{color:var(--gold-deep);font-size:15px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;margin:3px 0 16px}
  .vd-recip .rw{display:flex;justify-content:space-between;font-size:16px;color:var(--muted);padding:6px 0}
  .vd-recip .rw b{color:var(--ink);font-weight:600}
  .vd-recip .rs{margin-top:16px;font-size:15px;display:flex;align-items:center;gap:8px;color:#2E8B57;font-weight:500}
  .vd-recip .rs .dot{width:9px;height:9px;border-radius:50%;background:#2E8B57}
  .vd-recip .rs.pending{color:var(--muted)}
  .vd-recip .rs.pending .dot{background:var(--muted)}
  .vd-add{display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;border:2px dashed var(--gold);background:var(--panel2);color:var(--gold-deep);gap:14px;min-height:250px;font-size:20px;font-weight:600}
  .vd-add .pl{width:56px;height:56px;border-radius:50%;border:2px solid var(--gold);display:flex;align-items:center;justify-content:center;color:var(--gold-deep);font-size:32px}
  .vd-foot{text-align:center;color:var(--muted);font-size:19px;padding:44px 0 66px;font-style:italic;font-family:var(--read);line-height:1.5}
  @media(max-width:960px){.vd-stats{grid-template-columns:1fr}.vd-actions{grid-template-columns:repeat(2,1fr)}.vd-act.feature{grid-column:span 2}.vd-cols{grid-template-columns:1fr}.vd-recips{grid-template-columns:repeat(2,1fr)}}
  @media(max-width:560px){.vd-actions{grid-template-columns:1fr}.vd-act.feature{grid-column:span 1}.vd-recips{grid-template-columns:1fr}}
  `;

  const IconMic = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />
    </svg>
  );

  return (
    <div className={"vd-root " + (theme === "dark" ? "vd-dark" : "vd-light")}>
      <style>{css}</style>

      <ReleaseGraceBanner />

      <div className="wrap" style={{ display: "flex", justifyContent: "flex-end", paddingTop: 16 }}>
        <button className="vd-theme-toggle" onClick={toggleTheme} title="Switch between light and dark">{theme === "dark" ? "\u2600 Light mode" : "\u263E Dark mode"}</button>
      </div>


      <div className="vd-topbar">
        <div className="wrap vd-topbar-in">
          <div className="vd-brand">
            <div className="vd-brand-mark">V</div>
            <div className="vd-brand-nm">Voice After <b>Life</b></div>
          </div>
          <nav className="vd-topnav">
            <a onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Dashboard</a>
            <a onClick={() => { const el = document.querySelector(".vd-actions"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}>My Legacy</a>
            <a onClick={() => { const el = document.querySelector(".vd-recips"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}>Legacy Circle</a>
            <a onClick={() => { const el = document.querySelector(".vd-hero"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }}>Account</a>
            <a onClick={() => setScreen("faq")}>Help</a>
          </nav>
          <div className="vd-topme">
            <span className="vd-topme-nm">{firstName}</span>
            <div className="vd-topme-av">{firstName.charAt(0).toUpperCase()}</div>
          </div>
        </div>
      </div>
      <section className="wrap vd-welcome">
        <div className="eyebrow">Your Legacy Continues</div>
        <h1>
          Welcome Back, <span className="gold">{firstName}</span>
        </h1>
        <p className="tag">"Every message you record today is a gift your family may treasure forever."</p>
      </section>

      <section className="wrap">
        <div className="vd-stats">
          <div className="card vd-hero">
            <div>
              <div className="hello">Good {partOfDay}, {firstName}</div>
              <div className="sub">
                {profile?.last_login_at
                  ? "Last login: " + new Date(profile.last_login_at).toLocaleDateString()
                  : "Welcome to your dashboard"}
              </div>
            </div>
            <div className="vd-prof">
              <div className="vd-av">
                {profile?.avatar_url ? <img src={profile.avatar_url} alt="" /> : firstName.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="nm">{fullName}</div>
                <span className="vd-pill">{planLabel} Plan</span>
              </div>
            </div>
          </div>

          <div className="vd-grid">
            <div className="vd-tile">
              <div className="lb">Legacy Completion</div>
              <div className="big">
                {completion}
                <small>%</small>
              </div>
              <div className="vd-bar">
                <span style={{ width: completion + "%" }} />
              </div>
              <div className="ft">You're building something beautiful</div>
            </div>
            <div className="vd-tile">
              <div className="lb">Storage Used</div>
              <div className="big">
                {storageUsedGB.toFixed(1)}
                <small> / {storageCapGB} GB</small>
              </div>
              <div className="vd-bar">
                <span style={{ width: storagePct + "%" }} />
              </div>
              <div className="ft">{(storageCapGB - storageUsedGB).toFixed(1)} GB remaining</div>
            </div>
            <div className="vd-tile">
              <div className="lb">Legacy Circle</div>
              <div className="big">
                {recipientCount}
                <small> of {recipientCapLabel}</small>
              </div>
              <div className="ft">{recipientCap === Infinity ? "Unlimited on your plan" : Math.max(0, recipientCap - recipientCount) + " more available on your plan"}</div>
            </div>
            <div className="vd-tile">
              <div className="lb">Messages Created</div>
              <div className="big">{messageCount}</div>
              <div className="ft">Across voice, video &amp; text</div>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <div className="eyebrow">&#9670; Identity Details</div>
          <div className="q" style={{ fontSize: 18 }}>Help us confirm your account</div>
          <p className="pr" style={{ marginLeft: 0 }}>These optional details help our verification team make sure a reported passing truly matches your account. They stay private and are only used to protect your Legacy.</p>
          <div style={{ marginTop: 14 }}>
            <label style={{ display: "block", fontSize: 14, color: "var(--muted)", marginBottom: 6 }}>Sibling name(s)</label>
            <input value={sib} onChange={(e) => setSib(e.target.value)} placeholder="e.g. Jane Smith, John Smith" style={idInput} />
            <label style={{ display: "block", fontSize: 14, color: "var(--muted)", margin: "14px 0 6px" }}>Parent name(s)</label>
            <input value={par} onChange={(e) => setPar(e.target.value)} placeholder="e.g. Mary Smith, Robert Smith" style={idInput} />
          </div>
          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <button onClick={saveIdentity} disabled={idBusy} style={idSaveBtn}>{idBusy ? "Saving…" : "Save details"}</button>
            {idMsg ? <span style={{ color: "var(--gold)", fontSize: 14 }}>{idMsg}</span> : null}
          </div>
        </div>

        <div className="card vd-qotd">
          <div className="eyebrow">&#10022; Question of the Day</div>
          <div className="q">"{QUESTION}"</div>
          <div className="pr">Answer in just a few minutes — every question answered helps build your Legacy.</div>
          <div className="vd-ans">{(qotdAnswered || qotdSkipped) ? (<div className="pr" style={{ marginLeft: 0 }}>{qotdAnswered ? "Answer saved - thank you. See you tomorrow." : "Skipped for today - see you tomorrow."}</div>) : (<>
            <div className="vd-a primary" onClick={() => setQotdType("video")} style={{ cursor: "pointer" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M23 7l-7 5 7 5V7z" />
                <rect x="1" y="5" width="15" height="14" rx="2" />
              </svg>
              Answer by Video
            </div>
            <div className="vd-a" onClick={() => setQotdType("voice")} style={{ cursor: "pointer" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4" />
              </svg>
              Voice
            </div>
            <div className="vd-a" onClick={() => setQotdType("text")} style={{ cursor: "pointer" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M4 7V5h16v2M9 5v14M7 19h4" />
              </svg>
              Text
            </div>
            <div className="vd-a" onClick={() => setQotdSkipped(true)} style={{ marginLeft: "auto", color: "var(--muted)", cursor: "pointer" }}>
              Skip for today &#8594;
            </div>
          </>)}</div>
        </div>
      </section>

      <div className="wrap">
        <div className="vd-divider" />
      </div>

      <section className="wrap" id="vd-what-to-do" style={{ scrollMarginTop: 96 }}>
        <div className="vd-sec">
          <h2>What would you like to do?</h2>
        </div>
        <div className="vd-actions">
          <div className="vd-act feature" onClick={() => openRecord(null)} style={{ cursor: "pointer" }}>
            <div className="ico">{IconMic}</div>
            <div>
              <div className="t">Record New Message</div>
              <div className="d">Leave a voice, video, or written message for someone you love.</div>
            </div>
          </div>
          <div className="vd-act" onClick={() => { if (recipientCount >= recipientCap) { alert("You've reached your plan's limit of " + recipientCap + " Legacy Members. Upgrade your plan to add more."); return; } setShowAddRecip(true); }} style={{ cursor: "pointer" }}>
            <div className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M19 8v6M22 11h-6" />
              </svg>
            </div>
            <div className="t">Add Legacy Member</div>
            <div className="d">Add a loved one who will receive your messages.</div>
          </div>
          <div className="vd-act" onClick={() => setShowStory(true)} style={{ cursor: "pointer" }}>
            <div className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
              </svg>
            </div>
            <div className="t">Record a Story</div>
            <div className="d">Share a memory or chapter of your life.</div>
          </div>
          <div className="vd-act" onClick={() => setUploadKind("photo")} style={{ cursor: "pointer" }}>
            <div className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
            <div className="t">Upload Photos</div>
            <div className="d">Preserve the pictures that tell your story.</div>
          </div>
          <div className="vd-act" onClick={() => setUploadKind("video")} style={{ cursor: "pointer" }}>
            <div className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M23 7l-7 5 7 5V7z" />
                <rect x="1" y="5" width="15" height="14" rx="2" />
              </svg>
            </div>
            <div className="t">Upload Videos</div>
            <div className="d">Add videos from your camera or phone.</div>
          </div>
          <div className="vd-act" onClick={() => setUploadKind("document")} style={{ cursor: "pointer" }}>
            <div className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
              </svg>
            </div>
            <div className="t">Upload Messages</div>
            <div className="d">Bring in letters or notes you've written.</div>
          </div>
          <div className="vd-act" onClick={() => setScreen("resources")} style={{ cursor: "pointer" }}>
            <div className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6M9 13h6M9 17h6" />
              </svg>
            </div>
            <div className="t">Legacy Resource Center</div>
            <div className="d">Download and print free planning worksheets.</div>
          </div>
          <div className="vd-act confess" onClick={() => setShowConfession(true)} style={{ cursor: "pointer" }}>
            <div className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </div>
            <div className="t">Confessions</div>
            <div className="d">Private matters, released only after verification.</div>
          </div>
        </div>
      </section>

      <div className="wrap">
        <div className="vd-divider" />
      </div>

      <section className="wrap">
        <div className="vd-cols">
          <div className="card vd-builder">
            <div className="wr">Legacy Builder — What Remains?</div>
            <h3>We'll help preserve your life, one memory at a time.</h3>
            <p>
              Every life has a story worth preserving. We'll guide you through a few thoughtful questions to help build
              your digital legacy. Answer by typing, recording your voice, or recording a video. Complete it all today —
              or come back anytime.
            </p>
            <div className="vd-pl">
              <span>Your progress</span>
              <span>
                {completion}% • {answeredSections} of {TOTAL_SECTIONS} sections
              </span>
            </div>
            <div className="vd-bar" style={{ marginBottom: 24 }}>
              <span style={{ width: completion + "%" }} />
            </div>
            <button
              className="vd-gold-btn"
              onClick={() => {
                const el = document.getElementById("vd-what-to-do");
                if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              Continue Building
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="card vd-rem">
            <h3>Your Reminders</h3>
            <div className="note">The pieces your family will look for first. Tap one to record it.</div>
            {REMINDERS.map((rm, i) => (
              <div
                className={"vd-r" + (rm.done ? " done" : "")}
                key={i}
                role={rm.done ? undefined : "button"}
                tabIndex={rm.done ? undefined : 0}
                style={rm.done ? undefined : { cursor: "pointer" }}
                title={rm.done ? undefined : "Record this now"}
                onClick={rm.done ? undefined : () => openRecord(rm.cat)}
                onKeyDown={rm.done ? undefined : (e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openRecord(rm.cat); }
                }}
              >
                <span className="bx">
                  {rm.done ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  ) : null}
                </span>
                <span className="tx">{rm.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="wrap">
        <div className="vd-divider" />
      </div>

      <section className="wrap">
        <div className="vd-sec">
          <h2>My Legacy Members</h2>
          <span className="lk">View All &#8594;</span>
        </div>
        <div className="vd-recips">
          {recipients.map((r) => {
            const c = msgCountsFor(r.id);
            const pending = r.consent_status !== "accepted";
            return (
              <div className="vd-recip" key={r.id} onClick={() => setOpenRecip(r)} style={{ cursor: "pointer" }}>
                <div className="ra">
                  {r.avatar_url ? <img src={r.avatar_url} alt="" /> : relEmoji(r.relationship)}
                </div>
                <div className="rn">{r.full_name}</div>
                <div className="rr">{r.relationship || "Legacy Member"}</div>
                <div className="rw">
                  <span>Messages</span>
                  <b>{c.total}</b>
                </div>
                <div className="rw">
                  <span>{c.handwritten ? "Voice · Video · Text · Letters" : "Voice · Video · Text"}</span>
                  <b>
                    {c.voice} · {c.video} · {c.text}{c.handwritten ? " · " + c.handwritten : ""}
                  </b>
                </div>
                <div className={"rs" + (pending ? " pending" : "")}>
                  <span className="dot" /> {acceptedLabel(r)}
                </div>
              </div>
            );
          })}
          <div className="vd-recip vd-add" onClick={() => { if (recipientCount >= recipientCap) { alert("You've reached your plan's limit of " + recipientCap + " Legacy Members. Upgrade your plan to add more."); return; } setShowAddRecip(true); }} style={{ cursor: "pointer" }}>
            <div className="pl">+</div>
            <div>Add a Legacy Member</div>
          </div>
        </div>
      </section>

      <LibrarySection />

      <TrustedVerifiersSection />

      <div className="vd-foot">
        Everything you create is encrypted and delivered only after your passing. — Voice After Life
      </div>
      {showAddRecip && (
        <AddRecipientModal
          onClose={() => setShowAddRecip(false)}
          onAdded={(r) =>
            setRecipients((prev) =>
              [...prev, r].sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""))
            )
          }
        />
      )}
      {openRecip && (
        <RecipientPageModal recipient={openRecip} onClose={() => setOpenRecip(null)} />
      )}
      {showRecord && (
        <RecordMessageModal
          recipients={recipients}
          initialCategory={recordCategory}
          onClose={() => { setShowRecord(false); setRecordCategory(null); }}
          onSaved={(m) => setMessages((prev) => [...prev, m])}
        />
      )}
      {showConfession && (
        <ConfessionModal recipients={recipients}
          onClose={() => setShowConfession(false)}
        />
      )}
      {qotdType && (
        <QotdAnswerModal recipients={recipients}
          question={QUESTION}
          initialType={qotdType}
          onClose={() => setQotdType(null)}
          onSaved={() => { setQotdType(null); setQotdAnswered(true); }}
        />
      )}
      {showStory && (
        <RecordStoryModal recipients={recipients}
          onClose={() => setShowStory(false)}
        />
      )}
      {uploadKind && (
        <UploadModal recipients={recipients}
          kind={uploadKind}
          onClose={() => setUploadKind(null)}
        />
      )}
    </div>
  );
};

// ===== LEGACY RESOURCE CENTER =====
// 24 printable planning worksheets ("Life & Legacy Planning Series").
//
// The PDFs are NOT public files. They live in a private Supabase Storage bucket
// and are handed out one at a time by /.netlify/functions/resource-download,
// which checks the caller's session and enforces a limit of RESOURCE_DAILY_LIMIT
// distinct worksheets per day. Re-downloading one you already claimed today is
// free. The catalog below (titles + descriptions) stays public so the page is
// still findable and indexable.
const RESOURCE_DAILY_LIMIT = 3;

// Georgia for the worksheet catalog: larger x-height and heavier strokes than
// the brand Cormorant, which matters for the older visitors this page is for.
const RC_FONT = "Georgia, 'Times New Roman', Times, serif";

const RESOURCE_GROUPS = [
  {
    id: "start",
    name: "Start Here",
    blurb:
      "Not sure where to begin? These two show you what you've already got covered and what's still a gap.",
    items: [
      {
        file: "legacy-readiness-assessment.pdf",
        title: "Legacy Readiness Assessment",
        pages: 2,
        desc:
          "A yes / no / unsure snapshot across legal, financial, medical, and family ground. Fill it in and the blanks tell you which of the other worksheets to print next.",
      },
      {
        file: "if-something-happens-to-me.pdf",
        title: "If Something Happens to Me",
        pages: 3,
        desc:
          "One page your family can pick up and act on: who to call, what exists, where it's kept. Deliberately holds no passwords or account numbers — only the map.",
      },
    ],
  },
  {
    id: "legal",
    name: "Wills, Trusts & Legal Decisions",
    blurb:
      "Come to a lawyer's office with these filled in and the meeting gets shorter, cheaper, and far more useful.",
    items: [
      {
        file: "will-preparation-worksheet.pdf",
        title: "Will Preparation Worksheet",
        pages: 2,
        desc:
          "Family, property, beneficiaries, executor, guardians for minor children, and the heirlooms people argue over — gathered before you sit down with an estate-planning professional.",
      },
      {
        file: "trust-planning-questionnaire.pdf",
        title: "Trust Planning Questionnaire",
        pages: 2,
        desc:
          "Helps you work out whether a trust actually fits your situation, and what to ask an attorney about probate, privacy, incapacity, and beneficiaries who need ongoing support.",
      },
      {
        file: "financial-power-of-attorney-worksheet.pdf",
        title: "Financial Power of Attorney Worksheet",
        pages: 2,
        desc:
          "Who would you trust to pay your bills and manage your money if you couldn't? Names an agent and an alternate, and scopes exactly which matters they should cover.",
      },
      {
        file: "healthcare-proxy-planning-worksheet.pdf",
        title: "Healthcare Proxy Planning Worksheet",
        pages: 2,
        desc:
          "Who speaks for you, and what they need to know about your care priorities — worked out calmly in advance instead of in a hospital corridor.",
      },
    ],
  },
  {
    id: "money",
    name: "Money, Insurance & Long-Term Care",
    blurb:
      "The paperwork that decides whether a hard year is survivable or ruinous for the people you leave behind.",
    items: [
      {
        file: "life-insurance-needs-assessment.pdf",
        title: "Life Insurance Needs Assessment",
        pages: 2,
        desc:
          "Works out who depends on your income, what obligations would outlive you, and for how long — so a conversation with an agent starts from real numbers.",
      },
      {
        file: "life-insurance-policy-inventory.pdf",
        title: "Life Insurance Policy Inventory",
        pages: 2,
        desc:
          "Unclaimed life insurance is one of the most common things families lose. This lists what policies exist, with whom, and where the full records are kept.",
      },
      {
        file: "beneficiary-review-worksheet.pdf",
        title: "Beneficiary Review Worksheet",
        pages: 2,
        desc:
          "Beneficiary designations override your will — and they quietly go stale after a marriage, divorce, birth, or death. This is the checklist that catches that.",
      },
      {
        file: "long-term-care-planning-assessment.pdf",
        title: "Long-Term Care Planning Assessment",
        pages: 2,
        desc:
          "Where you'd want to receive care, who would provide it, what coverage you actually have, and how extended care costs would land on a spouse or partner.",
      },
      {
        file: "five-year-long-term-care-discussion-guide.pdf",
        title: "Five-Year Long-Term Care Discussion Guide",
        pages: 2,
        desc:
          "Medicaid generally looks back five years at transfers made for less than fair value. This lays out what to ask an elder-law attorney before you gift, retitle, or move anything.",
      },
      {
        file: "family-financial-accounts-inventory.pdf",
        title: "Family Financial Accounts Inventory",
        pages: 2,
        desc:
          "Banks, retirement plans, investments, debts, and the recurring bills nobody else knows about — institutions and locations only, never credentials.",
      },
    ],
  },
  {
    id: "records",
    name: "Records & Digital Life",
    blurb:
      "Where everything is — on paper and online — so nothing important is lost simply because no one knew it existed.",
    items: [
      {
        file: "important-documents-locator.pdf",
        title: "Important Documents Locator",
        pages: 2,
        desc:
          "A map to the will, deeds, policies, birth certificates, and military records: what exists, whether it's current, and who holds it.",
      },
      {
        file: "digital-legacy-inventory.pdf",
        title: "Digital Legacy Inventory",
        pages: 2,
        desc:
          "Accounts, photos, subscriptions, domains, and crypto — with what should happen to each. Keeps credentials in a password manager where they belong, not on the page.",
      },
    ],
  },
  {
    id: "wishes",
    name: "Final Wishes",
    blurb:
      "Most families decide all of this inside 48 hours, grieving and exhausted. Every line you fill in is one less thing they have to guess.",
    items: [
      {
        file: "funeral-and-memorial-wishes.pdf",
        title: "Funeral & Memorial Wishes",
        pages: 2,
        desc:
          "Burial or cremation, the tone of the service, military honors, music, speakers, readings, and the personal details that make it recognisably yours.",
      },
      {
        file: "obituary-worksheet.pdf",
        title: "Obituary Worksheet",
        pages: 3,
        desc:
          "The dates, names, and service details someone will otherwise be scrambling for — plus the stories that turn an obituary from a résumé into a portrait.",
      },
    ],
  },
  {
    id: "story",
    name: "Tell Your Story",
    blurb:
      "The part no lawyer can draft for you, and the part your family will actually reread.",
    items: [
      {
        file: "time-capsule-planning-worksheet.pdf",
        title: "Time Capsule Planning Worksheet",
        pages: 5,
        desc:
          "Plan the messages you want delivered on birthdays, anniversaries, holidays and milestone dates \u2014 before you sit down to record them. Covers who receives what, when it is delivered, and whether it repeats.",
      },
      {
        file: "life-story-interview-guide.pdf",
        title: "Life Story Interview Guide",
        pages: 5,
        desc:
          "Seven chapters of questions that get past \"fine\" and into the real story. Write the answers, or use it as a script and record yourself talking.",
      },
      {
        file: "legacy-letter-worksheet.pdf",
        title: "Legacy Letter Worksheet",
        pages: 5,
        desc:
          "A will passes on what you owned; a legacy letter passes on what you believed. Warm-up prompts first, then room to write the letter itself.",
      },
      {
        file: "things-i-want-my-children-to-know.pdf",
        title: "Things I Want My Children to Know",
        pages: 2,
        desc:
          "Structured prompts for the things parents mean to say and rarely do — what you admire in them, what life taught you, and what to hear if you're not there for a milestone.",
      },
      {
        file: "message-planning-worksheet.pdf",
        title: "Message Planning Worksheet",
        pages: 2,
        desc:
          "Who you want to speak to, what they should feel afterwards, and which milestone the message should arrive on. Plan it on paper and the recording takes ten minutes.",
      },
    ],
  },
  {
    id: "family",
    name: "Family History & Traditions",
    blurb:
      "Names usually vanish within three generations. These are the ones to do with the oldest person you can still call.",
    items: [
      {
        file: "veteran-legacy-and-service-record-workbook.pdf",
        title: "Veteran Legacy & Service Record Workbook",
        pages: 7,
        desc:
          "A record of service, benefits, discharge papers, honors and the stories your family should be able to find. Written for veterans and first responders, and for the families who will one day need all of it in one place.",
      },
      {
        file: "50-questions-to-ask-your-parents.pdf",
        title: "50 Questions to Ask Your Parents",
        pages: 2,
        desc:
          "An interview guide for a parent, grandparent, or elder — from earliest memories to the questions we rarely ask. Works as a conversation, a recording, or a video call.",
      },
      {
        file: "family-history-interview-workbook.pdf",
        title: "Family History Interview Workbook",
        pages: 3,
        desc:
          "Origins, migrations, service, traditions, and turning points — including a section for the story a relative is most afraid the family will forget.",
      },
      {
        file: "family-tree-worksheet.pdf",
        title: "Family Tree Worksheet",
        pages: 4,
        desc:
          "Four generations of names, dates, and places, with a page for the gaps — the specific questions to go and ask while someone can still answer them.",
      },
      {
        file: "family-recipe-legacy-worksheet.pdf",
        title: "Family Recipe Legacy Worksheet",
        pages: 2,
        desc:
          "One recipe, properly captured — including the part that's never written down: the technique, the timing, and what \"just enough\" actually means.",
      },
    ],
  },
];

const RESOURCE_COUNT = RESOURCE_GROUPS.reduce((n, g) => n + g.items.length, 0);

const DocIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.6" aria-hidden="true">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <path d="M14 2v6h6M9 13h6M9 17h6" />
  </svg>
);

const rcBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  borderRadius: 6,
  padding: "10px 18px",
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textDecoration: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  border: "1px solid transparent",
};

const ResourceCard = ({ item, unlocked, claimed, outOfDownloads, busy, onGet, onLocked }) => {
  const mine = claimed.indexOf(item.file) !== -1;
  // Already claimed today → always available. Otherwise it costs one of the three.
  const blocked = unlocked && outOfDownloads && !mine;
  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
        border: `1px solid ${mine ? GOLD : BORDER}`,
        borderRadius: 12,
        padding: 26,
        display: "flex",
        flexDirection: "column",
        opacity: blocked ? 0.62 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <DocIcon />
        <div>
          <h3 style={{ fontFamily: RC_FONT, fontSize: 24, fontWeight: 700, margin: 0, color: TEXT, lineHeight: 1.32 }}>
            {item.title}
          </h3>
          <p style={{ color: mine ? GOLD : MUTED, fontFamily: RC_FONT, fontSize: 13.5, letterSpacing: "0.08em", textTransform: "uppercase", margin: "7px 0 0" }}>
            {mine ? "✓ Collected today" : "PDF · " + item.pages + " pages · Free to print"}
          </p>
        </div>
      </div>
      <p style={{ color: MUTED, fontFamily: RC_FONT, fontSize: 17.5, lineHeight: 1.72, margin: "8px 0 22px", flex: 1 }}>
        {item.desc}
      </p>
      {!unlocked ? (
        <button
          onClick={onLocked}
          style={{ ...rcBtn, background: "transparent", color: GOLD, borderColor: BORDER, alignSelf: "flex-start" }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          Unlock free
        </button>
      ) : blocked ? (
        <span style={{ ...rcBtn, background: "transparent", color: MUTED, borderColor: BORDER, alignSelf: "flex-start", cursor: "default" }}>
          Back tomorrow
        </span>
      ) : (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={() => onGet(item, false)}
            disabled={busy}
            style={{ ...rcBtn, background: GOLD, color: "#111", borderColor: GOLD, opacity: busy ? 0.6 : 1 }}
          >
            {busy ? "Preparing…" : "Download"}
          </button>
          <button
            onClick={() => onGet(item, true)}
            disabled={busy}
            style={{ ...rcBtn, background: "transparent", color: GOLD, borderColor: GOLD, opacity: busy ? 0.6 : 1 }}
          >
            Open &amp; print
          </button>
        </div>
      )}
    </div>
  );
};

const ResourceCenter = ({ user, setScreen }) => {
  const unlocked = !!user;
  const isGuest = !!(user && user.accountType === "guest");
  const [remaining, setRemaining] = useState(null); // null = not yet known
  const [claimed, setClaimed] = useState([]);
  const [busyFile, setBusyFile] = useState(null);
  const [notice, setNotice] = useState("");
  const goSignUp = () => setScreen("guest");

  const authHeader = async () => {
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    const tok = data && data.session && data.session.access_token;
    return tok ? { Authorization: "Bearer " + tok, "Content-Type": "application/json" } : null;
  };

  // How many are left today?
  useEffect(() => {
    let active = true;
    if (!unlocked) { setRemaining(null); setClaimed([]); return; }
    (async () => {
      try {
        const headers = await authHeader();
        if (!headers) return;
        const res = await fetch("/.netlify/functions/resource-download", {
          method: "POST",
          headers: headers,
          body: JSON.stringify({ action: "status", tzOffset: new Date().getTimezoneOffset() }),
        });
        const out = await res.json();
        if (!active || !res.ok) return;
        setRemaining(typeof out.remaining === "number" ? out.remaining : null);
        setClaimed(out.claimed || []);
      } catch (e) {
        /* counter just stays hidden */
      }
    })();
    return () => { active = false; };
  }, [unlocked, user]);

  const getFile = async (item, openIt) => {
    if (busyFile) return;
    setNotice("");
    setBusyFile(item.file);
    try {
      const headers = await authHeader();
      if (!headers) { setBusyFile(null); goSignUp(); return; }
      const res = await fetch("/.netlify/functions/resource-download", {
        method: "POST",
        headers: headers,
        body: JSON.stringify({ file: item.file, tzOffset: new Date().getTimezoneOffset() }),
      });
      const out = await res.json();
      setBusyFile(null);
      if (res.status === 429) {
        setRemaining(0);
        setClaimed(out.claimed || claimed);
        setNotice(out.message || "You've collected today's worksheets. Come back tomorrow.");
        return;
      }
      if (!res.ok) {
        setNotice(out.error || "That worksheet could not be prepared. Please try again.");
        return;
      }
      if (typeof out.remaining === "number") setRemaining(out.remaining);
      if (out.claimed) setClaimed(out.claimed);
      if (openIt) {
        window.open(out.url, "_blank", "noopener");
      } else {
        const a = document.createElement("a");
        a.href = out.url;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (e) {
      setBusyFile(null);
      setNotice("We couldn't reach the worksheet library just now. Please try again.");
    }
  };

  const outOfDownloads = remaining === 0;

  return (
    <div>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "60px 40px 0" }}>
        <PageHead
          kicker="Legacy Resource Center"
          kickerSize={22}
          subtitleSize={19}
          title="Free Worksheets to"
          emphasis="Print & Fill In"
          subtitle={
            RESOURCE_COUNT +
            " planning worksheets you can download, print, and write on by hand — from wills and long-term care to the family stories nobody wrote down. Free whether or not you ever become a member."
          }
        />

        {!unlocked && (
          <div
            style={{
              background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
              border: `1px solid ${GOLD}`,
              borderRadius: 14,
              padding: "30px 34px",
              marginBottom: 44,
              textAlign: "center",
            }}
          >
            <h2 className="serif" style={{ fontSize: 27, fontWeight: 400, margin: 0, color: TEXT }}>
              All {RESOURCE_COUNT} worksheets are free
            </h2>
            <p style={{ color: MUTED, fontFamily: RC_FONT, fontSize: 18, lineHeight: 1.7, maxWidth: 680, margin: "14px auto 24px" }}>
              Create a free Guest Account — an email address and a password, nothing more. No
              payment, no identity check, no credit card. You can collect {RESOURCE_DAILY_LIMIT}{" "}
              worksheets a day, so there's always a reason to come back.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <GoldBtn onClick={goSignUp}>Create a free account</GoldBtn>
              <GoldBtn outline onClick={() => setScreen("login")}>
                I already have one
              </GoldBtn>
            </div>
          </div>
        )}

        {unlocked && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
              border: `1px solid ${outOfDownloads ? GOLD : BORDER}`,
              borderRadius: 12,
              padding: "20px 26px",
              marginBottom: 40,
            }}
          >
            <div>
              <p style={{ color: TEXT, fontFamily: RC_FONT, fontSize: 20, margin: 0, fontWeight: 700 }}>
                {remaining === null
                  ? "Loading your downloads…"
                  : outOfDownloads
                  ? "That's your " + RESOURCE_DAILY_LIMIT + " for today."
                  : remaining + " of " + RESOURCE_DAILY_LIMIT + " downloads left today"}
              </p>
              <p style={{ color: MUTED, fontFamily: RC_FONT, fontSize: 17.5, margin: "7px 0 0", lineHeight: 1.65 }}>
                {outOfDownloads
                  ? "Come back tomorrow for " + RESOURCE_DAILY_LIMIT + " more. Anything you collected today stays available."
                  : "You can collect " + RESOURCE_DAILY_LIMIT + " worksheets a day. Re-downloading one you already took today is free."}
              </p>
            </div>
          </div>
        )}

        {notice && (
          <div
            style={{
              background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
              border: `1px solid ${GOLD}`,
              borderRadius: 10,
              padding: "16px 20px",
              marginBottom: 30,
              color: TEXT,
              fontFamily: RC_FONT,
              fontSize: 17.5,
              lineHeight: 1.65,
            }}
          >
            {notice}
          </div>
        )}

        {RESOURCE_GROUPS.map((group) => (
          <section key={group.id} style={{ marginBottom: 54 }}>
            <h2 className="serif" style={{ fontSize: 30, fontWeight: 400, color: TEXT, margin: "0 0 6px" }}>
              {group.name}
            </h2>
            <p style={{ color: MUTED, fontFamily: RC_FONT, fontSize: 18.5, lineHeight: 1.68, margin: "0 0 26px", maxWidth: 700 }}>
              {group.blurb}
            </p>
            <div className="rc-grid">
              {group.items.map((item) => (
                <ResourceCard
                  key={item.file}
                  item={item}
                  unlocked={unlocked}
                  claimed={claimed}
                  outOfDownloads={outOfDownloads}
                  busy={busyFile === item.file}
                  onGet={getFile}
                  onLocked={goSignUp}
                />
              ))}
            </div>
          </section>
        ))}

        <div
          style={{
            background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
            border: `1px solid ${BORDER}`,
            borderRadius: 14,
            padding: "34px 34px 38px",
            textAlign: "center",
            marginBottom: 70,
          }}
        >
          <h2 className="serif" style={{ fontSize: 28, fontWeight: 400, color: TEXT, margin: 0 }}>
            {isGuest ? "Ready for the part paper can't do?" : "Paper can hold the plan. It can't hold your voice."}
          </h2>
          <p style={{ color: MUTED, fontFamily: RC_FONT, fontSize: 17.5, lineHeight: 1.75, maxWidth: 680, margin: "14px auto 24px" }}>
            These worksheets capture the decisions and the facts. What families say they
            miss most is the sound of someone — the laugh, the way they told a story, the
            thing they always said. Voice After Life records your voice and video messages
            and delivers them to the people you name, at the moment you choose.
          </p>
          <GoldBtn onClick={() => setScreen("signup")}>
            {isGuest ? "Upgrade my account" : "See membership plans"}
          </GoldBtn>
        </div>

        <p style={{ color: MUTED, fontFamily: RC_FONT, fontSize: 14.5, lineHeight: 1.75, textAlign: "center", marginBottom: 60, maxWidth: 820, marginLeft: "auto", marginRight: "auto" }}>
          These worksheets are provided for general planning and conversation purposes only.
          They are not legal, tax, financial, insurance, medical, Medicaid, or other
          professional advice, and they are not a substitute for documents prepared or
          reviewed by qualified professionals. Laws and individual circumstances vary.
        </p>
      </div>
      <SiteFooter setScreen={setScreen} />
    </div>
  );
};

// ===== WHICH DOOR THEY CAME IN BY =====
// The ManyChat comment-keyword funnel sends people to /free-account?k=will.
// That keyword is the only thing that says which post, which worksheet and which
// platform produced a lead, so it is captured here, written onto the auth user's
// metadata as signup_source, and carried by the HubSpot sync worker into the
// "Signup source" contact property.
//
// It is stashed in sessionStorage on first sight because people rarely sign up
// on the first screen — they follow the link, read the catalog, and only then
// create the account, by which point the query string is long gone.
//
// Keep this list identical to KEYWORDS in netlify/functions/lead-magnet.js.
const MANYCHAT_KEYWORDS = [
  "willkit",
  "trustkit",
  "proxykit",
  "insurancekit",
  "documentskit",
  "digitalkit",
  "funeralkit",
  "letterkit",
  "parentskit",
  "veterankit",
];
// Channel tags that are not one of the ten keywords, each mapped to the source
// label it records.
//
// Instagram runs one automation per keyword, so its DM link carries that
// keyword and we learn exactly which worksheet pulled the person in. Facebook
// runs a single flow answering all ten keywords with the same reply, so its
// button can only say "this came from Facebook" — hence ?k=facebook.
//
// TikTok is NOT a ManyChat route and must not be labelled as one. Its comment
// trigger is region-blocked in the US, and a DM from a non-follower arrives as
// a message request the automation never sees (tested 2026-08-30). The whole
// TikTok funnel is the plain bio link, so it records "tiktok_bio" — calling it
// "manychat_tiktok" would put a lie in the HubSpot report.
//
// Anything not in either list is ignored and the visitor is recorded as an
// ordinary Legacy Resource Center signup, so a stray or guessed ?k= cannot
// write junk into the attribution.
const CHANNEL_SOURCES = { facebook: "manychat_facebook", tiktok: "tiktok_bio" };
const SIGNUP_SOURCE_KEY = "val_signup_source";

function signupSource() {
  try {
    const raw = new URLSearchParams(window.location.search).get("k") || "";
    const k = raw.toLowerCase().replace(/[^a-z]/g, "");
    if (MANYCHAT_KEYWORDS.indexOf(k) !== -1) {
      window.sessionStorage.setItem(SIGNUP_SOURCE_KEY, "manychat_" + k);
    } else if (Object.prototype.hasOwnProperty.call(CHANNEL_SOURCES, k)) {
      // hasOwnProperty, not `CHANNEL_SOURCES[k]` — `k` comes from the query
      // string, and a bare lookup would match inherited names like
      // "constructor" and write junk into the attribution.
      window.sessionStorage.setItem(SIGNUP_SOURCE_KEY, CHANNEL_SOURCES[k]);
    }
    return window.sessionStorage.getItem(SIGNUP_SOURCE_KEY) || "legacy_resource_center";
  } catch (e) {
    // Private browsing can throw on sessionStorage. Attribution is never worth
    // breaking a signup over.
    return "legacy_resource_center";
  }
}

// ===== FREE GUEST ACCOUNT =====
// A lightweight account whose only privilege is the Legacy Resource Center.
// No Stripe identity check, no plan, no payment. The account type and the
// marketing opt-in are stored on the Supabase auth user's metadata
// (account_type: "guest"), so this needs no schema change and no new RLS rules.
// Upgrading later is just the normal paid signup with the same email.
const GuestSignup = ({ setScreen, setUser }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [agreeTerms, setAgreeTerms] = useState(false);
  // Marketing consent starts UNCHECKED. VAL operates internationally, and a
  // pre-ticked box is not valid consent under GDPR/UK GDPR (Planet49, C-673/17).
  // The opt-in and its timestamp are recorded on the auth user's metadata so
  // there is a record of when consent was actually given.
  const [optIn, setOptIn] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [busy, setBusy] = useState(false);

  const finish = () => {
    setScreen("resources");
  };

  const createGuest = async () => {
    if (busy) return;
    if (!form.email || !form.password) {
      alert("Please enter your email and choose a password.");
      return;
    }
    if (form.password.length < 6) {
      alert("Please choose a password of at least 6 characters.");
      return;
    }
    if (!agreeTerms) {
      alert("Please accept the Terms and Privacy Policy to continue.");
      return;
    }
    if (!supabase) {
      alert("Accounts are not configured yet.");
      return;
    }
    setBusy(true);
    const { data, error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          account_type: "guest",
          full_name: form.name || null,
          marketing_opt_in: !!optIn,
          marketing_opt_in_at: optIn ? new Date().toISOString() : null,
          signup_source: signupSource(),
        },
      },
    });
    setBusy(false);
    if (error) {
      const msg = (error.message || "").toLowerCase();
      if (msg.indexOf("already") !== -1 || msg.indexOf("registered") !== -1) {
        if (
          window.confirm(
            "You already have a Voice After Life account with that email.\n\nClick OK to sign in instead."
          )
        ) {
          setScreen("login");
        }
        return;
      }
      alert(error.message);
      return;
    }
    if (data && data.session) {
      setUser({
        name: form.name || (form.email || "").split("@")[0],
        email: form.email,
        id: data.user && data.user.id,
        plan: "",
        accountType: "guest",
      });
      finish();
      return;
    }
    setOtpSent(true);
  };

  const verifyCode = async () => {
    if (busy) return;
    const code = (otpCode || "").trim();
    if (code.length < 6) {
      alert("Please enter the 6-digit code from your email.");
      return;
    }
    if (!supabase) return;
    setBusy(true);
    let res = await supabase.auth.verifyOtp({ email: form.email, token: code, type: "signup" });
    if (res.error) {
      const alt = await supabase.auth.verifyOtp({ email: form.email, token: code, type: "email" });
      if (!alt.error) res = alt;
    }
    setBusy(false);
    if (res.error) {
      alert(res.error.message || "That code was not correct. Please try again.");
      return;
    }
    const d = res.data;
    setUser({
      name: form.name || (form.email || "").split("@")[0],
      email: form.email,
      id: d && d.user && d.user.id,
      plan: "",
      accountType: "guest",
    });
    finish();
  };

  const resendCode = async () => {
    if (busy || !form.email || !supabase) return;
    setBusy(true);
    const { error } = await supabase.auth.resend({ type: "signup", email: form.email });
    setBusy(false);
    if (error) { alert(error.message); return; }
    alert("A new code is on its way to " + form.email + ".");
  };

  const boxStyle = {
    border: `1px solid ${BORDER}`,
    background: SURFACE2,
    borderRadius: 8,
    padding: "13px 15px",
    fontSize: 12.5,
    color: MUTED,
    lineHeight: 1.6,
    textAlign: "left",
  };

  return (
    <div>
      <div style={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
        <div
          className="fade-up"
          style={{
            background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`,
            border: `1px solid ${BORDER}`,
            borderRadius: 16,
            padding: 44,
            width: "100%",
            maxWidth: 470,
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 30 }}>
            <Logo size={28} />
            <h2 className="serif" style={{ fontSize: 29, marginTop: 22, fontWeight: 400, color: TEXT }}>
              Free Guest Account
            </h2>
            <p style={{ color: MUTED, fontSize: 14.5, marginTop: 10, lineHeight: 1.65 }}>
              Unlocks all {RESOURCE_COUNT} Legacy Resource Center worksheets to download and
              print. No payment, no identity check, no credit card.
            </p>
          </div>

          {otpSent ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={boxStyle}>
                We emailed a 6-digit code to <strong style={{ color: TEXT }}>{form.email}</strong>.
                Enter it below to confirm your address and open the Resource Center.
              </div>
              <input
                placeholder="Enter 6-digit code"
                inputMode="numeric"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))}
                style={{ width: "100%", boxSizing: "border-box", textAlign: "center", letterSpacing: "6px", fontSize: 20 }}
              />
              <GoldBtn onClick={verifyCode} disabled={busy} style={{ width: "100%", padding: "13px 0" }}>
                {busy ? "Verifying..." : "Confirm & open the worksheets"}
              </GoldBtn>
              <button
                type="button"
                onClick={resendCode}
                disabled={busy}
                style={{ background: "none", border: "none", color: MUTED, fontSize: 12.5, textDecoration: "underline", cursor: "pointer" }}
              >
                Resend code
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <input
                placeholder="First Name (optional)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                placeholder="Email Address"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <PasswordInput
                placeholder="Choose a Password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />

              <label style={{ ...boxStyle, display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={optIn}
                  onChange={(e) => setOptIn(e.target.checked)}
                  style={{ width: "auto", marginTop: 3, accentColor: GOLD }}
                />
                <span>
                  <strong style={{ color: TEXT }}>Send me helpful legacy-planning email.</strong>{" "}
                  Occasional guides, new worksheets, and Voice After Life news. You can
                  unsubscribe from any email, any time.
                </span>
              </label>

              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12.5, color: MUTED, lineHeight: 1.55, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  style={{ width: "auto", marginTop: 3, accentColor: GOLD }}
                />
                <span>
                  I have read and agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setScreen("legal")}
                    style={{ background: "none", border: "none", color: GOLD, fontSize: 12.5, padding: 0, textDecoration: "underline", cursor: "pointer" }}
                  >
                    Terms and Privacy Policy
                  </button>
                  .
                </span>
              </label>

              <GoldBtn onClick={createGuest} disabled={busy || !agreeTerms} style={{ width: "100%", padding: "14px 0" }}>
                {busy ? "Creating..." : "Create my free account"}
              </GoldBtn>

              <p style={{ textAlign: "center", color: MUTED, fontSize: 12.5, lineHeight: 1.6, margin: "2px 0 0" }}>
                A Guest Account opens the Resource Center only. It does not record or store
                messages — that's what a membership is for.
              </p>
            </div>
          )}

          <p style={{ textAlign: "center", marginTop: 22, color: MUTED, fontSize: 13.5 }}>
            Already have an account?{" "}
            <button
              onClick={() => setScreen("login")}
              style={{ background: "none", border: "none", color: GOLD, fontSize: 13.5, cursor: "pointer" }}
            >
              Sign in
            </button>
          </p>
          <p style={{ textAlign: "center", marginTop: 8, color: MUTED, fontSize: 13.5 }}>
            Want the full vault?{" "}
            <button
              onClick={() => setScreen("signup")}
              style={{ background: "none", border: "none", color: GOLD, fontSize: 13.5, cursor: "pointer" }}
            >
              See membership plans
            </button>
          </p>
        </div>
      </div>
      <SiteFooter setScreen={setScreen} />
    </div>
  );
};

const PageHead = ({ kicker, title, emphasis, subtitle, kickerSize = 11, subtitleSize = 16 }) => (
  <div style={{ textAlign: "center", marginBottom: 56 }}>
    <p
      style={{
        color: GOLD,
        fontSize: kickerSize,
        letterSpacing: "0.3em",
        textTransform: "uppercase",
        marginBottom: 16,
      }}
    >
      {kicker}
    </p>
    <h1 className="serif" style={{ fontSize: 52, fontWeight: 400, marginBottom: 16 }}>
      {title} {emphasis && <em style={{ color: GOLD }}>{emphasis}</em>}
    </h1>
    {subtitle && (
      <p
        style={{
          color: MUTED,
          fontSize: subtitleSize,
          maxWidth: 600,
          margin: "0 auto",
          lineHeight: 1.7,
        }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

// LEGAL HUB + individual policy viewer
const LegalPage = ({ setScreen, openId, onOpen }) => {
  const current = POLICIES.find((p) => p.id === openId);

  if (current) {
    return (
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "60px 40px" }}>
        <button
          onClick={() => onOpen(null)}
          style={{
            background: "none",
            border: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: 13,
            padding: "8px 16px",
            borderRadius: 6,
            marginBottom: 32,
          }}
        >
          ← All Policies
        </button>
        <h1 className="serif" style={{ fontSize: 40, fontWeight: 400, marginBottom: 8 }}>
          {current.title}
        </h1>
        <div className="gold-line" style={{ margin: "24px 0 32px" }} />
        {current.sections.map((s, i) => (
          <div key={i} style={{ marginBottom: 28 }}>
            {s.h && (
              <h3
                style={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: TEXT,
                  marginBottom: 10,
                }}
              >
                {s.h}
              </h3>
            )}
            <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.8 }}>{s.p}</p>
          </div>
        ))}
        <p style={{ color: "#6a6458", fontSize: 12, marginTop: 40, fontStyle: "italic" }}>
          This summary is provided for transparency and Twilio/carrier review.
          The full Corporate Manual governs in the event of any conflict.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "60px 40px" }}>
      <PageHead
        kicker="Transparency"
        title="Terms, Conditions"
        emphasis="& Policies"
        subtitle="Everything that governs how Voice After Life protects you, your Content, and your designated Legacy Members. Select any policy to read it."
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 16,
        }}
      >
        {POLICIES.map((p) => (
          <button
            key={p.id}
            onClick={() => onOpen(p.id)}
            style={{
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 10,
              padding: 24,
              textAlign: "left",
              color: TEXT,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = GOLD_DARK)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = BORDER)}
          >
            <p className="serif" style={{ fontSize: 19, marginBottom: 6 }}>
              {p.title}
            </p>
            <p style={{ color: GOLD, fontSize: 13 }}>Read →</p>
          </button>
        ))}
      </div>
      <SiteFooter setScreen={setScreen} />
    </div>
  );
};

// COMPANY PAGE — history & founding story (edit the copy freely)
const CompanyPage = ({ setScreen }) => (
  <div>
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "60px 40px" }}>
      <PageHead
        kicker="Our Story"
        title="The Company Behind"
        emphasis="Voice After Life"
        subtitle="Preserving voices, stories, and love for the generations who come after."
        kickerSize={20}
        subtitleSize={20}
      />
      <div style={{ color: MUTED, fontSize: 20, lineHeight: 2 }}>
        <p style={{ marginBottom: 30 }}>
          Voice After Life was founded on a simple belief: that the people we
          love should never have to wonder what we would have said. Our mission
          is to preserve the voice, presence, and wisdom of every person who
          wishes to leave something meaningful behind&mdash;delivered with
          dignity, security, and care, at the moment it is meant to be received.
        </p>
        <p style={{ marginBottom: 30 }}>
          Life is filled with moments we intend to revisit&mdash;words of
          encouragement, expressions of love, heartfelt apologies, family
          traditions, personal stories, and lessons learned through a lifetime
          of experience. Too often, those conversations are delayed until
          tomorrow, never realizing that tomorrow is never guaranteed. Voice
          After Life was created to help ensure those moments are preserved
          before they are lost.
        </p>
        <p style={{ marginBottom: 30 }}>
          The inspiration for Voice After Life came from founder Dr. Joe
          Robinson, Ph.D., ABD whose own experiences with the loss of his
          daughter and parents revealed how quickly a person&rsquo;s voice,
          laughter, wisdom, and unique personality can fade from memory. Like
          many families, he realized there were questions left unasked, stories
          left untold, and words that could never be spoken again. From that
          realization came a vision: to create a secure platform where people
          could preserve not only their messages, but also their voice,
          personality, values, and legacy for the people they love.
        </p>
        <p style={{ marginBottom: 30 }}>
          What began as a single idea has grown into a broader mission&mdash;to
          help families preserve memories, document their history, safeguard
          important life moments, and ensure future generations can hear the
          voices, stories, and wisdom of those who came before them. Voice After
          Life is more than a technology platform; it is a commitment to
          protecting life&rsquo;s most meaningful connections.
        </p>
        <p style={{ marginBottom: 30 }}>
          We believe every person has a story worth telling, every family has a
          legacy worth preserving, and every voice deserves the opportunity to
          be heard long after it has fallen silent.
        </p>
        <h3 className="serif" style={{ fontSize: 34, color: TEXT, margin: "48px 0 20px" }}>
          Our <em style={{ color: GOLD }}>Mission</em>
        </h3>
        <p style={{ marginBottom: 30 }}>
          To provide a secure and trusted home for humanity&rsquo;s most
          personal messages, preserving love, guidance, memories, and
          life&rsquo;s wisdom so they may inspire and comfort future generations
          beyond a single lifetime.
        </p>
        <p
          className="serif"
          style={{ fontSize: 25, color: TEXT, fontStyle: "italic", marginTop: 72, lineHeight: 1.7 }}
        >
          &ldquo;Every life has a story, every voice has meaning, every legacy
          deserves to live on.&rdquo;{" "}
          <span style={{ color: GOLD, fontStyle: "normal" }}>&ndash; Voice After Life</span>
        </p>
      </div>
    </div>
    <SiteFooter setScreen={setScreen} />
  </div>
);

// MEDIA PAGE — future articles & publications
const MediaPage = ({ setScreen }) => (
  <div>
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "60px 40px" }}>
      <PageHead
        kicker="Press & Media"
        title="News, Articles"
        emphasis="& Publications"
        subtitle="Coverage, announcements, and stories about Voice After Life."
      />
      <ArticleGallery />
      {/* Template for a future article card — duplicate as needed:
      <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 28, marginTop: 20 }}>
        <p style={{ color: GOLD, fontSize: 12 }}>OUTLET · DATE</p>
        <h3 className="serif" style={{ fontSize: 22, margin: "8px 0" }}>Article Title</h3>
        <p style={{ color: MUTED }}>Short description…</p>
      </div>
      */}
    </div>
    <SiteFooter setScreen={setScreen} />
  </div>
);

// Standalone How It Works page (reuses landing content focus)
const HowItWorksPage = ({ setScreen }) => (
  <div>
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "60px 40px 0" }}>
      <PageHead
        kicker="How It Works"
        title="Two Ways to Leave"
        emphasis="Your Legacy"
        subtitle="Share your story openly, or seal your most private words for after — both protected in one secure vault."
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 24,
          marginBottom: 24,
        }}
      >
        <div style={{ background: `linear-gradient(135deg, ${SURFACE}, ${SURFACE2})`, border: `1px solid ${GOLD_DARK}`, borderRadius: 14, padding: 36 }}>
          <div style={{ fontSize: 34, marginBottom: 14 }}>🎥</div>
          <h3 className="serif" style={{ fontSize: 26, marginBottom: 12 }}>
            Record Your <em style={{ color: GOLD }}>Video</em>
          </h3>
          <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.8 }}>
            Preserve your voice, face, and spirit. Record biographies, tell your
            own story, share family history, pass on wisdom, and capture
            milestones — birthdays, weddings, graduations, and everyday
            reflections — the heart of your living legacy for generations.
          </p>
        </div>
        <div style={{ background: `linear-gradient(135deg, ${SURFACE}, #0d0d14)`, border: `1px solid ${BORDER}`, borderRadius: 14, padding: 36 }}>
          <div style={{ fontSize: 34, marginBottom: 14 }}>🔒</div>
          <h3 className="serif" style={{ fontSize: 26, marginBottom: 12 }}>
            Record Your <em style={{ color: GOLD }}>Confessions</em>
          </h3>
          <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.8 }}>
            Confessions are completely private — no immediate notification and no
            prior notification to anyone. They remain sealed and encrypted, and
            are delivered only upon the verified passing of the User.
          </p>
        </div>
      </div>
      <div style={{ textAlign: "center", padding: "24px 0 8px" }}>
        <GoldBtn onClick={() => setScreen("signup")} style={{ padding: "14px 36px" }}>
          Preserve Your Legacy
        </GoldBtn>
      </div>
    </div>
    <SiteFooter setScreen={setScreen} />
  </div>
);

// ============================================================
// URL ROUTING — every screen and policy gets a real, shareable
// web address (e.g. /legal/privacy) that loads directly. Uses the
// History API; the Netlify public/_redirects file serves index.html
// for all paths so deep links never 404.
// ============================================================
const SCREEN_PATHS = {
  landing: "/",
  faq: "/faq",
  company: "/company",
  media: "/media",
  legal: "/legal",
  login: "/signin",
  signup: "/get-started",
  dashboard: "/dashboard",
  reset: "/reset-password",
  staff: "/staff",
  reportDeath: "/report-death",
  recipient: "/keepsake",
  verify: "/verify",
  deleteAccount: "/delete-account",
  resources: "/legacy-resource-center",
  guest: "/free-account",
};
const PATH_TO_SCREEN = Object.fromEntries(
  Object.entries(SCREEN_PATHS).map(([s, p]) => [p, s])
);

// ===== ACCOUNT SEPARATION =====
// A @voiceafterlife.com address is a STAFF address and nothing else: it may
// reach the staff console, the Command Center and the AI Marketing Center,
// and it may never open a customer dashboard. Any other address is a CUSTOMER
// address and may never reach staff tooling.
//
// This is not tidiness. A staff member who also holds a customer account is
// how a reviewer ends up able to see -- and approve -- a case touching their
// own legacy. The database enforces both halves as CHECK constraints on
// public.staff and public.users; what follows is the half the person actually
// sees, so they get an explanation instead of a database error.
const STAFF_EMAIL_DOMAIN = "@voiceafterlife.com";
const isStaffEmail = (e) =>
  String(e || "").trim().toLowerCase().endsWith(STAFF_EMAIL_DOMAIN);

const parseLocation = () => {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const legalMatch = path.match(/^\/legal\/([a-z0-9-]+)$/i);
  if (legalMatch) {
    const found = POLICIES.find((p) => p.id === legalMatch[1].toLowerCase());
    if (found) return { screen: "legal", policyId: found.id };
  }
  // Trusted Verifier confirmation link. The token in the path IS the
  // credential -- these people have no account and must never be asked to
  // make one in order to answer a question we asked them.
  const verifyMatch = path.match(/^\/verify\/([A-Za-z0-9_-]+)$/);
  if (verifyMatch) return { screen: "verify", policyId: null };
  const keepMatch = path.match(/^\/keepsake\/([a-zA-Z0-9]+)$/);
  if (keepMatch) return { screen: "recipient", policyId: null };
  // Returning from Stripe Checkout (success_url) — drop the user into their dashboard.
  if (path === "/welcome" || /session_id=/.test(window.location.search || "")) {
    return { screen: "dashboard", policyId: null };
  }
  return { screen: PATH_TO_SCREEN[path] || "landing", policyId: null };
};

const pathFor = (screen, policyId) => {
  if (screen === "legal" && policyId) return "/legal/" + policyId;
  return SCREEN_PATHS[screen] || "/";
};

// ===== HUMAN DASHBOARD (VAL staff console) =====
const SBG = "var(--sf-bg)";
const SCARD = "var(--sf-card)";
const STXT = "var(--sf-text)";
const SMUT = "var(--sf-muted)";
const SBOR = "var(--sf-border)";
const SGOLD = "var(--sf-gold)";
const SGOLD_DARK = "var(--sf-gold-dark)";
const SF_THEME_CSS = ".sf-root{zoom:1.15;--sf-bg:#0A0A0A;--sf-card:#141414;--sf-text:#F5F0E8;--sf-muted:#9A9080;--sf-border:#2A2A2A;--sf-gold:#C9A84C;--sf-gold-dark:#5a4a1e;--sf-ok:#9fe0a0;--sf-ok-bg:#12180f;--sf-info:#88b0d1;--sf-err:#ee8888;--sf-hi-bg:#1d1809;--sf-hi-bg2:#1a1610}.sf-root.sf-light{--sf-bg:#f5f1e6;--sf-card:#ffffff;--sf-text:#26221b;--sf-muted:#6b6455;--sf-border:#e0d7c4;--sf-gold:#9a7d24;--sf-gold-dark:#d9c89a;--sf-ok:#1f7a34;--sf-ok-bg:#e7f3ea;--sf-info:#2c5f86;--sf-err:#b3261e;--sf-hi-bg:#fbf3d6;--sf-hi-bg2:#faf6e8}";

const SF_INPUT = { width: "100%", boxSizing: "border-box", background: SCARD, border: "1px solid " + SBOR, color: STXT, borderRadius: 8, padding: "10px 12px", fontSize: 17, marginBottom: 10 };
const SF_PRIMARY = { background: SGOLD, color: "#111", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 17, fontWeight: 600, cursor: "pointer" };
const SF_GHOST = { background: "transparent", border: "1px solid " + SBOR, color: STXT, borderRadius: 8, padding: "7px 14px", fontSize: 16, cursor: "pointer" };
const SF_DANGER = { background: "transparent", border: "1px solid #5a3030", color: "var(--sf-err)", borderRadius: 8, padding: "7px 14px", fontSize: 16, cursor: "pointer" };

const StaffShell = ({ children, right }) => {
  const [theme, setTheme] = useState(() => { try { return localStorage.getItem("val_staff_theme") === "light" ? "light" : "dark"; } catch (e) { return "dark"; } });
  const toggleTheme = () => { setTheme((t) => { const nt = t === "light" ? "dark" : "light"; try { localStorage.setItem("val_staff_theme", nt); } catch (e) {} return nt; }); };
  return (
    <div className={"sf-root" + (theme === "light" ? " sf-light" : "")} style={{ minHeight: "100vh", background: SBG, color: STXT }}>
      <style>{styles}</style>
      <style>{SF_THEME_CSS}</style>
      <div style={{ borderBottom: "1px solid " + SBOR, padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontFamily: "Georgia, serif", fontSize: 26, display: "flex", alignItems: "center", gap: 12 }}>
          <a href="/" onClick={(e) => { e.preventDefault(); window.history.pushState({}, "", "/"); window.dispatchEvent(new PopStateEvent("popstate")); }} title="Back to home" aria-label="Back to home" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, background: SBG, border: "1.5px solid " + SGOLD, borderRadius: 4, textDecoration: "none", flexShrink: 0 }}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill={SGOLD} /></svg>
          </a>
          <span style={{ color: SGOLD }}>Voice After Life</span>
          <span style={{ color: SMUT, fontSize: 19, marginLeft: 10 }}>{"Human Dashboard · Staff"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={toggleTheme} style={{ ...SF_GHOST }}>{theme === "light" ? "☾ Dark mode" : "☀ Light mode"}</button>
          {right}
        </div>
      </div>
      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "24px 20px 60px" }}>{children}</div>
    </div>
  );
};

const PasswordInput = ({ style, ...rest }) => {
  const [show, setShow] = useState(false);
  const s = style || {};
  const { width, ...restStyle } = s;
  const wrapStyle = { position: "relative", display: "block", width: width || "100%" };
  const inStyle = { ...restStyle, width: "100%", boxSizing: "border-box", paddingRight: 42 };
  return (
    <span style={wrapStyle}>
      <input {...rest} type={show ? "text" : "password"} style={inStyle} />
      <button type="button" tabIndex={-1} onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} title={show ? "Hide password" : "Show password"} style={{ position: "absolute", top: 0, right: 0, height: "100%", width: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", color: show ? SGOLD : SMUT, padding: 0 }}>
        {show ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>
        )}
      </button>
    </span>
  );
};

const StaffLogin = ({ onDone }) => {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const resetPw = async () => {
    setErr(""); setResetMsg("");
    const em = (email || "").trim();
    if (!em) { setErr("Enter your staff email above, then tap Reset password."); return; }
    if (!supabase) { setErr("Password reset is not available right now."); return; }
    setBusy(true);
    let status = "not_approved";
    try { const c = await supabase.rpc("staff_reset_check", { p_email: em }); if (!c.error && c.data) status = c.data; } catch (e) {}
    if (status !== "approved") {
      setBusy(false);
      setErr("That email isn't an approved staff account. If you're a new team member, use 'Request staff access' below to get approved first.");
      return;
    }
    const r = await supabase.auth.resetPasswordForEmail(em, { redirectTo: window.location.origin + "/reset-password" });
    setBusy(false);
    if (r.error) { setErr(r.error.message); return; }
    setResetMsg("A password reset link has been sent to " + em + ". Check your inbox (and spam) to choose a new password.");
  };
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    if (!isStaffEmail(email)) {
      setBusy(false);
      setErr(
        "The staff area is for Voice After Life staff addresses only (ending in " +
          STAFF_EMAIL_DOMAIN + "). If you are a customer, sign in at voiceafterlife.com/signin."
      );
      return;
    }
    const r = await supabase.auth.signInWithPassword({ email: email.trim(), password: pw });
    if (r.error) { setBusy(false); setErr(r.error.message); return; }
    const s = await supabase.rpc("is_staff");
    if (s.data !== true) {
      let status = null;
      try { const st = await supabase.rpc("my_staff_status"); status = st.data; } catch (e2) {}
      await supabase.auth.signOut();
      setBusy(false);
      if (status === "pending") setErr("Your staff access request is still awaiting IT approval. You'll be able to sign in once it's approved.");
      else if (status === "denied") setErr("Your staff access request was not approved. Please contact IT if you believe this is a mistake.");
      else setErr("This account is not authorized for the staff area.");
      return;
    }
    setBusy(false); onDone();
  };
  const request = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const resp = await fetch("/.netlify/functions/staff-request", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ full_name: name.trim(), email: email.trim(), password: pw }) });
      const txt = await resp.text(); let data = {}; try { data = JSON.parse(txt); } catch (e2) {}
      setBusy(false);
      if (!resp.ok) { setErr(data.error || "Could not submit your request."); return; }
      setDone(true);
    } catch (e2) { setBusy(false); setErr("Could not submit your request. Please try again."); }
  };
  const linkBtn = { background: "none", border: "none", color: SGOLD, cursor: "pointer", textDecoration: "underline", fontSize: 16, padding: 0 };
  return (
    <StaffShell>
      <div style={{ maxWidth: 380, margin: "48px auto" }}>
        {done ? (
          <div>
            <h2 style={{ fontFamily: "Georgia, serif", color: SGOLD, margin: "0 0 6px" }}>Request received</h2>
            <p style={{ color: SMUT, fontSize: 17, lineHeight: 1.6 }}>{"Thanks" + (name ? ", " + name.split(" ")[0] : "") + ". Your request for staff access has been sent to IT for approval. You'll be able to sign in here once it's approved."}</p>
            <button onClick={() => { setDone(false); setMode("signin"); setPw(""); setName(""); }} style={{ ...SF_GHOST, marginTop: 12 }}>Back to sign in</button>
          </div>
        ) : mode === "signin" ? (
          <>
            <h2 style={{ fontFamily: "Georgia, serif", color: SGOLD, margin: "0 0 6px" }}>Staff Sign In</h2>
            <p style={{ color: SMUT, fontSize: 16, marginBottom: 18 }}>VAL team members only. Please use your staff credentials.</p>
            <form onSubmit={submit}>
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={SF_INPUT} required />
              <PasswordInput type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} style={SF_INPUT} required />
              {err && <div style={{ color: "var(--sf-err)", fontSize: 16, margin: "6px 0 10px" }}>{err}</div>}
              <button type="submit" disabled={busy} style={{ ...SF_PRIMARY, width: "100%" }}>{busy ? "Signing in..." : "Sign in"}</button>
            </form>
            {resetMsg && <div style={{ color: "var(--sf-ok)", fontSize: 16, margin: "4px 0 2px", textAlign: "center", lineHeight: 1.5 }}>{resetMsg}</div>}
            <p style={{ color: SMUT, fontSize: 16, marginTop: 14, textAlign: "center" }}>Forgot your password? <button type="button" onClick={resetPw} disabled={busy} style={linkBtn}>Reset password</button></p>
            <p style={{ color: SMUT, fontSize: 16, marginTop: 16, textAlign: "center" }}>New team member? <button onClick={() => { setErr(""); setMode("request"); }} style={linkBtn}>Request staff access</button></p>
          </>
        ) : (
          <>
            <h2 style={{ fontFamily: "Georgia, serif", color: SGOLD, margin: "0 0 6px" }}>Request staff access</h2>
            <p style={{ color: SMUT, fontSize: 16, marginBottom: 18 }}>For VAL team members. Use your company email; IT will review and approve your access.</p>
            <form onSubmit={request}>
              <input type="text" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} style={SF_INPUT} required />
              <input type="email" placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} style={SF_INPUT} required />
              <PasswordInput type="password" placeholder="Choose a password (8+ characters)" value={pw} onChange={(e) => setPw(e.target.value)} style={SF_INPUT} required />
              {err && <div style={{ color: "var(--sf-err)", fontSize: 16, margin: "6px 0 10px" }}>{err}</div>}
              <button type="submit" disabled={busy} style={{ ...SF_PRIMARY, width: "100%" }}>{busy ? "Submitting..." : "Request access"}</button>
            </form>
            <p style={{ color: SMUT, fontSize: 16, marginTop: 16, textAlign: "center" }}>Already have access? <button onClick={() => { setErr(""); setMode("signin"); }} style={linkBtn}>Sign in</button></p>
          </>
        )}
      </div>
    </StaffShell>
  );
};


const StaffManage = ({ me }) => {
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [owner, setOwner] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const load = async () => { const r = await supabase.from("staff").select("*").order("created_at", { ascending: true }); if (!r.error) setList(r.data || []); };
  useEffect(() => { load(); }, []);
  const invite = async (e) => {
    e.preventDefault(); setErr(""); setMsg(""); setBusy(true);
    try {
      const s = await supabase.auth.getSession();
      const token = s.data && s.data.session ? s.data.session.access_token : "";
      const resp = await fetch("/.netlify/functions/invite-staff", { method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ email: email.trim(), full_name: name.trim(), is_owner: owner }) });
      const txt = await resp.text(); let data = {}; try { data = JSON.parse(txt); } catch (er) {}
      setBusy(false);
      if (!resp.ok) { setErr(data.error || "Could not send the invite."); return; }
      setMsg("Invitation sent to " + email.trim() + ". They will set their own password.");
      setEmail(""); setName(""); setOwner(false); load();
    } catch (er) { setBusy(false); setErr("Something went wrong sending the invite."); }
  };
  const deactivate = async (id) => { await supabase.rpc("deactivate_staff", { p_staff_id: id }); load(); };
  const approve = async (id, owner) => { await supabase.rpc("approve_staff", { p_id: id, p_owner: !!owner }); load(); };
  const deny = async (id) => { await supabase.rpc("deny_staff", { p_id: id }); load(); };
  return (
    <div>
      <div style={{ background: SCARD, border: "1px solid " + SBOR, borderRadius: 14, padding: "18px 20px", marginBottom: 18 }}>
        <h3 style={{ color: SGOLD, fontFamily: "Georgia, serif", margin: "0 0 10px" }}>Invite a team member</h3>
        <form onSubmit={invite}>
          <input type="text" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} style={SF_INPUT} required />
          <input type="email" placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} style={SF_INPUT} required />
          <label style={{ display: "flex", gap: 8, alignItems: "center", color: SMUT, fontSize: 16, margin: "2px 0 12px" }}>
            <input type="checkbox" checked={owner} onChange={(e) => setOwner(e.target.checked)} /> Can manage staff (owner)
          </label>
          {err && <div style={{ color: "var(--sf-err)", fontSize: 16, marginBottom: 8 }}>{err}</div>}
          {msg && <div style={{ color: "var(--sf-ok)", fontSize: 16, marginBottom: 8 }}>{msg}</div>}
          <button type="submit" disabled={busy} style={SF_PRIMARY}>{busy ? "Sending..." : "Send invitation"}</button>
        </form>
      </div>
      {list.filter((s) => s.status === "pending").length > 0 && (
        <div style={{ background: SCARD, border: "1px solid " + SGOLD_DARK, borderRadius: 14, padding: "14px 18px", marginBottom: 18 }}>
          <h3 style={{ color: SGOLD, fontFamily: "Georgia, serif", margin: "0 0 4px" }}>Pending access requests</h3>
          <p style={{ color: SMUT, fontSize: 14, margin: "0 0 6px" }}>Team members who self-registered and are awaiting approval.</p>
          {list.filter((s) => s.status === "pending").map((s) => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid " + SBOR, gap: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ color: STXT, fontSize: 17 }}>{s.full_name || "(no name)"}</div>
                <div style={{ color: SMUT, fontSize: 14 }}>{s.email}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => approve(s.id, false)} style={SF_GHOST}>Approve</button>
                <button onClick={() => approve(s.id, true)} style={SF_PRIMARY}>Approve as owner</button>
                <button onClick={() => deny(s.id)} style={SF_DANGER}>Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <h3 style={{ color: STXT, fontFamily: "Georgia, serif", margin: "0 0 8px" }}>Team</h3>
      {list.filter((s) => s.status !== "pending").map((s) => (
        <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid " + SBOR }}>
          <div>
            <div style={{ color: STXT, fontSize: 17 }}>{(s.full_name || s.email) + (s.is_owner ? "  (owner)" : "")}</div>
            <div style={{ color: SMUT, fontSize: 14 }}>{s.email + (s.is_active ? "" : " \u00b7 inactive")}</div>
          </div>
          {s.is_active && me && s.id !== me.id && <button onClick={() => deactivate(s.id)} style={SF_DANGER}>Deactivate</button>}
        </div>
      ))}
    </div>
  );
};

const StaffConsole = ({ me, onSignOut }) => {
  const [tab, setTab] = useState("active");
  const [active, setActive] = useState([]);
  const [closed, setClosed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [briefs, setBriefs] = useState({});
  const [briefErr, setBriefErr] = useState({});
  const [briefBusy, setBriefBusy] = useState(null);
  const [confirmApprove, setConfirmApprove] = useState(null);
  const [msg, setMsg] = useState("");
  const [confMap, setConfMap] = useState({});
  const [timeline, setTimeline] = useState({});
  const [idMarkers, setIdMarkers] = useState({});

  const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch (e) { return ""; } };
  const loadActive = async () => { const r = await supabase.rpc("staff_active_cases"); if (!r.error) setActive(r.data || []); };
  const loadClosed = async () => { const r = await supabase.rpc("staff_closed_cases"); if (!r.error) setClosed(r.data || []); };
  const refresh = async () => { setLoading(true); await loadActive(); await loadClosed(); setLoading(false); };
  useEffect(() => { refresh(); }, []);
  useEffect(() => { (async () => { const ids = active.map((c) => c.case_id).filter((id) => confMap[id] === undefined); if (!ids.length) return; const next = {}; for (const id of ids) { try { const r = await supabase.rpc("case_confidence", { p_case_id: id }); if (!r.error && r.data) next[id] = r.data; } catch (e) {} } if (Object.keys(next).length) setConfMap((m) => ({ ...m, ...next })); })(); }, [active]);

  // Verifier progress ("1 of 2 answered") for the collecting list. Falls back
  // silently if case_verifier_progress has not been applied yet, so the queue
  // still renders rather than going blank.
  const [vpMap, setVpMap] = useState({});
  useEffect(() => { (async () => {
    const ids = active.map((c) => c.case_id).filter((id) => vpMap[id] === undefined);
    if (!ids.length) return;
    const next = {};
    for (const id of ids) {
      try { const r = await supabase.rpc("case_verifier_progress", { p_case_id: id }); next[id] = (!r.error && r.data) ? r.data : null; }
      catch (e) { next[id] = null; }
    }
    if (Object.keys(next).length) setVpMap((m) => ({ ...m, ...next }));
  })(); }, [active]);

  const [ovReason, setOvReason] = useState("");
  const [ovBusy, setOvBusy] = useState(false);
  const [ovMsg, setOvMsg] = useState("");
  // Shortening the grace period is the most consequential thing a reviewer can
  // do short of approving, so it is a named action with its own reason field and
  // its own audit line, never a side effect of some other click.
  const overrideGrace = async (id) => {
    setOvBusy(true); setOvMsg("");
    try {
      const r = await supabase.rpc("override_grace_period", { p_case_id: id, p_reason: ovReason.trim() });
      if (r.error) { setOvMsg(r.error.message); }
      else if (!r.data || r.data.ok !== true) { setOvMsg((r.data && r.data.error) || "Could not shorten the grace period."); }
      else { setOvMsg("Shortened. The case can now be claimed and reviewed."); setOvReason(""); await refresh(); }
    } catch (e) { setOvMsg("Could not shorten the grace period."); }
    setOvBusy(false);
  };

  const claim = async (id) => { setMsg(""); setBusyId(id); const r = await supabase.rpc("claim_case", { p_case_id: id }); setBusyId(null); if (r.error) { setMsg(r.error.message); } else if (!r.data || r.data.ok !== true) { setMsg("That case was just taken by someone else."); } await refresh(); };
  const unclaim = async (id) => { setBusyId(id); await supabase.rpc("unclaim_case", { p_case_id: id }); setBusyId(null); setConfirmApprove(null); await refresh(); };
  const approve = async (id) => { setMsg(""); setBusyId(id); const r = await supabase.rpc("approve_case", { p_case_id: id }); setBusyId(null); setConfirmApprove(null); if (r.error) { setMsg(r.error.message); await refresh(); return; } const d = r.data || {}; const sent = await notifyReleaseForCase(id); setMsg("Released \u2014 " + (d.items_released || 0) + " items unlocked, " + (d.recipients_tokened || 0) + " Legacy Members granted access. " + sent); await refresh(); };
  const reject = async (id) => { setBusyId(id); const r = await supabase.rpc("reject_case", { p_case_id: id, p_reason: null }); setBusyId(null); if (r.error) { setMsg(r.error.message); } else { setMsg("Case rejected. Nothing was released."); } await refresh(); };
  const genBrief = async (id) => { setBriefBusy(id); setBriefErr((m) => ({ ...m, [id]: "" })); try { const s = await supabase.auth.getSession(); const token = s.data && s.data.session ? s.data.session.access_token : ""; const resp = await fetch("/.netlify/functions/generate-case-brief", { method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ case_id: id }) }); const txt = await resp.text(); let data = {}; try { data = JSON.parse(txt); } catch (e) {} if (!resp.ok) { setBriefBusy(null); setBriefErr((m) => ({ ...m, [id]: data.error || "Could not generate the brief." })); return; } setBriefBusy(null); setBriefs((m) => ({ ...m, [id]: data.brief || "(no brief returned)" })); } catch (e) { setBriefBusy(null); setBriefErr((m) => ({ ...m, [id]: "Something went wrong generating the brief." })); } };

  const [viewCase, setViewCase] = useState(null);
  const [notes, setNotes] = useState({});
  const [noteDraft, setNoteDraft] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteErr, setNoteErr] = useState("");
  const loadNotes = async (id) => { if (!id) return; try { const r = await supabase.rpc("list_case_notes", { p_case_id: id }); setNotes((m) => ({ ...m, [id]: (!r.error && r.data) ? r.data : [] })); } catch (e) { setNotes((m) => ({ ...m, [id]: [] })); } };
  const addNote = async () => { const body = (noteDraft || "").trim(); if (!body || !viewCase) return; setNoteBusy(true); setNoteErr(""); try { const r = await supabase.rpc("add_case_note", { p_case_id: viewCase, p_body: body }); if (r.error) { setNoteErr(r.error.message || "Could not save note."); } else { const row = Array.isArray(r.data) ? r.data[0] : r.data; setNotes((m) => ({ ...m, [viewCase]: [ ...(m[viewCase] || []), row ] })); setNoteDraft(""); } } catch (e) { setNoteErr("Could not save note."); } finally { setNoteBusy(false); } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadIdMarkers = async (id) => { if (!id) return; try { const r = await supabase.rpc("case_identity_markers", { p_case_id: id }); setIdMarkers((m) => ({ ...m, [id]: (!r.error && r.data) ? r.data : null })); } catch (e) { setIdMarkers((m) => ({ ...m, [id]: null })); } };
  const nameOverlap = (rep, acc) => { const parts = (s) => String(s || "").toLowerCase().split(/[,;/&\n]+|\band\b/).map((x) => x.trim()).filter(Boolean); const a = parts(rep), b = parts(acc); if (!a.length && !b.length) return { state: "empty", label: "No data" }; if (!a.length || !b.length) return { state: "empty", label: "Incomplete" }; const bTok = new Set(b.join(" ").split(/\s+/).filter((w) => w.length > 1)); let matched = 0; a.forEach((nm) => { const ws = nm.split(/\s+/).filter((w) => w.length > 1); if (ws.some((w) => bTok.has(w))) matched++; }); if (matched === 0) return { state: "none", label: "No match" }; if (matched === a.length) return { state: "match", label: "Match" }; return { state: "partial", label: "Partial" }; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (viewCase) { setNoteDraft(""); setNoteErr(""); loadNotes(viewCase); loadIdMarkers(viewCase); } }, [viewCase]);
  const caseNo = (id) => { let h = 2166136261 >>> 0; const s = String(id || ""); for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return 100000 + (h % 900000); };
  const fmtDT = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("en-US", { month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" }); } catch (e) { return ""; } };
  const confColor = (s) => (s >= 80 ? "#5FBF77" : s >= 55 ? "#E0B341" : "#E07A5F");
  const findCase = (id) => active.find((c) => c.case_id === id) || closed.find((c) => c.case_id === id) || null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { (async () => { const ids = closed.map((c) => c.case_id).filter((id) => confMap[id] === undefined); if (!ids.length) return; const next = {}; for (const id of ids) { try { const r = await supabase.rpc("case_confidence", { p_case_id: id }); if (!r.error) next[id] = r.data; } catch (e) {} } if (Object.keys(next).length) setConfMap((m) => ({ ...m, ...next })); })(); }, [closed]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (viewCase && briefs[viewCase] === undefined && briefBusy !== viewCase) { genBrief(viewCase); } }, [viewCase]);
  // The Verification Timeline. Reloaded every time a case is opened rather
  // than cached for the session, because the agent writes to it continuously
  // and a reviewer looking at a live case needs what is true now, not what was
  // true when they first opened the queue.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!viewCase) return;
    let alive = true;
    (async () => {
      const r = await supabase.rpc("case_timeline", { p_case_id: viewCase });
      if (!alive) return;
      setTimeline((m) => ({ ...m, [viewCase]: r.error ? [] : (r.data || []) }));
    })();
    return () => { alive = false; };
  }, [viewCase]);

  const TH = { fontSize: 14, letterSpacing: 0.8, textTransform: "uppercase", color: SMUT, textAlign: "left", fontWeight: 600, padding: "0 12px 9px", whiteSpace: "nowrap" };
  const TD = { padding: "14px 12px", borderTop: "1px solid " + SBOR, fontSize: 18, verticalAlign: "middle", color: STXT };
  const confPct = (id) => { const cf = confMap[id]; if (cf === undefined) return <span style={{ color: SMUT }}>{"…"}</span>; if (!cf) return <span style={{ color: SMUT }}>{"—"}</span>; const col = confColor(cf.score); return (<span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}><span style={{ color: col, fontWeight: 700, fontSize: 19 }}>{cf.score + "%"}</span><span style={{ display: "inline-block", width: 44, height: 6, borderRadius: 3, background: SBOR, overflow: "hidden" }}><span style={{ display: "block", height: "100%", width: cf.score + "%", background: col }} /></span></span>); };
  const caseLink = (id) => (<button onClick={() => setViewCase(id)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: SGOLD, fontWeight: 600, fontSize: 19, letterSpacing: 0.3, borderBottom: "1px dotted #c9a84c66", fontFamily: "inherit" }}>{"#" + caseNo(id)}</button>);

  // A case is VISIBLE **and CLAIMABLE** from the moment it is reported.
  //
  // Two changes landed together on 2026-09-06, at Joe's instruction: "The rule to
  // release the case to the Human Dashboard is not a hard stop." claim_case()
  // lost its grace-period checks and approve_case() gained one. Reaching a
  // reviewer was never the dangerous act — RELEASING a legacy is — so the guard
  // moved to where release actually happens. A reviewer now works the case from
  // day one while the agent keeps researching alongside them, and the grace
  // window only holds back the final approval.
  const graceLeftMs = (c) => (c.grace_until ? new Date(c.grace_until).getTime() - Date.now() : 0);
  const inGrace = (c) => graceLeftMs(c) > 0;
  const daysLeft = (c) => Math.max(1, Math.ceil(graceLeftMs(c) / 86400000));

  // THE COMBINED CLOCK.
  // Joe, 2026-09-06: "The 30 days is a combined timeline apply to the AI Agent
  // compiling the AI Brief and the Human reviewer. Both inspecting the file for
  // release should take no more than 30 days." So it is ONE clock, started the
  // day the death was reported, not one for the agent and another for the
  // reviewer. 30 days is the target. 90 is the outside limit, held in reserve
  // for a case that genuinely needs more investigation.
  const SLA_TARGET_DAYS = 30;
  const SLA_MAX_DAYS = 90;
  const slaDay = (c) => {
    const t = c.reported_at ? new Date(c.reported_at).getTime() : 0;
    if (!t) return null;
    return Math.max(1, Math.floor((Date.now() - t) / 86400000) + 1);
  };
  const slaColor = (d) => (d === null ? SMUT : d > SLA_MAX_DAYS ? "#E07A5F" : d > SLA_TARGET_DAYS ? "#E0B341" : "#7FB08A");
  const slaCell = (c) => {
    const d = slaDay(c);
    if (d === null) return <span style={{ color: SMUT }}>{"\u2014"}</span>;
    const over = d > SLA_TARGET_DAYS;
    const col = slaColor(d);
    return (
      <span
        title={over
          ? "Past the 30-day target. " + SLA_MAX_DAYS + " days is the outside limit for a decision on this case."
          : "One clock for both halves of the work: the agent's research and the reviewer's decision share these 30 days."}
        style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}
      >
        <span style={{ color: col, fontWeight: 700, fontSize: 17 }}>{"Day " + d}</span>
        <span style={{ color: SMUT, fontSize: 15 }}>{over ? "\u00b7 past 30" : "of " + SLA_TARGET_DAYS}</span>
        <span style={{ display: "inline-block", width: 42, height: 5, borderRadius: 3, background: SBOR, overflow: "hidden" }}>
          <span style={{ display: "block", height: "100%", width: Math.min(100, Math.round((d / SLA_TARGET_DAYS) * 100)) + "%", background: col }} />
        </span>
      </span>
    );
  };

  // ONE queue, ordered exactly the way claim_case() orders it — oldest
  // grace_until first — so what a reviewer is offered on screen is always what
  // the database will actually let them take. Whether the grace window is still
  // open now changes the chip and the release guard, not who can pick the case up.
  const unassigned = active
    .filter((c) => !c.assigned_to)
    .slice()
    .sort((a, b) => new Date(a.grace_until || 0) - new Date(b.grace_until || 0));
  const collecting = unassigned.filter(inGrace);
  const waiting = unassigned;
  const inProgress = active.filter((c) => c.assigned_to);
  const closedSorted = closed.slice().sort((a, b) => new Date(a.grace_until || 0) - new Date(b.grace_until || 0));
  const nextUp = unassigned[0];
  const tabBtn = (key, label) => (<button onClick={() => { setViewCase(null); setTab(key); }} style={{ background: "none", border: "none", borderBottom: tab === key ? "2px solid " + SGOLD : "2px solid transparent", color: tab === key ? SGOLD : SMUT, fontSize: 19, fontWeight: tab === key ? 600 : 400, padding: "0 2px 12px", marginRight: 28, cursor: "pointer", fontFamily: "inherit" }}>{label}</button>);

  return (
    <StaffShell right={<div style={{ display: "flex", gap: 12, alignItems: "center" }}><span style={{ color: SMUT, fontSize: 17 }}>{me ? (me.full_name || me.email) : ""}</span><button onClick={onSignOut} style={SF_GHOST}>Sign out</button></div>}>
      {viewCase ? (() => {
        const c = findCase(viewCase);
        if (!c) return (<div><button onClick={() => setViewCase(null)} style={{ ...SF_GHOST, marginBottom: 16 }}>{"← Back"}</button><div style={{ color: SMUT }}>Case not found.</div></div>);
        const cf = confMap[viewCase];
        // A HALTed case must never render green. If a Trusted Verifier has told
        // us the person is alive, the number is beside the point -- the chip is
        // red however high the score happens to be.
        const col = cf ? (cf.halted ? "#E07A5F" : confColor(cf.score)) : SMUT;
        const isClosed = !!c.decision;
        const mine = c.assigned_to && me && c.assigned_to === me.id;
        return (
          <div style={{ fontSize: 22, lineHeight: 1.6 }}>
            <button onClick={() => { setViewCase(null); }} style={{ background: "none", border: "none", color: SMUT, cursor: "pointer", fontSize: 18, marginBottom: 20, fontFamily: "inherit" }}>{"← Back to " + (isClosed ? "Closed / History" : "Active Queue")}</button>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 14, marginBottom: 6 }}>
              <h2 style={{ fontFamily: "Georgia, serif", color: SGOLD, fontSize: 36, fontWeight: 600, margin: 0 }}>AI Case Brief</h2>
              {cf && <span style={{ color: col, fontSize: 20, fontWeight: 700 }}>{"· Confidence " + cf.score + "% · " + cf.recommendation}</span>}
            </div>
            <div style={{ color: SMUT, fontSize: 19, margin: "4px 0 24px", lineHeight: 1.6 }}>{"Case #" + caseNo(c.case_id) + "  ·  Reported by " + (c.reported_by_name || "someone") + "  ·  Grace ended " + fmtDate(c.grace_until) + "  ·  " + (c.item_count || 0) + " items  ·  " + (c.recipient_count || 0) + " Legacy Members  ·  " + (isClosed ? ("Reviewed by " + (c.decided_by_name || "staff")) : ("Assigned to " + (mine ? "you" : (c.assignee_name || "—"))))}</div>

            {cf && (
              <div style={{ background: SCARD, border: "1px solid " + SBOR, borderRadius: 12, padding: "22px 24px", marginBottom: 22 }}>
                <div style={{ color: SGOLD, fontSize: 16, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>AI Confidence</div>
                <div style={{ display: "flex", alignItems: "center", gap: 18, margin: "8px 0 18px" }}>
                  <div style={{ fontSize: 60, fontWeight: 800, color: col, lineHeight: 1 }}>{cf.score + "%"}</div>
                  <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: 0.6, padding: "7px 15px", borderRadius: 999, border: "1px solid " + col, color: col }}>{cf.recommendation}</div>
                </div>
                {cf.cancelled && <div style={{ color: "#E07A5F", fontSize: 17, marginBottom: 10 }}>{"⚠ The User cancelled during the grace period."}</div>}
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {(cf.signals || []).map((s, si) => (
                    <div key={si} style={{ display: "flex", alignItems: "flex-start", gap: 13, padding: "13px 0", borderTop: si === 0 ? "none" : "1px solid " + SBOR, fontSize: 20 }}>
                      <span style={{ color: s.pass ? "#5FBF77" : "#E07A5F", fontSize: 23, width: 22, flexShrink: 0 }}>{s.pass ? "✔" : "–"}</span>
                      <span style={{ flex: 1 }}><span style={{ fontWeight: 600 }}>{s.label}</span><span style={{ color: SMUT, display: "block", fontSize: 18, marginTop: 2 }}>{s.detail}</span></span>
                      <span style={{ color: SMUT, whiteSpace: "nowrap", fontSize: 18, paddingLeft: 14 }}>{s.points + " / " + s.max}</span>
                    </div>
                  ))}
                </div>
                <div style={{ color: SMUT, fontSize: 16, marginTop: 14, fontStyle: "italic" }}>{"Transparent, signal-based estimate to assist review — not a decision."}</div>
              </div>
            )}

            {cf && cf.halted && (
              <div style={{ background: "#2a1414", border: "2px solid #E07A5F", borderRadius: 12, padding: "20px 24px", marginBottom: 22 }}>
                <div style={{ color: "#E07A5F", fontSize: 16, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>Halted</div>
                <div style={{ color: STXT, fontSize: 22, marginTop: 8, lineHeight: 1.5 }}>
                  {(cf.verifiers_denied || 1) + " Trusted Verifier" + ((cf.verifiers_denied || 1) === 1 ? " has" : "s have") + " told us this person is still alive."}
                </div>
                <div style={{ color: SMUT, fontSize: 18, marginTop: 8, lineHeight: 1.5 }}>
                  Someone the account holder chose in advance, for exactly this moment, says the report is wrong. Do not approve this case on the strength of the score. Read the timeline below and contact them directly.
                </div>
              </div>
            )}

            {(() => {
              const row = active.find((x) => x.case_id === viewCase);
              if (!row || !inGrace(row)) return null;
              const halted = !!(cf && cf.halted);
              // Threshold 70, measured on the headline score. Joe's call,
              // 2026-09-06: a Trusted Verifier confirmation is now worth 45 (35
              // for the first, 10 for the second) and online evidence only 5, so
              // the maximum reachable while the window is open is 90 — clear of
              // 70 without needing the grace-exclusion this used to apply. One
              // number on screen instead of two. Mirrors override_grace_period().
              const gate = cf && typeof cf.score === "number" ? cf.score : null;
              const eligible = !halted && gate !== null && gate >= 70;
              return (
                <div style={{ background: SCARD, border: "1px solid " + SBOR, borderLeft: "3px solid " + (eligible ? SGOLD : SBOR), borderRadius: 12, padding: "20px 24px", marginBottom: 22 }}>
                  <div style={{ color: SMUT, fontSize: 16, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>Grace period</div>
                  <div style={{ color: STXT, fontSize: 20, marginTop: 8, lineHeight: 1.5 }}>
                    {daysLeft(row) + (daysLeft(row) === 1 ? " day" : " days") + " left. You can review this case now \u2014 it just cannot be approved until the window closes."}
                  </div>

                  <div style={{ color: SMUT, fontSize: 17, marginTop: 8, lineHeight: 1.55 }}>
                    {"The grace period is the account holder's window to tell us they are alive. Nothing is released while it runs. It can be shortened at 70% confidence or above; a Trusted Verifier saying the person is alive blocks that outright, at any score. Shortening it releases nothing by itself — a staff member still has to approve the case."}
                  </div>
                  {eligible ? (
                    <div style={{ marginTop: 16 }}>
                      <textarea
                        value={ovReason}
                        onChange={(e) => setOvReason(e.target.value)}
                        rows={2}
                        placeholder="Why is it right to shorten this window? Kept on the case permanently."
                        style={{ width: "100%", boxSizing: "border-box", background: "var(--sf-bg)", border: "1px solid " + SBOR, color: STXT, borderRadius: 8, padding: "10px 12px", fontSize: 17, font: "inherit" }}
                      />
                      <button
                        onClick={() => overrideGrace(viewCase)}
                        disabled={ovBusy || ovReason.trim().length < 15}
                        style={{ ...SF_PRIMARY, marginTop: 10, opacity: (ovBusy || ovReason.trim().length < 15) ? 0.5 : 1 }}
                      >
                        {ovBusy ? "Shortening\u2026" : "Shorten the grace period"}
                      </button>
                    </div>
                  ) : (
                    <div style={{ color: SMUT, fontSize: 17, marginTop: 12, fontStyle: "italic" }}>
                      {halted
                        ? "A Trusted Verifier says this person is alive. The window cannot be shortened."
                        : (gate === null ? "Waiting for a confidence score."
                          : "Confidence is " + gate + "%. It must reach 70%.")}
                    </div>
                  )}
                  {ovMsg ? <div style={{ color: ovMsg.indexOf("Shortened") === 0 ? "#7FB08A" : "#E07A5F", fontSize: 17, marginTop: 12 }}>{ovMsg}</div> : null}
                </div>
              );
            })()}

            <div style={{ color: SMUT, fontSize: 16, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>Verification Timeline</div>
            <div style={{ background: SCARD, border: "1px solid " + SBOR, borderRadius: 12, padding: "22px 24px", marginBottom: 22 }}>
              <div style={{ color: SMUT, fontSize: 17, marginBottom: 16 }}>
                Everything the system has done on this case, in order. Searches that found nothing are listed too -- six sources checked with no match is not the same as never having looked.
              </div>
              {timeline[viewCase] === undefined ? (
                <div style={{ color: SMUT, fontSize: 18 }}>Loading...</div>
              ) : !timeline[viewCase].length ? (
                <div style={{ color: SMUT, fontSize: 18, lineHeight: 1.5 }}>
                  Nothing recorded yet. The agent picks up a new report within ten minutes: it tells the account holder, asks their Trusted Verifiers, and searches for a published obituary.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {timeline[viewCase].map((ev, ei) => {
                    const bad = ev.kind === "verifier_denied" || ev.kind === "user_cancelled" || ev.kind === "release_delivery_gap";
                    const good = ev.kind === "verifier_confirmed" || (ev.kind === "evidence_found" && ev.strength === "strong");
                    const weak = ev.kind === "evidence_none" || ev.kind === "investigation_failed" || ev.kind === "verifier_contact_failed" || ev.kind === "user_notify_failed";
                    const dot = bad ? "#E07A5F" : good ? "#5FBF77" : weak ? SMUT : SGOLD;
                    const when = (() => { try { return new Date(ev.occurred_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch (e3) { return ""; } })();
                    const actor = ev.actor === "ai_agent" ? "AI agent" : ev.actor === "verifier" ? "Trusted Verifier" : ev.actor === "staff" ? "Staff" : ev.actor === "user" ? "Account holder" : "System";
                    return (
                      <div key={ei} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 0", borderTop: ei === 0 ? "none" : "1px solid " + SBOR }}>
                        <span style={{ width: 11, height: 11, borderRadius: 999, background: dot, flexShrink: 0, marginTop: 8 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ color: SMUT, fontSize: 15 }}>{when + "  \u00b7  " + actor}</div>
                          <div style={{ color: STXT, fontSize: 20, marginTop: 3, lineHeight: 1.4 }}>{ev.headline}</div>
                          {ev.detail ? <div style={{ color: SMUT, fontSize: 17, marginTop: 4, lineHeight: 1.5 }}>{ev.detail}</div> : null}
                          {ev.source_url ? (
                            <a href={ev.source_url} target="_blank" rel="noreferrer noopener" style={{ color: SGOLD, fontSize: 16, marginTop: 5, display: "inline-block", wordBreak: "break-all" }}>Open the source</a>
                          ) : null}
                        </div>
                        {ev.strength ? (
                          <span style={{ color: ev.strength === "strong" ? "#5FBF77" : SMUT, border: "1px solid " + (ev.strength === "strong" ? "#5FBF77" : SBOR), borderRadius: 999, padding: "3px 11px", fontSize: 14, whiteSpace: "nowrap", flexShrink: 0, marginTop: 4 }}>
                            {ev.strength + (ev.strength === "strong" ? "" : " \u00b7 0 pts")}
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {idMarkers[viewCase] && idMarkers[viewCase].ok !== false && (
              <>
                <div style={{ color: SMUT, fontSize: 16, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>Identity Match</div>
                <div style={{ background: SCARD, border: "1px solid " + SBOR, borderRadius: 12, padding: "22px 24px", marginBottom: 22 }}>
                  <div style={{ color: SMUT, fontSize: 17, marginBottom: 6 }}>{"Names on the death report vs. what the account holder saved. A supporting signal for identity — not a decision."}</div>
                  {[{ label: "Sibling name(s)", rep: idMarkers[viewCase].reported_siblings, acc: idMarkers[viewCase].account_siblings }, { label: "Parent name(s)", rep: idMarkers[viewCase].reported_parents, acc: idMarkers[viewCase].account_parents }].map((row, ri) => {
                    const mm = nameOverlap(row.rep, row.acc);
                    const mc = mm.state === "match" ? "#5FBF77" : mm.state === "partial" ? "#E0B341" : mm.state === "none" ? "#E07A5F" : SMUT;
                    return (
                      <div key={ri} style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", padding: "14px 0", borderTop: "1px solid " + SBOR }}>
                        <div style={{ flex: "1 1 220px" }}>
                          <div style={{ color: SMUT, fontSize: 15, marginBottom: 3 }}>{"Reported · " + row.label}</div>
                          <div style={{ color: STXT, fontSize: 20 }}>{row.rep || "—"}</div>
                        </div>
                        <div style={{ flex: "1 1 220px" }}>
                          <div style={{ color: SMUT, fontSize: 15, marginBottom: 3 }}>{"On file · " + row.label}</div>
                          <div style={{ color: STXT, fontSize: 20 }}>{row.acc || "—"}</div>
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 700, padding: "6px 14px", borderRadius: 999, border: "1px solid " + mc, color: mc, whiteSpace: "nowrap" }}>{mm.label}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div style={{ color: SMUT, fontSize: 16, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>AI Case Brief</div>
            <div style={{ background: SCARD, border: "1px solid " + SBOR, borderRadius: 12, padding: "24px 28px", marginBottom: 22, fontSize: 23, lineHeight: 1.7, color: STXT, whiteSpace: "pre-wrap" }}>
              {briefErr[viewCase] ? <span style={{ color: "var(--sf-err)" }}>{briefErr[viewCase]}</span> : (briefs[viewCase] !== undefined ? briefs[viewCase] : (briefBusy === viewCase ? "Generating the brief…" : "Preparing the brief…"))}
            </div>

            <div style={{ color: SMUT, fontSize: 16, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700, margin: "0 0 10px" }}>Brief Insert — Reviewer Notes</div>
            <div style={{ background: SCARD, border: "1px solid " + SBOR, borderRadius: 12, padding: "18px 22px", marginBottom: 22 }}>
              {(notes[viewCase] && notes[viewCase].length > 0) ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: (mine && !isClosed) ? 18 : 0 }}>
                  {notes[viewCase].map((n) => (
                    <div key={n.id} style={{ borderLeft: "3px solid " + SGOLD, background: SCARD, borderRadius: 6, padding: "12px 15px" }}>
                      <div style={{ color: STXT, fontSize: 20, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{n.body}</div>
                      <div style={{ color: SMUT, fontSize: 16, marginTop: 8 }}>{(n.author_name || "Staff") + " · " + fmtDT(n.created_at)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: SMUT, fontSize: 18, fontStyle: "italic", marginBottom: (mine && !isClosed) ? 18 : 0 }}>{notes[viewCase] === undefined ? "Loading notes…" : "No notes yet."}</div>
              )}
              {mine && !isClosed && (
                <div>
                  <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Type a brief insert — notes, concerns, or outcomes — before making your release recommendation." rows={4} style={{ width: "100%", boxSizing: "border-box", background: SCARD, border: "1px solid " + SBOR, borderRadius: 8, color: STXT, fontSize: 19, lineHeight: 1.6, padding: "12px 14px", fontFamily: "inherit", resize: "vertical" }} />
                  {noteErr && <div style={{ color: "var(--sf-err)", fontSize: 17, marginTop: 8 }}>{noteErr}</div>}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
                    <button onClick={addNote} disabled={noteBusy || !noteDraft.trim()} style={{ ...SF_PRIMARY, opacity: (noteBusy || !noteDraft.trim()) ? 0.5 : 1 }}>{noteBusy ? "Saving…" : "Save note"}</button>
                    <span style={{ color: SMUT, fontSize: 16 }}>{"Each note is time-stamped automatically when saved."}</span>
                  </div>
                </div>
              )}
            </div>

            {isClosed ? (
              <div style={{ background: "var(--sf-ok-bg)", border: "1px solid #5fbf7733", borderRadius: 12, padding: "18px 22px", fontSize: 19, color: "var(--sf-ok)" }}>{"Decision: " + (c.decision === "approved" ? "Released" : "Rejected") + " · reviewed by " + (c.decided_by_name || "staff") + " · completed " + fmtDT(c.decided_at) + ". This case is closed and read-only."}</div>
            ) : mine ? (
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                {confirmApprove === viewCase ? (
                  <>
                    <span style={{ color: "var(--sf-err)", fontSize: 19 }}>{"Release the library for " + (c.subject_name || "this user") + "? This cannot be undone."}</span>
                    <button onClick={async () => { await approve(viewCase); setViewCase(null); }} disabled={busyId === viewCase} style={{ ...SF_PRIMARY, fontSize: 19, padding: "12px 22px" }}>Yes, release</button>
                    <button onClick={() => setConfirmApprove(null)} style={SF_GHOST}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setConfirmApprove(viewCase)} style={{ ...SF_PRIMARY, fontSize: 19, padding: "12px 22px" }}>Approve &amp; release</button>
                    <button onClick={async () => { await reject(viewCase); setViewCase(null); }} disabled={busyId === viewCase} style={{ ...SF_GHOST, color: "#E07A5F", borderColor: "#e07a5f66" }}>Reject</button>
                    <button onClick={async () => { await unclaim(viewCase); setViewCase(null); }} style={SF_GHOST}>Release back to pool</button>
                  </>
                )}
              </div>
            ) : (
              <div style={{ color: SMUT, fontSize: 18, fontStyle: "italic" }}>{"This case is assigned to " + (c.assignee_name || "another agent") + ". Only the assigned reviewer can act on it."}</div>
            )}
          </div>
        );
      })() : (
        <>
          <h1 style={{ fontFamily: "Georgia, serif", color: SGOLD, margin: "0 0 2px", fontSize: 34 }}>Release approvals</h1>
          <p style={{ color: SMUT, fontSize: 17, margin: "0 0 16px" }}>Every reported case, oldest first, from the day it arrives. Claim the next case in line to work it while the agent keeps researching. Approving releases that person's library to their Legacy Members and cannot be undone.</p>
          <div style={{ borderBottom: "1px solid " + SBOR, marginBottom: 16 }}>
            {tabBtn("active", "Active Queue")}
            {tabBtn("closed", "Closed / History")}
            {me && me.is_owner && tabBtn("staff", "Team")}
            <button onClick={refresh} style={{ ...SF_GHOST, float: "right", marginBottom: 6 }}>Refresh</button>
          </div>
          {msg && <div style={{ color: "var(--sf-ok)", fontSize: 17, marginBottom: 12 }}>{msg}</div>}

          {tab === "active" && (
            <div>
              {loading ? <div style={{ color: SMUT }}>Loading{"…"}</div> : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--sf-hi-bg2)", border: "1px solid " + SBOR, borderLeft: "3px solid " + SGOLD, borderRadius: 8, padding: "12px 16px", margin: "4px 0 20px", fontSize: 17, color: STXT, lineHeight: 1.5 }}>
                    <span>{"⚖️"}</span>
                    <div>{"Cases are worked in order of age. You can only claim the next case up — the oldest one still unassigned — and you can claim it from the day it is reported. Release still waits for the grace period to close."}</div>
                  </div>

                  {/* ONE queue since 2026-09-06. There used to be two tables here: a
                      read-only "Collecting evidence" list of cases still inside the grace
                      period, and below it the cases a reviewer could actually claim. That
                      split existed only because claim_case() refused cases in grace. It no
                      longer does, so keeping the split would mean telling reviewers
                      something untrue. The COLLECTING chip survives, because "the agent is
                      still gathering" is worth seeing \u2014 it just no longer means hands off. */}
                  <h3 style={{ color: "var(--sf-info)", fontSize: 16, textTransform: "uppercase", letterSpacing: 1.4, margin: "6px 2px 10px", fontWeight: 700 }}>
                    {"Awaiting review (" + waiting.length + ")" + (collecting.length ? " \u00b7 " + collecting.length + " still collecting" : "")}
                  </h3>
                  {nextUp && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--sf-hi-bg)", border: "1px solid #c9a84c44", borderRadius: 8, padding: "13px 16px", marginBottom: 12, fontSize: 19, flexWrap: "wrap" }}>
                      <span>{"Next Case Up For Review:"}</span>
                      <b style={{ color: SGOLD }}>{"#" + caseNo(nextUp.case_id)}</b>
                      <span>{"Reported " + fmtDT(nextUp.reported_at)}</span>
                      {slaCell(nextUp)}
                      <button onClick={() => claim(nextUp.case_id)} disabled={busyId === nextUp.case_id} style={{ ...SF_PRIMARY, marginLeft: "auto" }}>{busyId === nextUp.case_id ? "Assigning\u2026" : "Assign Next Case To Me"}</button>
                    </div>
                  )}
                  {waiting.length === 0 ? <div style={{ color: SMUT, fontSize: 18, padding: "4px 0 10px" }}>No unassigned cases.</div> : (
                    <>
                      <div style={{ color: SMUT, fontSize: 16, lineHeight: 1.55, margin: "0 2px 10px" }}>
                        {"Oldest first. A case marked COLLECTING is still inside the account holder\u2019s grace period and the agent is still gathering \u2014 you can claim it and work it now. It simply cannot be approved until that window closes."}
                      </div>
                      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, marginBottom: 26 }}>
                        <thead><tr><th style={TH}>Reported</th><th style={TH}>Status</th><th style={TH}>Case #</th><th style={TH}>Confidence</th><th style={TH}>Trusted Verifiers</th><th style={TH}>Combined clock</th><th style={TH}></th></tr></thead>
                        <tbody>
                          {waiting.map((c, i) => {
                            const isNext = i === 0;
                            const vp = vpMap[c.case_id];
                            const cf2 = confMap[c.case_id];
                            const still = inGrace(c);
                            return (
                              <tr key={c.case_id} style={isNext ? { background: "var(--sf-hi-bg2)" } : null}>
                                <td style={{ ...TD, ...(isNext ? { borderLeft: "3px solid " + SGOLD } : null) }}>{fmtDT(c.reported_at)}</td>
                                <td style={TD}>
                                  <span style={{ display: "inline-flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
                                    {isNext ? <span style={{ fontSize: 14, fontWeight: 700, padding: "5px 11px", borderRadius: 999, background: SGOLD, color: "#12100a" }}>{"\u25b6 NEXT UP"}</span> : null}
                                    {still
                                      ? <span title={daysLeft(c) + (daysLeft(c) === 1 ? " day" : " days") + " of grace left. Claimable now; not approvable yet."} style={{ fontSize: 14, fontWeight: 700, padding: "5px 11px", borderRadius: 999, background: "#7fb08a1e", border: "1px solid #7fb08a4d", color: "#7FB08A" }}>COLLECTING</span>
                                      : <span style={{ fontSize: 14, fontWeight: 700, padding: "5px 11px", borderRadius: 999, background: "#88b0d118", border: "1px solid #88b0d14d", color: "var(--sf-info)" }}>OPEN</span>}
                                  </span>
                                </td>
                                <td style={TD}>{caseLink(c.case_id)}</td>
                                <td style={TD}>{confPct(c.case_id)}</td>
                                <td style={{ ...TD, color: cf2 && cf2.halted ? "#E07A5F" : SMUT }}>
                                  {cf2 && cf2.halted
                                    ? "Halted \u2014 verifier says alive"
                                    : (vp
                                        ? (vp.asked
                                            ? (vp.answered + " of " + vp.asked + " answered"
                                                + (vp.confirmed ? " \u00b7 " + vp.confirmed + " confirmed" : "")
                                                + (vp.denied ? " \u00b7 " + vp.denied + " say alive" : ""))
                                            : "None on file")
                                        : "\u2014")}
                                </td>
                                <td style={TD}>{slaCell(c)}</td>
                                <td style={{ ...TD, textAlign: "right" }}>{isNext ? <button onClick={() => claim(c.case_id)} disabled={busyId === c.case_id} style={{ ...SF_PRIMARY, padding: "8px 14px", fontSize: 17 }}>Assign to me</button> : <span style={{ color: SMUT, fontSize: 16, border: "1px solid " + SBOR, borderRadius: 7, padding: "7px 13px" }}>Queued</span>}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}

                  <h3 style={{ color: "#E0B341", fontSize: 16, textTransform: "uppercase", letterSpacing: 1.4, margin: "18px 2px 10px", fontWeight: 700 }}>{"In review (" + inProgress.length + ")"}</h3>
                  {inProgress.length === 0 ? <div style={{ color: SMUT, fontSize: 18, padding: "4px 0" }}>Nothing in progress.</div> : (
                    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                      <thead><tr><th style={TH}>Reported</th><th style={TH}>Status</th><th style={TH}>Case #</th><th style={TH}>Confidence</th><th style={TH}>Combined clock</th><th style={TH}>Assigned to</th></tr></thead>
                      <tbody>
                        {inProgress.map((c) => {
                          const mine = me && c.assigned_to === me.id;
                          const still = inGrace(c);
                          return (
                            <tr key={c.case_id}>
                              <td style={TD}>{fmtDT(c.reported_at)}</td>
                              <td style={TD}>
                                <span style={{ display: "inline-flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
                                  <span style={{ fontSize: 14, fontWeight: 700, padding: "5px 11px", borderRadius: 999, background: "#e0b34118", border: "1px solid #e0b3414d", color: "#E0B341" }}>IN REVIEW</span>
                                  {still ? <span title={daysLeft(c) + (daysLeft(c) === 1 ? " day" : " days") + " of grace left. Cannot be approved until it ends."} style={{ fontSize: 14, fontWeight: 700, padding: "5px 11px", borderRadius: 999, background: "#7fb08a1e", border: "1px solid #7fb08a4d", color: "#7FB08A" }}>IN GRACE</span> : null}
                                </span>
                              </td>
                              <td style={TD}>{caseLink(c.case_id)}</td>
                              <td style={TD}>{confPct(c.case_id)}</td>
                              <td style={TD}>{slaCell(c)}</td>
                              <td style={TD}>{c.assignee_name || "—"}{mine ? <b style={{ color: SGOLD }}>{" (you)"}</b> : null}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          )}

          {tab === "closed" && (
            <div>
              {closedSorted.length === 0 ? <div style={{ color: SMUT, fontSize: 18 }}>No closed cases yet.</div> : (
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead><tr><th style={TH}>Date &amp; time in queue</th><th style={TH}>Status</th><th style={TH}>Case #</th><th style={TH}>Confidence</th><th style={TH}>Reviewed by</th><th style={TH}>Review completed</th></tr></thead>
                  <tbody>
                    {closedSorted.map((c) => {
                      const rel = c.decision === "approved";
                      return (
                        <tr key={c.case_id}>
                          <td style={TD}>{fmtDT(c.grace_until)}</td>
                          <td style={TD}><span style={{ fontSize: 14, fontWeight: 700, padding: "5px 11px", borderRadius: 999, background: rel ? "#5fbf7718" : "#e07a5f18", border: "1px solid " + (rel ? "#5fbf774d" : "#e07a5f4d"), color: rel ? "#5FBF77" : "#E07A5F" }}>{rel ? "✔ RELEASED" : "REJECTED"}</span></td>
                          <td style={TD}>{caseLink(c.case_id)}</td>
                          <td style={TD}>{confPct(c.case_id)}</td>
                          <td style={TD}>{c.decided_by_name || "staff"}</td>
                          <td style={{ ...TD, color: "var(--sf-ok)", whiteSpace: "nowrap" }}>{fmtDT(c.decided_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === "staff" && me && me.is_owner && <StaffManage me={me} />}
        </>
      )}
    </StaffShell>
  );
};

const StaffApp = () => {
  const [checking, setChecking] = useState(true);
  const [session, setSession] = useState(null);
  const [isStaffUser, setIsStaffUser] = useState(false);
  const [me, setMe] = useState(null);
  const check = async () => {
    setChecking(true);
    if (!supabase) { setChecking(false); return; }
    const s = await supabase.auth.getSession();
    const sess = s.data ? s.data.session : null;
    setSession(sess);
    if (sess) {
      const r = await supabase.rpc("is_staff");
      const ok = r.data === true;
      setIsStaffUser(ok);
      if (ok) { const prof = await supabase.from("staff").select("*").eq("id", sess.user.id).single(); setMe(prof.data || null); }
      else setMe(null);
    } else { setIsStaffUser(false); setMe(null); }
    setChecking(false);
  };
  useEffect(() => { check(); }, []);
  const onSignOut = async () => { await supabase.auth.signOut(); setSession(null); setIsStaffUser(false); setMe(null); };
  if (checking) return <StaffShell><div style={{ color: SMUT }}>Loading...</div></StaffShell>;
  if (!session) return <StaffLogin onDone={check} />;
  if (!isStaffUser) return (
    <StaffShell right={<button onClick={onSignOut} style={SF_GHOST}>Sign out</button>}>
      <div style={{ maxWidth: 460, margin: "40px auto", textAlign: "center" }}>
        <h2 style={{ fontFamily: "Georgia, serif", color: SGOLD }}>Staff access only</h2>
        <p style={{ color: SMUT, fontSize: 17 }}>This account does not have access to the VAL staff area. If you believe this is a mistake, contact your administrator.</p>
      </div>
    </StaffShell>
  );
  return <StaffConsole me={me} onSignOut={onSignOut} />;
};

const RecipientPortal = () => {
  const _m = typeof window !== "undefined" ? window.location.pathname.match(/\/keepsake\/([a-zA-Z0-9]+)/) : null;
  const token = _m ? _m[1] : null;
  const [status, setStatus] = useState(token ? "loading" : "error");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!token) return;
    let alive = true;
    fetch("/.netlify/functions/recipient-bundle?token=" + encodeURIComponent(token))
      .then((res) => res.json())
      .then((j) => { if (!alive) return; if (j && j.ok) { setData(j); setStatus("ok"); } else { setStatus("error"); } })
      .catch(() => { if (alive) setStatus("error"); });
    return () => { alive = false; };
  }, [token]);
  const wrap = { minHeight: "100vh", background: BLACK, color: TEXT, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 20px" };
  if (status === "loading") {
    return (
      <div style={wrap}>
        <style>{styles}</style>
        <Logo size={40} />
        <p style={{ color: MUTED, marginTop: 24 }}>Opening your messages...</p>
      </div>
    );
  }
  if (status !== "ok" || !data) {
    return (
      <div style={wrap}>
        <style>{styles}</style>
        <Logo size={40} />
        <h1 className="serif" style={{ fontSize: 30, marginTop: 24 }}>This link isn't available</h1>
        <p style={{ color: MUTED, maxWidth: 440, textAlign: "center", lineHeight: 1.7 }}>This secure link is invalid or has expired. If you believe this is a mistake, contact support@voiceafterlife.com.</p>
      </div>
    );
  }
  const items = data.items || [];
  return (
    <div style={{ minHeight: "100vh", background: BLACK, color: TEXT }}>
      <style>{styles}</style>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 20px 90px" }}>
        <div style={{ display: "flex", justifyContent: "center" }}><Logo size={40} /></div>
        <p style={{ color: GOLD, fontSize: 12, letterSpacing: "0.3em", textTransform: "uppercase", textAlign: "center", marginTop: 28 }}>A message left for you</p>
        <h1 className="serif" style={{ fontSize: "clamp(30px, 5vw, 44px)", textAlign: "center", lineHeight: 1.15, marginTop: 8 }}>For {data.recipient || "you"}</h1>
        <p style={{ color: MUTED, textAlign: "center", fontSize: 17, lineHeight: 1.8, marginTop: 12 }}>{(data.sender || "A loved one") + " left these to be shared with you through Voice After Life."}</p>
        {items.length === 0 && (<p style={{ color: MUTED, textAlign: "center", marginTop: 40 }}>There are no messages to show yet.</p>)}
        <div style={{ marginTop: 40, display: "flex", flexDirection: "column", gap: 20 }}>
          {items.map((it, i) => (
            <div key={it.id || i} style={{ background: SURFACE, border: "1px solid " + BORDER, borderRadius: 14, padding: 22 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
                <h3 className="serif" style={{ fontSize: 20 }}>{it.title || "Untitled"}</h3>
                <span style={{ color: GOLD, fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{it.kind === "letter" ? "handwritten" : (it.kind || "message")}</span>
              </div>
              {it.kind === "text" && it.text && (<p style={{ color: TEXT, fontSize: 16, lineHeight: 1.9, whiteSpace: "pre-wrap" }}>{it.text}</p>)}
              {it.kind === "voice" && it.mediaUrl && (<audio controls preload="none" style={{ width: "100%" }} src={it.mediaUrl} />)}
              {it.kind === "video" && it.mediaUrl && (<video controls preload="none" style={{ width: "100%", borderRadius: 8 }} src={it.mediaUrl} />)}
              {it.kind === "photo" && it.mediaUrl && (<img alt={it.title || "photo"} src={it.mediaUrl} style={{ width: "100%", borderRadius: 8 }} />)}
              {/* A handwritten letter is shown full width, as a letter should be —
                  a picture of the person's own hand, not a file to download. A
                  scan saved as a PDF opens in a new tab instead. */}
              {it.kind === "letter" && it.mediaUrl && (
                /\.pdf(\?|$)/i.test(String(it.mediaUrl).split("?")[0])
                  ? (<a href={it.mediaUrl} target="_blank" rel="noreferrer" style={{ color: GOLD }}>Open the letter</a>)
                  : (<img alt={it.title || "A handwritten letter"} src={it.mediaUrl} style={{ width: "100%", borderRadius: 8 }} />)
              )}
              {it.kind === "file" && it.mediaUrl && (<a href={it.mediaUrl} target="_blank" rel="noreferrer" style={{ color: GOLD }}>Download {it.fileName || "file"}</a>)}
              {it.text && it.kind !== "text" && (<p style={{ color: MUTED, fontSize: 14, lineHeight: 1.7, marginTop: 12, whiteSpace: "pre-wrap" }}>{it.text}</p>)}
              {it.kind !== "text" && !it.mediaUrl && (<p style={{ color: MUTED, fontSize: 13 }}>This item could not be loaded.</p>)}
            </div>
          ))}
        </div>
        <p style={{ color: MUTED, fontSize: 12, textAlign: "center", marginTop: 56, lineHeight: 1.7 }}>Shared securely through Voice After Life. Please keep this link private.</p>
      </div>
    </div>
  );
};

/* ===== REPORT A DEATH — public intake flow ===== */
function formatStamp(dt) {
  const p = (n) => (n < 10 ? "0" + n : "" + n);
  let h = dt.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return (
    p(dt.getMonth() + 1) +
    "/" +
    p(dt.getDate()) +
    "/" +
    dt.getFullYear() +
    "  " +
    h +
    ":" +
    p(dt.getMinutes()) +
    ":" +
    p(dt.getSeconds()) +
    " " +
    ampm
  );
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || "");
      const comma = res.indexOf(",");
      resolve(comma >= 0 ? res.slice(comma + 1) : res);
    };
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

const RD_FIELD_WRAP = { marginBottom: 18, textAlign: "left" };
const RD_LABEL = {
  display: "block",
  fontSize: 17,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: MUTED,
  marginBottom: 8,
};
const RD_INPUT = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.16)",
  borderRadius: 6,
  padding: "15px 16px",
  color: TEXT,
  fontSize: 20,
  fontFamily: "inherit",
  outline: "none",
};

function RdField({ label, req, type, value, onChange, textarea, placeholder }) {
  return (
    <div style={RD_FIELD_WRAP}>
      <label style={RD_LABEL}>
        {label} {req ? <span style={{ color: GOLD }}>*</span> : null}
      </label>
      {textarea ? (
        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder || ""}
          rows={3}
          style={{ ...RD_INPUT, resize: "vertical" }}
        />
      ) : (
        <input
          type={type || "text"}
          value={value}
          onChange={onChange}
          placeholder={placeholder || ""}
          style={RD_INPUT}
        />
      )}
    </div>
  );
}

function RdUpload({ label, file, onPick }) {
  return (
    <div style={{ marginBottom: 14, textAlign: "left" }}>
      <label style={RD_LABEL}>{label}</label>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          background: "rgba(255,255,255,0.04)",
          border: "1px dashed rgba(255,255,255,0.22)",
          borderRadius: 6,
          padding: "15px 16px",
          cursor: "pointer",
        }}
      >
        <span style={{ color: file ? TEXT : MUTED, fontSize: 19, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {file ? file.name : "Choose a file (PDF or image)"}
        </span>
        <span style={{ color: GOLD, fontSize: 15, letterSpacing: "0.06em", textTransform: "uppercase", flexShrink: 0 }}>
          Browse
        </span>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.doc,.docx,image/*,application/pdf"
          onChange={onPick}
          style={{ display: "none" }}
        />
      </label>
    </div>
  );
}

/**
 * VerifierRespondPage  —  /verify/<token>
 *
 * A Trusted Verifier has no account and never will. The unguessable token in
 * the URL is the credential, the same pattern as the keepsake portal. Asking
 * these people to register in order to answer a question WE asked THEM, about
 * whether someone has died, would be indefensible.
 *
 * Three answers, all of them useful. "I am not sure" is offered as a real
 * choice rather than buried, because a verifier who is uncertain and has only
 * yes/no in front of them will guess, and a guess here is worse than silence.
 *
 * verifier_respond() in Postgres is idempotent: a second click returns the
 * answer already given rather than overwriting it, so a forwarded message
 * cannot flip somebody\u2019s answer.
 */
/**
 * DeleteAccountPage  \u2014  /delete-account
 *
 * Apple 5.1.1(v) wants three things and this page does all three: the person
 * can initiate deletion themselves, it deletes the ACCOUNT rather than merely
 * deactivating it, and what happens, how long it takes and what is retained
 * are all stated before they commit. The mobile app links straight here, which
 * Apple permits, so there is one implementation and no chance of the two front
 * ends drifting apart on something irreversible.
 *
 * ?stop=<token> is the cancel link from the confirmation email and works with
 * NO SIGN-IN. That is deliberate: the person who most needs to stop a deletion
 * is the one who has just lost control of their account.
 */
function DeleteAccountPage({ setScreen }) {
  const stopToken = (() => {
    try { return new URLSearchParams(window.location.search).get("stop") || ""; } catch (e) { return ""; }
  })();

  const [phase, setPhase] = useState(stopToken ? "stopping" : "loading");
  const [pending, setPending] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [needsStaff, setNeedsStaff] = useState("");
  const [scheduled, setScheduled] = useState("");

  const call = async (payload, withAuth) => {
    const headers = { "Content-Type": "application/json" };
    if (withAuth) {
      const s = await supabase.auth.getSession();
      const tk = s.data && s.data.session ? s.data.session.access_token : "";
      if (!tk) return { ok: false, error: "Please sign in first." };
      headers.Authorization = "Bearer " + tk;
    }
    const resp = await fetch("/.netlify/functions/account-deletion", {
      method: "POST", headers, body: JSON.stringify(payload),
    });
    const txt = await resp.text();
    try { return JSON.parse(txt); } catch (e) { return { ok: false, error: "Something went wrong." }; }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (stopToken) {
        const r = await call({ action: "cancel_by_token", token: stopToken }, false);
        if (!alive) return;
        if (r.ok) setPhase("stopped");
        else { setErr(r.error || "This link is no longer active."); setPhase("stop_failed"); }
        return;
      }
      if (!supabase) { setPhase("form"); return; }
      const s = await supabase.auth.getSession();
      if (!alive) return;
      if (!s.data || !s.data.session) { setPhase("signin"); return; }
      const r = await call({ action: "status" }, true);
      if (!alive) return;
      if (r && r.pending) { setPending(r); setPhase("pending"); }
      else setPhase("form");
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setErr(""); setNeedsStaff(""); setBusy(true);
    const r = await call({ action: "request" }, true);
    setBusy(false);
    if (r.needs_staff) { setNeedsStaff(r.error || ""); return; }
    if (!r.ok) { setErr(r.error || "We could not start this. Please try again."); return; }
    setScheduled(r.scheduled_for || "");
    setPhase("requested");
  };

  const stopIt = async () => {
    setErr(""); setBusy(true);
    const r = await call({ action: "cancel" }, true);
    setBusy(false);
    if (!r.ok) { setErr(r.error || "We could not stop this. Please try again."); return; }
    setPhase("stopped");
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    } catch (e) { return "shortly"; }
  };

  const shell = (inner) => (
    <section style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 24px" }}>
      <div style={{ width: "100%", maxWidth: 620, margin: "0 auto" }}>{inner}</div>
    </section>
  );

  const kicker = (s) => (
    <div style={{ fontSize: 13, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD, marginBottom: 16 }}>{s}</div>
  );

  if (phase === "loading" || phase === "stopping")
    return shell(<div style={{ color: MUTED, fontSize: 17, textAlign: "center" }}>One moment...</div>);

  if (phase === "stopped")
    return shell(
      <div style={{ textAlign: "center" }}>
        {kicker("Stopped")}
        <h1 style={{ fontSize: 32, fontWeight: 400, lineHeight: 1.3, marginBottom: 14 }}>Your account is safe</h1>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, marginBottom: 28 }}>
          The deletion has been cancelled and nothing was removed. Everything you have recorded is exactly where you left it.
        </p>
        <GoldBtn onClick={() => setScreen("dashboard")} style={{ padding: "14px 32px" }}>Back to my account</GoldBtn>
      </div>
    );

  if (phase === "stop_failed")
    return shell(
      <div style={{ textAlign: "center" }}>
        <h1 style={{ fontSize: 30, fontWeight: 400, lineHeight: 1.3, marginBottom: 14 }}>This link is no longer active</h1>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, marginBottom: 28 }}>{err}</p>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7 }}>If you are worried about your account, please contact us straight away.</p>
      </div>
    );

  if (phase === "signin")
    return shell(
      <div style={{ textAlign: "center" }}>
        {kicker("Delete your account")}
        <h1 style={{ fontSize: 32, fontWeight: 400, lineHeight: 1.3, marginBottom: 14 }}>Please sign in first</h1>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, marginBottom: 28 }}>
          For something this permanent we need to know it is really you.
        </p>
        <GoldBtn onClick={() => setScreen("login")} style={{ padding: "14px 32px" }}>Sign in</GoldBtn>
      </div>
    );

  if (phase === "pending" || phase === "requested") {
    const when = fmt(phase === "requested" ? scheduled : pending && pending.scheduled_for);
    return shell(
      <div style={{ textAlign: "center" }}>
        {kicker("Scheduled")}
        <h1 style={{ fontSize: 32, fontWeight: 400, lineHeight: 1.3, marginBottom: 16 }}>Your account will be deleted on {when}</h1>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, marginBottom: 16 }}>
          Nothing has been deleted yet. We have emailed you a confirmation with a link that stops this without signing in, in case you ever cannot get back into your account.
        </p>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, marginBottom: 30 }}>
          You can change your mind at any point before that date.
        </p>
        {err ? <p style={{ color: "#E07A5F", fontSize: 16, marginBottom: 16 }}>{err}</p> : null}
        <GoldBtn onClick={stopIt} disabled={busy} style={{ padding: "14px 32px" }}>
          {busy ? "Stopping..." : "Keep my account"}
        </GoldBtn>
      </div>
    );
  }

  const ready = typed.trim().toUpperCase() === "DELETE";

  return shell(
    <div>
      {kicker("Delete your account")}
      <h1 style={{ fontSize: 34, fontWeight: 400, lineHeight: 1.3, marginBottom: 18 }}>This cannot be undone</h1>

      <div style={{ background: SURFACE, border: "1px solid " + BORDER, borderRadius: 14, padding: "22px 24px", marginBottom: 18 }}>
        <div style={{ color: GOLD, fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 12 }}>What will be deleted</div>
        <ul style={{ color: MUTED, fontSize: 17, lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
          <li>Every voice recording, video, message, story and answer you have saved</li>
          <li>Your Legacy Circle, and every invitation you have sent</li>
          <li>Your account and your ability to sign in</li>
        </ul>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, marginTop: 14, marginBottom: 0 }}>
          <strong style={{ color: GOLD }}>Your Legacy Members will never receive any of it.</strong> Once this is done, neither you nor we can bring it back.
        </p>
      </div>

      <div style={{ background: SURFACE, border: "1px solid " + BORDER, borderRadius: 14, padding: "22px 24px", marginBottom: 18 }}>
        <div style={{ color: GOLD, fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 12 }}>What we keep, and why</div>
        <ul style={{ color: MUTED, fontSize: 17, lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
          <li>Billing records, which we are required to retain for tax and accounting</li>
          <li>Any death report filed about you by another person, with the link to your account removed — that is their submission and our record of it, not yours to erase</li>
        </ul>
      </div>

      <div style={{ background: SURFACE, border: "1px solid " + BORDER, borderRadius: 14, padding: "22px 24px", marginBottom: 24 }}>
        <div style={{ color: GOLD, fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 12 }}>How long it takes</div>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, margin: 0 }}>
          Seven days. We email you straight away with a link that cancels it without signing in, and you can stop it from here at any point before then. After seven days it happens automatically and cannot be reversed.
        </p>
      </div>

      {needsStaff ? (
        <div style={{ background: "#2a1414", border: "1px solid #E07A5F", borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ color: "#E07A5F", fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10 }}>We need to do this with you</div>
          <p style={{ fontSize: 17, lineHeight: 1.7, margin: 0 }}>{needsStaff}</p>
        </div>
      ) : null}

      <label style={{ display: "block", color: MUTED, fontSize: 14, marginBottom: 8 }}>
        Type DELETE to confirm you understand
      </label>
      <input
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder="DELETE"
        style={{ width: "100%", boxSizing: "border-box", background: SURFACE, border: "1px solid " + BORDER, color: "inherit", borderRadius: 10, padding: "12px 14px", fontSize: 17, font: "inherit", marginBottom: 18 }}
      />

      {err ? <p style={{ color: "#E07A5F", fontSize: 16, marginBottom: 16 }}>{err}</p> : null}

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <button
          onClick={submit}
          disabled={!ready || busy}
          style={{ background: ready && !busy ? "#8c2f28" : "transparent", border: "1px solid " + (ready && !busy ? "#8c2f28" : BORDER), color: ready && !busy ? "#fff" : MUTED, borderRadius: 10, padding: "14px 30px", fontSize: 17, font: "inherit", cursor: ready && !busy ? "pointer" : "default" }}
        >
          {busy ? "Starting..." : "Delete my account"}
        </button>
        <GoldBtn outline onClick={() => setScreen("dashboard")} style={{ padding: "14px 30px" }}>Keep my account</GoldBtn>
      </div>
    </div>
  );
}

function VerifierRespondPage() {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState("");
  const [err, setErr] = useState("");
  const m = window.location.pathname.match(/^\/verify\/([A-Za-z0-9_-]+)$/);
  const token = m ? m[1] : "";

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token || !supabase) { setLoading(false); return; }
      const r = await supabase.rpc("verifier_context", { p_token: token });
      if (!alive) return;
      setCtx(r.error ? null : r.data);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [token]);

  const answer = async (response) => {
    setErr("");
    setBusy(response);
    const r = await supabase.rpc("verifier_respond", {
      p_token: token,
      p_response: response,
      p_note: note.trim() || null,
    });
    setBusy("");
    if (r.error) { setErr(r.error.message); return; }
    if (!r.data || r.data.ok !== true) {
      setErr((r.data && r.data.error) || "We could not record your answer. Please try again.");
      return;
    }
    setDone(response);
  };

  const shell = (inner) => (
    <section style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 24px" }}>
      <div style={{ width: "100%", maxWidth: 560, margin: "0 auto", textAlign: "center" }}>{inner}</div>
    </section>
  );

  if (loading) return shell(<div style={{ color: MUTED, fontSize: 17 }}>One moment...</div>);

  if (!ctx || ctx.ok !== true)
    return shell(
      <div>
        <h1 style={{ fontSize: 30, fontWeight: 400, lineHeight: 1.3, marginBottom: 14 }}>This link is not valid</h1>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7 }}>
          It may have been mistyped, or the question may already have been settled. If someone asked you to confirm something, please reply to the message they sent you.
        </p>
      </div>
    );

  const who = ctx.subject_name || "the account holder";
  const settled = done || (ctx.answered ? ctx.response : "");

  if (settled)
    return shell(
      <div>
        <div style={{ fontSize: 13, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD, marginBottom: 16 }}>Thank you</div>
        <h1 style={{ fontSize: 30, fontWeight: 400, lineHeight: 1.3, marginBottom: 16 }}>
          {settled === "denied" ? "Thank you \u2014 that matters" : "Your answer has been recorded"}
        </h1>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7 }}>
          {settled === "confirmed"
            ? "You told us that " + who + " has passed away. A member of our staff reads every case personally before anything is shared with anyone. We are sorry for your loss."
            : settled === "denied"
            ? "You told us that " + who + " is still alive. We have stopped the process and flagged this for a person on our team to look at straight away. Nothing has been shared with anyone."
            : "You told us you were not certain. That is a genuinely useful answer, and it goes to the person reviewing this."}
        </p>
      </div>
    );

  if (ctx.closed)
    return shell(
      <div>
        <h1 style={{ fontSize: 30, fontWeight: 400, lineHeight: 1.3, marginBottom: 14 }}>This has already been settled</h1>
        <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7 }}>Thank you for coming to help. Nothing is needed from you now.</p>
      </div>
    );

  const choice = (label, hint, response, tone) => (
    <button
      onClick={() => answer(response)}
      disabled={!!busy}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "1px solid " + (tone === "alive" ? GOLD : BORDER),
        color: "inherit",
        borderRadius: 12,
        padding: "16px 18px",
        marginBottom: 12,
        cursor: busy ? "default" : "pointer",
        opacity: busy && busy !== response ? 0.45 : 1,
        font: "inherit",
      }}
    >
      <div style={{ fontSize: 18, marginBottom: 3 }}>{busy === response ? "Sending..." : label}</div>
      <div style={{ color: MUTED, fontSize: 14, lineHeight: 1.5 }}>{hint}</div>
    </button>
  );

  return shell(
    <div>
      <div style={{ fontSize: 13, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD, marginBottom: 16 }}>A careful question</div>
      <h1 style={{ fontSize: 32, fontWeight: 400, lineHeight: 1.3, marginBottom: 16 }}>{"Has " + who + " passed away?"}</h1>
      <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, marginBottom: 10 }}>
        {who + " named you as someone we could turn to if anyone ever reported that they had died. Someone has now made that report."}
      </p>
      <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, marginBottom: 26 }}>
        We do not know whether it is true, and nothing has been shared with anyone. Whatever you tell us, a person on our staff reads it before anything happens.
      </p>
      <div style={{ textAlign: "left" }}>
        {choice("Yes, they have passed away", "We will pass this to the person reviewing the case.", "confirmed", "")}
        {choice("No \u2014 they are alive", "This stops the process immediately and flags it for a person to look at.", "denied", "alive")}
        {choice("I am not sure", "A perfectly good answer. It is recorded as-is and nothing is assumed from it.", "unsure", "")}
      </div>
      <label style={{ display: "block", textAlign: "left", color: MUTED, fontSize: 13, margin: "18px 0 6px" }}>
        Anything you would like to add (optional)
      </label>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        placeholder="Anything that would help the person reviewing this."
        style={{ width: "100%", boxSizing: "border-box", background: SURFACE, border: "1px solid " + BORDER, color: "inherit", borderRadius: 10, padding: "10px 12px", fontSize: 15, font: "inherit", lineHeight: 1.5 }}
      />
      {err ? <p style={{ color: "#E07A5F", fontSize: 15, marginTop: 14 }}>{err}</p> : null}
      <p style={{ color: MUTED, fontSize: 13, lineHeight: 1.6, marginTop: 22 }}>
        You do not need an account or a password. This link is yours alone and can only be answered once.
      </p>
    </div>
  );
}

function ReportDeathPage({ setScreen }) {
  const [step, setStep] = useState("warning");
  const [stamp, setStamp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    dFullName: "",
    dDob: "",
    dPassing: "",
    dPhone: "",
    dEmail: "",
    dSiblings: "",
    dParents: "",
    fFullName: "",
    fRelationship: "",
    fPhone: "",
    fEmail: "",
    fReason: "",
  });
  const [files, setFiles] = useState({
    obituary: null,
    death_certificate: null,
    funeral_program: null,
    supporting_document: null,
  });

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const pick = (kind) => (e) => {
    const file = (e.target.files && e.target.files[0]) || null;
    setFiles((prev) => ({ ...prev, [kind]: file }));
  };

  const begin = () => {
    setStamp(formatStamp(new Date()));
    setErr("");
    setStep("form");
  };

  const submit = async () => {
    setErr("");
    if (
      !form.dFullName.trim() ||
      !form.dEmail.trim() ||
      !form.fFullName.trim() ||
      !form.fRelationship.trim() ||
      !form.fEmail.trim()
    ) {
      setErr("Please complete the required fields marked with *.");
      return;
    }
    setBusy(true);
    try {
      const docs = [];
      const kinds = ["obituary", "death_certificate", "funeral_program", "supporting_document"];
      for (let i = 0; i < kinds.length; i++) {
        const kind = kinds[i];
        const file = files[kind];
        if (!file) continue;
        if (file.size > 250 * 1024 * 1024) {
          setErr("Each file must be 250 MB or smaller.");
          setBusy(false);
          return;
        }
        if (!supabase) {
          setErr("Uploads aren't available right now. Please try again later.");
          setBusy(false);
          return;
        }
        // Get a one-time signed upload URL, then send the file straight to secure
        // storage from the browser. This bypasses the serverless request-size limit,
        // so large scans and PDFs upload reliably.
        const signRes = await fetch("/.netlify/functions/sign-doc-upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ kind: kind, filename: file.name, contentType: file.type }),
        });
        const signData = await signRes.json().catch(() => ({}));
        if (!signRes.ok || !signData.ok || !signData.path || !signData.token) {
          setErr((signData && signData.error) || "We could not prepare your document upload. Please try again.");
          setBusy(false);
          return;
        }
        const up = await supabase.storage
          .from(signData.bucket)
          .uploadToSignedUrl(signData.path, signData.token, file, { contentType: file.type || undefined });
        if (up.error) {
          setErr("We could not upload " + file.name + ". Please try again.");
          setBusy(false);
          return;
        }
        docs.push({ kind: kind, filename: file.name, contentType: file.type, path: signData.path });
      }
      const res = await fetch("/.netlify/functions/report-death", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submittedAt: new Date().toISOString(),
          deceased: {
            fullName: form.dFullName,
            dob: form.dDob,
            dateOfPassing: form.dPassing,
            phone: form.dPhone,
            email: form.dEmail,
            siblings: form.dSiblings,
            parents: form.dParents,
          },
          filer: {
            fullName: form.fFullName,
            relationship: form.fRelationship,
            phone: form.fPhone,
            email: form.fEmail,
            reason: form.fReason,
          },
          documents: docs,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setErr((data && data.error) || "We could not submit your report. Please try again.");
        setBusy(false);
        return;
      }
      setStep("done");
    } catch (e) {
      setErr("We could not submit your report. Please try again.");
    }
    setBusy(false);
  };

  const shell = (inner, maxWidth) => (
    <section
      style={{
        minHeight: "72vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 24px",
      }}
    >
      <div style={{ width: "100%", maxWidth: maxWidth || 560, margin: "0 auto", textAlign: "center" }}>
        {inner}
      </div>
    </section>
  );

  if (step === "warning") {
    return shell(
      <div>
        <div style={{ fontSize: 14, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD, marginBottom: 18 }}>
          Warning
        </div>
        <h1 style={{ fontSize: 34, fontWeight: 400, lineHeight: 1.3, marginBottom: 16 }}>
          Are you sure you want to report a Voice After Life death?
        </h1>
        <p style={{ color: MUTED, fontSize: 18, lineHeight: 1.7, marginBottom: 36 }}>
          This begins a formal verification process. If so, choose Continue. Otherwise, return to the home page.
        </p>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <GoldBtn onClick={() => setStep("intro")} style={{ padding: "14px 36px" }}>
            Continue
          </GoldBtn>
          <GoldBtn outline onClick={() => setScreen("landing")} style={{ padding: "14px 36px" }}>
            Back Home
          </GoldBtn>
        </div>
      </div>,
      560
    );
  }

  if (step === "intro") {
    return shell(
      <div>
        <p style={{ fontSize: 22, lineHeight: 1.8, color: TEXT, marginBottom: 40 }}>
          We&rsquo;re deeply sorry for your loss. If someone you love created a Voice After Life
          account, we&rsquo;re here to help you honor their wishes. Our verification team will
          carefully review the information you provided and conduct a 30 day investigation prior to
          releasing any messages to the designated Legacy Members.
        </p>
        <GoldBtn onClick={begin} style={{ padding: "14px 40px" }}>
          Begin Verification
        </GoldBtn>
      </div>,
      620
    );
  }

  if (step === "done") {
    return shell(
      <div>
        <div style={{ fontSize: 44, marginBottom: 18, color: GOLD }}>&#10003;</div>
        <h1 style={{ fontSize: 30, fontWeight: 400, marginBottom: 16 }}>Your report has been received</h1>
        <p style={{ color: MUTED, fontSize: 18, lineHeight: 1.8, marginBottom: 36 }}>
          Thank you. Our verification team will carefully review the information you provided. We
          hope the messages, videos, and communication left behind bring you peace. We are sorry for
          your loss.
        </p>
        <GoldBtn outline onClick={() => setScreen("landing")} style={{ padding: "14px 36px" }}>
          Return Home
        </GoldBtn>
      </div>,
      560
    );
  }

  /* step === "form" */
  const sectionTitle = {
    fontSize: 18,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: GOLD,
    margin: "0 0 18px",
    paddingBottom: 10,
    borderBottom: "1px solid rgba(255,255,255,0.12)",
  };

  return (
    <section style={{ padding: "56px 24px", maxWidth: 640, margin: "0 auto" }}>
      <div
        style={{
          fontSize: 17,
          color: MUTED,
          fontFamily: "monospace",
          marginBottom: 28,
          padding: "10px 14px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 6,
        }}
      >
        Verification opened: {stamp}
      </div>

      <h3 style={sectionTitle}>Information About the Deceased</h3>
      <RdField label="Deceased Full Name" req value={form.dFullName} onChange={set("dFullName")} />
      <RdField label="Date of Birth (If Known)" type="date" value={form.dDob} onChange={set("dDob")} />
      <RdField label="Date of Passing (If Known)" type="date" value={form.dPassing} onChange={set("dPassing")} />
      <RdField label="Deceased Phone Number (If Known)" type="tel" value={form.dPhone} onChange={set("dPhone")} />
      <RdField label="Deceased Email" req type="email" value={form.dEmail} onChange={set("dEmail")} />
      <RdField label="Sibling Name(s) (If Known)" value={form.dSiblings} onChange={set("dSiblings")} />
      <RdField label="Parent Name(s) (If Known)" value={form.dParents} onChange={set("dParents")} />

      <h3 style={{ ...sectionTitle, marginTop: 40 }}>Person Filing the Death Verification</h3>
      <RdField label="Your Full Name" req value={form.fFullName} onChange={set("fFullName")} />
      <RdField label="Your Relationship to the Deceased" req value={form.fRelationship} onChange={set("fRelationship")} />
      <RdField label="Your Phone Number" type="tel" value={form.fPhone} onChange={set("fPhone")} />
      <RdField label="Your Email Address" req type="email" value={form.fEmail} onChange={set("fEmail")} />
      <RdField label="Reason for Notification" textarea value={form.fReason} onChange={set("fReason")} />

      <p
        style={{
          fontSize: 18,
          lineHeight: 1.75,
          color: MUTED,
          background: "rgba(212,175,55,0.06)",
          border: "1px solid rgba(212,175,55,0.25)",
          borderRadius: 6,
          padding: "16px 18px",
          margin: "10px 0 34px",
          textAlign: "left",
        }}
      >
        ALL Legacy Members on the User&rsquo;s Voice After Life account will receive a text notification
        after our 30 day waiting period and investigation, typically this can range between 30 to 40
        days. We hope the messages, videos and communication left behind brings you peace. We are
        sorry for your loss.
      </p>

      <h3 style={{ ...sectionTitle, marginTop: 10 }}>Upload Documents</h3>
      <RdUpload label="Upload Obituary" file={files.obituary} onPick={pick("obituary")} />
      <RdUpload label="Upload Death Certificate" file={files.death_certificate} onPick={pick("death_certificate")} />
      <RdUpload label="Upload Funeral Program" file={files.funeral_program} onPick={pick("funeral_program")} />
      <RdUpload label="Upload Supporting Document" file={files.supporting_document} onPick={pick("supporting_document")} />

      {err ? (
        <p style={{ color: "#e0a1a1", fontSize: 17, margin: "18px 0 0", textAlign: "left" }}>{err}</p>
      ) : null}

      <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 32 }}>
        <GoldBtn onClick={submit} disabled={busy} style={{ padding: "14px 44px" }}>
          {busy ? "Submitting…" : "Submit"}
        </GoldBtn>
        <GoldBtn outline onClick={() => setScreen("landing")} disabled={busy} style={{ padding: "14px 28px" }}>
          Cancel
        </GoldBtn>
      </div>
    </section>
  );
}


function PressReleaseModal({ onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
      <div onClick={function (e) { e.stopPropagation(); }} style={{ maxWidth: 820, width: "100%", position: "relative" }}>
        <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 12, right: 12, zIndex: 2, cursor: "pointer", background: "rgba(0,0,0,0.5)", color: "#F5F1E8", border: "1px solid #C9A84C", borderRadius: 999, width: 38, height: 38, fontSize: 20, lineHeight: "1" }}>&times;</button>
        <div style={{ background: SURFACE, border: "1px solid rgba(201,168,76,0.35)", borderRadius: 14, padding: "40px 36px", textAlign: "left" }}>
        <p style={{ color: GOLD, fontSize: 17, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 10 }}>
          For Immediate Release · August 4, 2026
        </p>
        <h3 className="serif" style={{ fontSize: 48, lineHeight: 1.25, margin: "0 0 14px", color: "#F5F1E8" }}>
          Voice After Life Officially Launches a New Era of Digital Legacy Preservation
        </h3>
        <p style={{ color: "#d8d1c2", fontSize: 26, fontWeight: 600, lineHeight: 1.6, margin: "0 0 22px" }}>
          An innovative platform that enables individuals to preserve voice, video, and written messages for loved ones — while building one of the world's first AI-powered digital legacy ecosystems.
        </p>
        <p style={{ color: "#c3bbac", fontSize: 22, lineHeight: 1.8, margin: "0 0 16px" }}>
          Voice After Life proudly announces the official launch of its digital legacy platform across 31 countries and 5 languages. Voice After Life provides individuals and families with a secure, safe way to preserve their voice, life history, video messages, written memories, family stories, recipes, and life's most meaningful moments for future generations to enjoy.
        </p>
        <p style={{ color: "#c3bbac", fontSize: 22, lineHeight: 1.8, margin: "0 0 16px" }}>
          One of the greatest gifts you can leave your loved ones is your voice, your ideas, your memories, your praises, your congratulations. Voice After Life combines modern technology with compassionate purpose. The platform allows users to create personal messages that are securely stored and delivered only after a verified passing, ensuring that love, guidance, encouragement, and life's wisdom continue long after we are gone.
        </p>
        <blockquote style={{ borderLeft: "3px solid " + GOLD, margin: "0 0 20px", padding: "6px 0 6px 18px", color: "#d8d1c2", fontStyle: "italic", fontSize: 26, lineHeight: 1.7 }}>
          "We created Voice After Life because every life has a story worth preserving," said founder Dr. Joe Robinson, Ph.D., ABD, USMC Veteran. "Too many families lose not only someone they love, but also the opportunity to hear one more story, one more piece of advice, or simply one more 'I love you.' Our mission is to help preserve those moments before they're lost forever."
        </blockquote>
        <p style={{ color: "#c3bbac", fontSize: 22, lineHeight: 1.8, margin: "0 0 16px" }}>
          Unlike traditional cloud storage or messaging platforms, Voice After Life is designed specifically for legacy preservation. Users can securely record voice messages, videos, and written communications, designate Legacy Members, and trust that messages will be released only after a structured verification process. Not even Voice After Life staff have access.
        </p>
        <p style={{ color: GOLD, fontSize: 19, letterSpacing: "0.12em", textTransform: "uppercase", margin: "24px 0 10px" }}>Key Features</p>
        <ul style={{ color: "#c3bbac", fontSize: 22, lineHeight: 1.9, margin: "0 0 20px", paddingLeft: 20 }}>
          <li>Secure voice, video, and written message preservation</li>
          <li>Encrypted digital storage</li>
          <li>Legacy Member pre-notification system</li>
          <li>Identity verification during account creation</li>
          <li>Multi-step death verification process before message release</li>
          <li>Mobile-friendly user experience</li>
          <li>AI-assisted operations designed to improve security and efficiency</li>
          <li>A real-time Command Center that monitors platform activity, customer growth, operational metrics, and business performance</li>
        </ul>
        <p style={{ color: "#c3bbac", fontSize: 22, lineHeight: 1.8, margin: "0 0 16px" }}>
          The company begins its first national digital advertising campaign immediately following launch, introducing Voice After Life to families, Veterans, caregivers, estate planners, funeral homes, and individuals seeking a secure way to preserve their memories and final wishes. Voice After Life's long-term vision extends far beyond message preservation, with several preservation-of-life platforms currently under development.
        </p>
        <p style={{ color: "#c3bbac", fontSize: 22, lineHeight: 1.8, margin: "0 0 16px" }}>
          Dr. Robinson also expressed his sincere appreciation to those who encouraged and supported the vision throughout its development. "Every successful journey is strengthened by the encouragement of others," Robinson said. "I am especially grateful to my lifelong friend who I met in Marine Corps boot camp, James Robinson, and to Sredrick Robinson and Scott Robinson, my adult children, whose encouragement, honest conversations, prayers, and unwavering belief in this vision helped inspire me to continue building Voice After Life from an idea into a reality."
        </p>
        <div style={{ background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.35)", borderRadius: 10, padding: "16px 20px", margin: "0 0 20px" }}>
          <p style={{ color: "#e7e0d0", fontSize: 22, lineHeight: 1.7, margin: 0 }}>
            <strong style={{ color: GOLD }}>Military and First Responders</strong> receive a 30% discount every 11th of the month, synonymous with Veterans Day. Voice After Life is the gift that keeps on giving. Visitors can learn more, explore subscription options, and begin preserving their own legacy today at{" "}
            <a href="https://www.voiceafterlife.com" style={{ color: GOLD, textDecoration: "underline" }}>www.voiceafterlife.com</a>.
          </p>
        </div>
        <div style={{ borderTop: "1px solid rgba(201,168,76,0.25)", marginTop: 24, paddingTop: 20 }}>
          <p style={{ color: GOLD, fontSize: 19, letterSpacing: "0.12em", textTransform: "uppercase", margin: "0 0 8px" }}>About Voice After Life</p>
          <p style={{ color: "#c3bbac", fontSize: 22, lineHeight: 1.8, margin: "0 0 18px" }}>
            Voice After Life is a digital legacy technology company dedicated to helping people preserve their voice, stories, wisdom, and personal messages for the people they love. Through secure technology, thoughtful design, and AI-assisted operations, the company is building a platform where every person has the opportunity to leave behind more than memories — they can leave behind their own voice.
          </p>
          <p style={{ color: GOLD, fontSize: 19, letterSpacing: "0.12em", textTransform: "uppercase", margin: "0 0 8px" }}>Media Contact</p>
          <p style={{ color: "#c3bbac", fontSize: 22, lineHeight: 1.9, margin: 0 }}>
            Media Inquiries: <a href="mailto:media@voiceafterlife.com" style={{ color: GOLD, textDecoration: "none" }}>media@voiceafterlife.com</a><br />
            Press Inquiries: <a href="mailto:press@voiceafterlife.com" style={{ color: GOLD, textDecoration: "none" }}>press@voiceafterlife.com</a>
          </p>
        </div>
      </div>
      
      </div>
    </div>
  );
}

function ArticleGallery() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [light, setLight] = useState(false);
  const [showPress, setShowPress] = useState(false);
  useEffect(function () {
    fetch("/.netlify/functions/public-articles")
      .then(function (r) { return r.json(); })
      .then(function (d) { setItems((d && d.articles) || []); setLoaded(true); })
      .catch(function () { setLoaded(true); });
  }, []);
  const CURATED = [];
  const gold = light ? "#b8892b" : "#C9A84C";
  const ink = light ? "#1d1a16" : "#F5F1E8";
  const muted = light ? "#6b6357" : "#9A9080";
  const cardBg = light ? "#ffffff" : "rgba(255,255,255,0.04)";
  const line = light ? "#e9e3d8" : "#2A2A2A";
  const goldSoft = light ? "#f3ead7" : "rgba(201,168,76,0.14)";
  const panelBg = light ? "#faf8f4" : "transparent";
  const fmtDate = function (s) { if (!s) return ""; const d = new Date(s); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); };
  const press = { pressRelease: true, pillar: "Press Release", title: "Voice After Life Officially Launches a New Era of Digital Legacy Preservation", excerpt: "Our official launch announcement \u2014 the story and history of Voice After Life.", published_at: "2026-08-04", image_url: null };
  const rest = CURATED.concat(items).slice().sort(function (a, b) { return new Date(b.published_at || 0) - new Date(a.published_at || 0); });
  const all = [press].concat(rest);
  const cardShell = { background: cardBg, border: "1px solid " + line, borderRadius: 14, overflow: "hidden", textDecoration: "none", cursor: "pointer" };
  return (
    <div style={{ background: panelBg, borderRadius: 16, padding: light ? "26px 22px 36px" : "6px 0 20px", marginTop: 28, transition: "background .2s ease" }}>
      {showPress ? <PressReleaseModal onClose={function () { setShowPress(false); }} /> : null}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <h3 className="serif" style={{ color: ink, fontSize: 26, margin: 0 }}>Latest stories &amp; guides</h3>
        <button onClick={function () { setLight(!light); }} aria-label="Toggle light and dark" style={{ cursor: "pointer", background: goldSoft, color: gold, border: "1px solid " + line, borderRadius: 999, padding: "8px 16px", fontSize: 13, fontWeight: 600, letterSpacing: "0.04em" }}>{light ? "\u263E  Dark" : "\u2600  Light"}</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 22 }}>
        {all.map(function (a, i) {
          const dateStr = fmtDate(a.published_at);
          const media = a.image_url
            ? <div style={{ aspectRatio: "16 / 10", backgroundImage: "url(" + a.image_url + ")", backgroundSize: "cover", backgroundPosition: "center" }} />
            : <div style={{ aspectRatio: "16 / 10", background: goldSoft, display: "flex", alignItems: "center", justifyContent: "center" }}><span className="serif" style={{ color: gold, fontStyle: "italic", fontSize: 22, opacity: 0.9 }}>Voice After Life</span></div>;
          const inner = (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              {media}
              <div style={{ padding: "18px 20px 22px", display: "flex", flexDirection: "column", flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  {a.pillar ? <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: gold, background: goldSoft, padding: "4px 10px", borderRadius: 999 }}>{a.pillar}</span> : <span />}
                  {dateStr ? <span style={{ fontSize: 12, color: muted }}>{dateStr}</span> : null}
                </div>
                <h4 className="serif" style={{ color: ink, fontSize: 19, lineHeight: 1.25, margin: "0 0 8px" }}>{a.title}</h4>
                <p style={{ color: muted, fontSize: 14.5, margin: "0 0 16px", flex: 1 }}>{a.excerpt}</p>
                <span style={{ color: gold, fontWeight: 600, fontSize: 14 }}>Read the story &rarr;</span>
              </div>
            </div>
          );
          if (a.pressRelease) return <div key={i} onClick={function () { setShowPress(true); }} style={cardShell}>{inner}</div>;
          return a.url ? <a key={i} href={a.url} style={cardShell}>{inner}</a> : <div key={i} style={cardShell}>{inner}</div>;
        })}
      </div>
    </div>
  );
}

export default function App() {
  const first = parseLocation();
  const [screen, setScreenState] = useState(first.screen);
  const [legalPolicyId, setLegalPolicyId] = useState(first.policyId);
  const [user, setUser] = useState(null);
  const [lang, setLang] = useState("en");
  // Site-wide light/dark theme. Default light; remembered in localStorage and
  // applied as a class on <html> so every screen (and the CSS variables) follow.
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("val_theme") === "dark" ? "dark" : "light"; } catch (e) { return "light"; }
  });
  useEffect(() => {
    try {
      const el = document.documentElement;
      if (theme === "dark") el.classList.add("v-dark"); else el.classList.remove("v-dark");
    } catch (e) {}
  }, [theme]);
  const toggleTheme = () => setTheme((tm) => {
    const nt = tm === "dark" ? "light" : "dark";
    try { localStorage.setItem("val_theme", nt); } catch (e) {}
    return nt;
  });

  // Change screen AND update the browser address bar.
  const setScreen = (s) => {
    setScreenState(s);
    setLegalPolicyId(null);
    window.history.pushState({}, "", pathFor(s, null));
    window.scrollTo(0, 0);
  };

  // Open a specific policy at its own URL (/legal/:id).
  const openLegalPolicy = (id) => {
    setScreenState("legal");
    setLegalPolicyId(id);
    window.history.pushState({}, "", id ? "/legal/" + id : "/legal");
    window.scrollTo(0, 0);
  };

  // Keep the app in sync with the browser back/forward buttons.
  useEffect(() => {
    const onPop = () => {
      const loc = parseLocation();
      setScreenState(loc.screen);
      setLegalPolicyId(loc.policyId);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    const apply = (session, navigate) => {
      if (!active || !session || !session.user) return;
      const u = session.user;
      const meta = u.user_metadata || {};
      const acct = meta.account_type || "";
      setUser({ name: meta.full_name || (u.email || "").split("@")[0], email: u.email, id: u.id, plan: "", accountType: acct });
      if (navigate && parseLocation().screen !== "reset" && parseLocation().screen !== "staff" && parseLocation().screen !== "signup") {
        // Guest accounts have no vault — their home is the Resource Center.
        const home = acct === "guest" ? "resources" : "dashboard";
        setScreenState(home);
        window.history.replaceState({}, "", SCREEN_PATHS[home]);
      }
    };
    supabase.auth.getSession().then((res) => {
      apply(res && res.data && res.data.session, false);
    });
    const listener = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN") apply(session, true);
      else if (event === "SIGNED_OUT") { if (active) setUser(null); }
      else apply(session, false);
    });
    return () => {
      active = false;
      const sub = listener && listener.data && listener.data.subscription;
      if (sub) sub.unsubscribe();
    };
  }, []);

  if (screen === "staff") return <StaffApp />;
  if (screen === "recipient") return <RecipientPortal />;
  if (screen === "verify") return <VerifierRespondPage />;
  if (screen === "deleteAccount") return <DeleteAccountPage setScreen={setScreen} />;

  return (
    <div style={{ minHeight: "100vh", background: BLACK, color: TEXT }}>
      <style>{styles}</style>
      <div className="v-force-dark" style={{ background: BLACK, borderBottom: `1px solid ${GOLD}`, color: GOLD, textAlign: "center", padding: "9px 16px", fontSize: 13.5, letterSpacing: "0.03em" }}>
        <span style={{ marginRight: 6 }}>🎖️</span>
        Military &amp; First Responders — <strong style={{ fontWeight: 700 }}>30% off Voice After Life, for life.</strong>{" "}
        <button onClick={() => { const el = document.getElementById("military-discount"); if (el) { el.scrollIntoView({ behavior: "smooth" }); } else { window.open("https://verifypass.com/auth/da782475d5", "VerifyPass", "width=475,height=700"); } }} style={{ background: "none", border: "none", color: "#C9A84C", textDecoration: "underline", fontWeight: 700, cursor: "pointer", fontSize: 13.5, letterSpacing: "0.03em" }}>Verify your service →</button>
      </div>
      <Nav
        screen={screen}
        setScreen={setScreen}
        user={user}
        setUser={setUser}
        lang={lang}
        setLang={setLang}
        theme={theme}
        toggleTheme={toggleTheme}
      />
      {screen === "landing" && <LandingPage setScreen={setScreen} />}
      {screen === "reportDeath" && <ReportDeathPage setScreen={setScreen} />}
      {screen === "company" && <CompanyPage setScreen={setScreen} />}
      {screen === "media" && <MediaPage setScreen={setScreen} />}
        {screen === "faq" && <FAQPage setScreen={setScreen} />}
      {screen === "legal" && (
        <LegalPage
          setScreen={setScreen}
          openId={legalPolicyId}
          onOpen={openLegalPolicy}
        />
      )}
      {screen === "login" && (
        (SIGNUPS_PAUSED && !DEV_ACCESS) ? <ComingSoon setScreen={setScreen} /> : <AuthPage mode="login" setScreen={setScreen} setUser={setUser} />
      )}
      {screen === "signup" && (
        SIGNUPS_PAUSED ? <ComingSoon setScreen={setScreen} /> : <AuthPage mode="signup" setScreen={setScreen} setUser={setUser} />
      )}
      {screen === "dashboard" && user && isStaffEmail(user.email) && (
        <section style={{ minHeight: "72vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 24px" }}>
          <div style={{ width: "100%", maxWidth: 520, margin: "0 auto", textAlign: "center" }}>
            <div style={{ fontSize: 13, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD, marginBottom: 16 }}>Staff account</div>
            <h1 style={{ fontSize: 30, fontWeight: 400, lineHeight: 1.3, marginBottom: 14 }}>This is a staff address</h1>
            <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, marginBottom: 28 }}>
              Voice After Life staff accounts do not have a personal dashboard. Your work address is for the staff console, the Command Center and the AI Marketing Center. If you are also a customer, please sign in with your personal email address.
            </p>
            <GoldBtn onClick={() => setScreen("staff")} style={{ padding: "14px 32px" }}>Go to the staff console</GoldBtn>
          </div>
        </section>
      )}
      {screen === "dashboard" && user && !isStaffEmail(user.email) && (
        user.accountType === "guest"
          ? <ResourceCenter user={user} setScreen={setScreen} />
          : <Dashboard user={user} setScreen={setScreen} />
      )}
      {screen === "resources" && (
        <ResourceCenter user={user} setScreen={setScreen} />
      )}
      {screen === "guest" && (
        <GuestSignup setScreen={setScreen} setUser={setUser} />
      )}
      {screen === "reset" && (
        <ResetPassword setScreen={setScreen} />
      )}
    </div>
  );
              }
